import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    LayoutDashboard, FolderKanban, FileText, ListChecks,
    FlaskConical, Code2, Target, ShieldCheck, Sparkles,
    Settings, Bell, Search, ChevronDown,
    ChevronRight, TrendingUp, TrendingDown, AlertTriangle,
    CheckCircle2, Clock, Zap, DollarSign, Bot, ArrowLeft, ArrowRight,
    Activity, Users, X, Filter, Download, Hash,
    BookOpen, CircleDot, AlertCircle, Info, Play, RefreshCw,
    CheckCheck, XCircle, Timer, Terminal, GitBranch, Image as ImageIcon, Eye, Rocket,
    LogOut, CornerDownLeft,
} from "lucide-react";
import { CoverageProvider, useCoverage } from "./CoverageProvider";
import { TestSuiteDataProvider, useTestSuiteData } from "./TestSuiteDataProvider";
// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    blue: "#2563EB",
    blueL: "#EFF6FF",
    blueM: "#BFDBFE",
    blueD: "#1D4ED8",
    purple: "#6366F1",
    purpleL: "#EEF2FF",
    green: "#059669",
    greenL: "#ECFDF5",
    orange: "#D97706",
    orangeL: "#FFFBEB",
    red: "#DC2626",
    redL: "#FEF2F2",
    slate50: "#F0F6FF",
    slate100: "#E0EEFF",
    slate200: "#C7D9F5",
    slate400: "#7EA4C8",
    slate500: "#5B85A8",
    slate600: "#3A6186",
    slate700: "#1E4270",
    slate800: "#0F2F5C",
    slate900: "#071A3E",
    white: "#FFFFFF",
    sidebarBg: "#FFFFFF",
    sidebarBorder: "#DBEAFE",
    sidebarText: "#334155",
    sidebarMuted: "#64748B",
    pageBg: "#F4F8FE",
};
const mono = { fontFamily: "DM Mono, monospace" };
const sans = { fontFamily: "Inter, sans-serif" };
const BOTTOM_WIDGET_HEIGHT = 400;
const FLAKY_TEST_RATE = {
    flakyTests: 2,
    automatedExecutions: 40,
    rate: 5,
};
// ─── Data ─────────────────────────────────────────────────────────────────────
const FUNNEL = [
    { label: "Requirements Extracted", count: "921", pct: 100, color: C.blue, sub: "AI extracted and structured requirements", state: "Complete" },
    { label: "Test Cases Generated", count: "2,341", pct: 97, color: C.purple, sub: "Test cases created from requirements", state: "Near Complete" },
    { label: "Automation Scripts", count: "1,876", pct: 80, color: C.green, sub: "Automation scripts generated", state: "In Progress" },
    { label: "Reviewed", count: "1,654", pct: 71, color: C.orange, sub: "Reviewed by QA team", state: "In Progress" },
    { label: "Release Ready", count: "68%", pct: 68, color: C.green, sub: "Ready for deployment", state: "In Progress" },
];
// `kind` links a tile to the coverage API's source kinds. Jira has no tile
// counterpart in the API yet, so it stays on demo data (see README).
const CONNECTED_SOURCES = [
    { label: "BRD Uploaded", icon: FileText, count: "12", color: C.blue, kind: "brd" },
    { label: "Screenshots", icon: ImageIcon, count: "28", color: C.orange, kind: "screen_capture" },
    { label: "Excel / CSV", icon: FileText, count: "8", color: C.green, kind: "test_sheet" },
    { label: "Jira Imports", icon: ListChecks, count: "46", color: C.blueD },
];
const DONUT_SLICES = [
    { label: "Covered", pct: 71, color: C.green, count: 654 },
    { label: "Partially Covered", pct: 18, color: C.orange, count: 166 },
    { label: "Missing", pct: 11, color: C.red, count: 101 },
];
const REQ_DETAIL = {
    "Covered": {
        headline: "Fully Covered Requirements",
        description: "These 654 requirements have complete test case coverage with mapped automation scripts.",
        color: C.green,
        icon: <CheckCircle2 size={20}/>,
        stats: [
            { label: "Requirements", value: "654", delta: "+3% vs last sprint", positive: true },
            { label: "Test Cases", value: "1,847", delta: "+127 this sprint", positive: true },
            { label: "Automated", value: "88%", delta: "+2%", positive: true },
            { label: "Avg Confidence", value: "94%", positive: true },
        ],
        reqs: [
            { id: "REQ-001", title: "User login with email and password", module: "Login", priority: "P0", testCases: 12, automationPct: 100, status: "Covered", lastUpdated: "Jul 22" },
            { id: "REQ-002", title: "OAuth2 social login (Google, GitHub)", module: "Login", priority: "P1", testCases: 8, automationPct: 100, status: "Covered", lastUpdated: "Jul 21" },
            { id: "REQ-003", title: "Session timeout after 30 min inactivity", module: "Login", priority: "P1", testCases: 5, automationPct: 100, status: "Covered", lastUpdated: "Jul 20" },
            { id: "REQ-004", title: "MFA via TOTP authenticator app", module: "Login", priority: "P0", testCases: 9, automationPct: 100, status: "Covered", lastUpdated: "Jul 22" },
            { id: "REQ-005", title: "Add to cart with quantity management", module: "Checkout", priority: "P0", testCases: 14, automationPct: 93, status: "Covered", lastUpdated: "Jul 19" },
            { id: "REQ-006", title: "Guest checkout without registration", module: "Checkout", priority: "P1", testCases: 7, automationPct: 86, status: "Covered", lastUpdated: "Jul 18" },
            { id: "REQ-007", title: "Coupon / promo code validation", module: "Checkout", priority: "P2", testCases: 6, automationPct: 100, status: "Covered", lastUpdated: "Jul 17" },
            { id: "REQ-008", title: "Customer profile CRUD operations", module: "Customer", priority: "P1", testCases: 11, automationPct: 91, status: "Covered", lastUpdated: "Jul 21" },
            { id: "REQ-009", title: "Address book management", module: "Customer", priority: "P2", testCases: 8, automationPct: 88, status: "Covered", lastUpdated: "Jul 20" },
            { id: "REQ-010", title: "Sales report CSV/Excel export", module: "Reports", priority: "P2", testCases: 4, automationPct: 75, status: "Covered", lastUpdated: "Jul 16" },
        ],
    },
    "Partially Covered": {
        headline: "Partially Covered Requirements",
        description: "These 166 requirements have some test coverage but are missing critical scenarios or edge cases.",
        color: C.orange,
        icon: <CircleDot size={20}/>,
        stats: [
            { label: "Requirements", value: "166", delta: "-5 this sprint", positive: false },
            { label: "Test Cases", value: "312", delta: "Missing ~180 more", positive: false },
            { label: "Avg Coverage", value: "54%", delta: "Below 80% threshold", positive: false },
            { label: "Blocked by", value: "18 PRs", positive: false },
        ],
        reqs: [
            { id: "REQ-101", title: "Credit card payment processing", module: "Payment", priority: "P0", testCases: 6, automationPct: 50, status: "Partial", lastUpdated: "Jul 15" },
            { id: "REQ-102", title: "PayPal and wallet integrations", module: "Payment", priority: "P0", testCases: 4, automationPct: 25, status: "Partial", lastUpdated: "Jul 14" },
            { id: "REQ-103", title: "Refund processing and partial refunds", module: "Payment", priority: "P1", testCases: 3, automationPct: 33, status: "Partial", lastUpdated: "Jul 13" },
            { id: "REQ-104", title: "Multi-currency display and conversion", module: "Checkout", priority: "P1", testCases: 5, automationPct: 60, status: "Partial", lastUpdated: "Jul 18" },
            { id: "REQ-105", title: "Saved payment methods management", module: "Customer", priority: "P1", testCases: 4, automationPct: 50, status: "Partial", lastUpdated: "Jul 16" },
            { id: "REQ-106", title: "Wishlist and save-for-later", module: "Customer", priority: "P2", testCases: 3, automationPct: 67, status: "Partial", lastUpdated: "Jul 15" },
            { id: "REQ-107", title: "Real-time inventory availability", module: "Checkout", priority: "P1", testCases: 5, automationPct: 40, status: "Partial", lastUpdated: "Jul 12" },
            { id: "REQ-108", title: "Scheduled report delivery via email", module: "Reports", priority: "P2", testCases: 2, automationPct: 0, status: "Partial", lastUpdated: "Jul 10" },
        ],
    },
    "Missing": {
        headline: "Requirements with No Coverage",
        description: "These 101 requirements have zero test cases. They represent untested functionality that could block release.",
        color: C.red,
        icon: <AlertCircle size={20}/>,
        stats: [
            { label: "Requirements", value: "101", delta: "+8 this sprint", positive: false },
            { label: "High Priority", value: "23", delta: "P0 / P1 gaps", positive: false },
            { label: "Est. Hours", value: "~340h", delta: "To generate coverage", positive: false },
            { label: "Risk Level", value: "Critical", positive: false },
        ],
        reqs: [
            { id: "REQ-201", title: "3D Secure (3DS2) authentication flow", module: "Payment", priority: "P0", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-202", title: "Payment retry logic on gateway failure", module: "Payment", priority: "P0", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-203", title: "Chargeback dispute webhook handling", module: "Payment", priority: "P1", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-204", title: "Dynamic tax calculation by region", module: "Checkout", priority: "P1", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-205", title: "Abandoned cart recovery emails", module: "Customer", priority: "P1", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-206", title: "GDPR data deletion / right to erasure", module: "Customer", priority: "P0", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-207", title: "Fraud detection scoring integration", module: "Payment", priority: "P0", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-208", title: "Cross-border shipping restrictions", module: "Checkout", priority: "P2", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-209", title: "Subscription billing & renewal flows", module: "Payment", priority: "P1", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
            { id: "REQ-210", title: "Accessibility (WCAG 2.1 AA) audit", module: "Reports", priority: "P2", testCases: 0, automationPct: 0, status: "Missing", lastUpdated: "—" },
        ],
    },
};
const PRIORITY_COLOR = {
    P0: { bg: C.blueL, fg: C.blueD },
    P1: { bg: C.redL, fg: C.red },
    P2: { bg: C.orangeL, fg: C.orange },
    P3: { bg: "#E8EFFD", fg: C.slate600 },
};
const MODULE_COLORS = {
    Login: C.blue, Payment: C.red, Checkout: C.orange, Customer: C.green, Reports: C.blueD,
};
// ─── Requirement Coverage Full Panel ─────────────────────────────────────────
// Flatten all reqs from all three slices into one unified list
const ALL_REQUIREMENTS = [
    ...REQ_DETAIL["Covered"].reqs,
    ...REQ_DETAIL["Partially Covered"].reqs,
    ...REQ_DETAIL["Missing"].reqs,
];
const COV_STATUS_CFG = {
    Covered: { bg: C.greenL, fg: C.green, label: "Covered", dot: C.green },
    Partial: { bg: C.orangeL, fg: C.orange, label: "Partial", dot: C.orange },
    Missing: { bg: C.redL, fg: C.red, label: "Missing", dot: C.red },
};
// ─── Demo fallback for the coverage API ──────────────────────────────────────
// The prototype's hardcoded numbers, reshaped into the API's response types.
// CoverageProvider serves these whenever the API is unreachable or empty, so
// the dashboard looks exactly as it always did without a backend running.
const DEMO_BRD_DOCUMENTS = [
    { id: "demo-1", name: "Loan Origination System BRD v2.4", requirements: 256, coveragePct: 78, status: "Covered", module: "Login" },
    { id: "demo-2", name: "Customer Portal BRD v1.3", requirements: 178, coveragePct: 71, status: "Covered", module: "Customer" },
    { id: "demo-3", name: "Payment Gateway Integration BRD v1.1", requirements: 142, coveragePct: 57, status: "Partial", module: "Payment" },
    { id: "demo-4", name: "User Management Module BRD v1.0", requirements: 98, coveragePct: 82, status: "Covered", module: "Login" },
    { id: "demo-5", name: "Reporting & Analytics BRD v1.0", requirements: 76, coveragePct: 32, status: "Missing", module: "Reports" },
];
/** Which demo BRD each module's requirements belong to. */
const DEMO_MODULE_TO_BRD = {
    Login: "demo-1", Customer: "demo-2", Payment: "demo-3", Checkout: "demo-3", Reports: "demo-5",
};
const DEMO_REQUIREMENT_VIEWS = ALL_REQUIREMENTS.map((r) => {
    const docId = DEMO_MODULE_TO_BRD[r.module] ?? "demo-1";
    return {
        id: r.id,
        extKey: r.id,
        title: r.title,
        description: null,
        module: r.module,
        priority: r.priority,
        status: r.status,
        testCases: r.testCases,
        automatedCases: Math.round((r.testCases * r.automationPct) / 100),
        automationPct: r.automationPct,
        screens: 0,
        sourceId: docId,
        sourceName: DEMO_BRD_DOCUMENTS.find((d) => d.id === docId)?.name ?? "Demo BRD",
        lastUpdated: r.lastUpdated,
    };
});
const COVERAGE_FALLBACK = {
    summary: {
        totalRequirements: DONUT_SLICES.reduce((s, d) => s + d.count, 0),
        coveragePct: DONUT_SLICES[0].pct,
        breakdown: {
            Covered: DONUT_SLICES[0].count,
            Partial: DONUT_SLICES[1].count,
            Missing: DONUT_SLICES[2].count,
        },
        slices: [
            { label: "Covered", status: "Covered", count: DONUT_SLICES[0].count, pct: DONUT_SLICES[0].pct },
            { label: "Partially Covered", status: "Partial", count: DONUT_SLICES[1].count, pct: DONUT_SLICES[1].pct },
            { label: "Missing", status: "Missing", count: DONUT_SLICES[2].count, pct: DONUT_SLICES[2].pct },
        ],
        testCases: { total: 2341, linked: 2341, automated: 1876, automationPct: 80 },
        screens: { total: 28, captures: 28, linked: 28 },
        modules: [],
        generatedAt: new Date().toISOString(),
    },
    sources: CONNECTED_SOURCES.filter((s) => s.kind).map((s) => ({
        kind: s.kind,
        label: s.label,
        count: Number(s.count),
        producedRequirements: 0,
        producedTestCases: 0,
        producedScreens: 0,
        lastIngestedAt: null,
    })),
    requirements: DEMO_REQUIREMENT_VIEWS,
    brdDocuments: DEMO_BRD_DOCUMENTS,
};
/** Merges live per-kind counts into the five presentation tiles. */
function useSourceTiles() {
    const { sources, live } = useCoverage();
    return useMemo(() => CONNECTED_SOURCES.map((tile) => {
        const match = tile.kind ? sources.find((s) => s.kind === tile.kind) : undefined;
        return {
            ...tile,
            count: match ? String(match.count) : tile.count,
            // Jira has no ingestion endpoint, so its tile is demo data even when live.
            isLive: Boolean(match) && live,
        };
    }), [sources, live]);
}
/** Small badge that makes it obvious whether numbers are real or demo. */
function LiveBadge() {
    const { live, loading, error } = useCoverage();
    if (loading) {
        return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: C.slate100, color: C.slate500 }}>Loading…</span>;
    }
    return (<span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={live ? { background: C.greenL, color: C.green } : { background: C.slate100, color: C.slate500 }} title={live ? "Live data from the coverage API" : (error ?? "Coverage API unavailable — showing demo data")}>
      {live ? "Live" : "Demo data"}
    </span>);
}
function RequirementCoveragePanel({ onClose }) {
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [filterModule, setFilterModule] = useState("All");
    const [filterPriority, setFilterPriority] = useState("All");
    const [expandedId, setExpandedId] = useState(null);
    const total = ALL_REQUIREMENTS.length;
    const covered = ALL_REQUIREMENTS.filter((r) => r.status === "Covered").length;
    const partial = ALL_REQUIREMENTS.filter((r) => r.status === "Partial").length;
    const missing = ALL_REQUIREMENTS.filter((r) => r.status === "Missing").length;
    const covPct = Math.round((covered / total) * 100);
    const modules = ["All", ...Array.from(new Set(ALL_REQUIREMENTS.map((r) => r.module)))];
    const priorities = ["All", "P0", "P1", "P2", "P3"];
    const statuses = ["All", "Covered", "Partial", "Missing"];
    const filtered = ALL_REQUIREMENTS.filter((r) => {
        const q = search.toLowerCase();
        return ((r.title.toLowerCase().includes(q) || r.id.toLowerCase().includes(q)) &&
            (filterStatus === "All" || r.status === filterStatus) &&
            (filterModule === "All" || r.module === filterModule) &&
            (filterPriority === "All" || r.priority === filterPriority));
    });
    // Mini donut for the panel header
    const SIZE = 64, CX = 32, CY = 32, OR = 28, IR = 18, GAP = 0.05;
    let theta = -Math.PI / 2;
    const miniArcs = [
        { pct: covPct, color: C.green },
        { pct: Math.round((partial / total) * 100), color: C.orange },
        { pct: 100 - covPct - Math.round((partial / total) * 100), color: C.red },
    ].map((d) => {
        const sweep = (d.pct / 100) * Math.PI * 2;
        const sa = theta + GAP, ea = theta + sweep - GAP;
        theta += sweep;
        const lg = sweep - 2 * GAP > Math.PI ? 1 : 0;
        const cos = Math.cos, sin = Math.sin;
        const path = [
            `M ${CX + OR * cos(sa)} ${CY + OR * sin(sa)}`,
            `A ${OR} ${OR} 0 ${lg} 1 ${CX + OR * cos(ea)} ${CY + OR * sin(ea)}`,
            `L ${CX + IR * cos(ea)} ${CY + IR * sin(ea)}`,
            `A ${IR} ${IR} 0 ${lg} 0 ${CX + IR * cos(sa)} ${CY + IR * sin(sa)} Z`,
        ].join(" ");
        return { ...d, path };
    });
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(1020px, 94vw)", background: C.white, boxShadow: "-4px 0 40px rgba(15,23,42,0.18)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-5 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
              <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                {miniArcs.map((arc, i) => (<path key={`mini-arc-${i}`} d={arc.path} fill={arc.color}/>))}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-[11px] font-bold" style={{ ...mono, color: C.slate900 }}>{covPct}%</span>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight" style={{ color: C.slate900 }}>REQUIREMENT COVERAGE</h2>
              <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>
                Release 2.4.1 · 921 total requirements · Traceability report
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Download size={11}/> Export CSV
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-5 divide-x shrink-0" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          {[
            { label: "Total Requirements", value: "921", color: C.slate800, icon: <BookOpen size={13}/>, sub: "BRD-sourced" },
            { label: "Covered", value: String(covered), color: C.green, icon: <CheckCircle2 size={13}/>, sub: `${covPct}% of total` },
            { label: "Partially Covered", value: String(partial), color: C.orange, icon: <CircleDot size={13}/>, sub: `${Math.round((partial / total) * 100)}% of total` },
            { label: "Missing", value: String(missing), color: C.red, icon: <AlertCircle size={13}/>, sub: "No test coverage" },
            { label: "Coverage Trend", value: "+3%", color: C.green, icon: <TrendingUp size={13}/>, sub: "vs last sprint" },
        ].map((s) => (<div key={s.label} className="px-5 py-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${s.color}15`, color: s.color }}>{s.icon}</div>
              <div>
                <p className="text-xl font-bold leading-none" style={{ ...mono, color: s.color }}>{s.value}</p>
                <p className="text-[10px] mt-0.5 font-semibold" style={{ color: C.slate600 }}>{s.label}</p>
                <p className="text-[10px]" style={{ color: C.slate400 }}>{s.sub}</p>
              </div>
            </div>))}
        </div>

        {/* Coverage bar + module breakdown */}
        <div className="px-8 py-4 shrink-0 flex items-center gap-6" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          {/* Stacked coverage bar */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold" style={{ color: C.slate500 }}>Overall Coverage</span>
              <span className="text-[11px]" style={{ ...mono, color: C.slate500 }}>654 / 921 requirements</span>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden gap-px">
              <div style={{ width: `${covPct}%`, background: C.green, transition: "width 0.6s ease" }} title="Covered"/>
              <div style={{ width: `${Math.round((partial / total) * 100)}%`, background: C.orange }} title="Partial"/>
              <div style={{ flex: 1, background: C.redL }} title="Missing"/>
            </div>
            <div className="flex items-center gap-4 mt-1.5">
              {[{ label: "Covered", color: C.green }, { label: "Partial", color: C.orange }, { label: "Missing", color: C.red }].map((l) => (<div key={l.label} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: l.color }}/>
                  <span className="text-[10px]" style={{ color: C.slate500 }}>{l.label}</span>
                </div>))}
            </div>
          </div>

          {/* Module coverage bars */}
          <div className="flex items-end gap-3 shrink-0">
            {modules.filter((m) => m !== "All").map((mod) => {
            const modReqs = ALL_REQUIREMENTS.filter((r) => r.module === mod);
            const modCov = modReqs.filter((r) => r.status === "Covered").length;
            const pct = modReqs.length ? Math.round((modCov / modReqs.length) * 100) : 0;
            const col = pct >= 80 ? C.green : pct >= 60 ? C.orange : C.red;
            return (<button key={mod} onClick={() => setFilterModule(filterModule === mod ? "All" : mod)} className="flex flex-col items-center gap-1 transition-opacity" style={{ opacity: filterModule !== "All" && filterModule !== mod ? 0.3 : 1 }}>
                  <span className="text-[10px] font-bold" style={{ ...mono, color: col }}>{pct}%</span>
                  <div className="w-10 rounded-t-md" style={{ height: Math.max(8, (pct / 100) * 40), background: filterModule === mod ? col : `${col}40`, border: `1px solid ${col}60` }}/>
                  <span className="text-[10px]" style={{ color: C.slate500 }}>{mod}</span>
                </button>);
        })}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-8 py-3 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ borderColor: C.sidebarBorder, background: C.blueL }}>
            <Search size={13} style={{ color: C.slate400 }}/>
            <input className="text-xs bg-transparent outline-none w-48" placeholder="Search requirements…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs" style={{ color: C.slate400 }}>Coverage:</span>
            {statuses.map((s) => {
            const color = s === "Covered" ? C.green : s === "Partial" ? C.orange : s === "Missing" ? C.red : C.slate700;
            return (<button key={s} onClick={() => setFilterStatus(s)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterStatus === s ? { background: color, color: C.white } : { background: C.blueL, color: C.slate600 }}>
                  {s}
                </button>);
        })}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs" style={{ color: C.slate400 }}>Module:</span>
            {modules.map((m) => (<button key={m} onClick={() => setFilterModule(m)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterModule === m ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>
                {m}
              </button>))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs" style={{ color: C.slate400 }}>Priority:</span>
            {priorities.map((p) => (<button key={p} onClick={() => setFilterPriority(p)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterPriority === p ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>
                {p}
              </button>))}
          </div>
          <span className="text-xs ml-auto shrink-0" style={{ color: C.slate400 }}>{filtered.length} of {total} shown</span>
        </div>

        {/* Table header */}
        <div className="grid px-8 py-2.5 text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ gridTemplateColumns: "80px 1fr 90px 72px 52px 80px 70px 64px", color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <span>ID</span><span>Requirement</span><span>Module</span><span>Coverage</span>
          <span>P</span><span>Test Cases</span><span>Automation</span><span>Updated</span>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (<div className="flex flex-col items-center justify-center h-40">
              <Info size={22} style={{ color: C.slate300 }}/>
              <p className="text-sm mt-2" style={{ color: C.slate400 }}>No requirements match your filters</p>
            </div>) : filtered.map((req) => {
            const cfg = COV_STATUS_CFG[req.status] ?? COV_STATUS_CFG["Missing"];
            const isOpen = expandedId === req.id;
            const modCases = ALL_REQUIREMENTS.filter((r) => r.module === req.module);
            const modCovPct = Math.round((modCases.filter((r) => r.status === "Covered").length / modCases.length) * 100);
            return (<div key={req.id}>
                <div className="grid px-8 items-center cursor-pointer hover:bg-blue-50 transition-colors group" style={{ gridTemplateColumns: "80px 1fr 90px 72px 52px 80px 70px 64px", paddingTop: 13, paddingBottom: 13, borderBottom: `1px solid ${C.slate50}`, background: isOpen ? C.slate50 : undefined }} onClick={() => setExpandedId(isOpen ? null : req.id)}>
                  <div className="flex items-center gap-1">
                    <Hash size={10} style={{ color: C.slate300 }}/>
                    <span className="text-[11px] font-medium" style={{ ...mono, color: C.slate500 }}>{req.id}</span>
                  </div>

                  <div className="min-w-0 pr-4">
                    <p className="text-xs font-medium truncate group-hover:text-blue-600 transition-colors" style={{ color: C.slate800 }}>{req.title}</p>
                  </div>

                  <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-md" style={{ background: `${MODULE_COLORS[req.module] ?? C.slate400}15`, color: MODULE_COLORS[req.module] ?? C.slate500 }}>{req.module}</span>

                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.dot }}/>
                    <span className="text-[11px] font-semibold" style={{ color: cfg.fg }}>{cfg.label}</span>
                  </div>

                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded text-center" style={{ background: PRIORITY_COLOR[req.priority].bg, color: PRIORITY_COLOR[req.priority].fg }}>{req.priority}</span>

                  <div className="flex items-center gap-1.5">
                    <FlaskConical size={11} style={{ color: C.slate300 }}/>
                    <span className="text-xs" style={{ ...mono, color: req.testCases === 0 ? C.red : C.slate700 }}>{req.testCases === 0 ? "None" : req.testCases}</span>
                  </div>

                  <div>
                    {req.automationPct === 0 ? (<span style={{ color: C.slate300, fontSize: 11 }}>—</span>) : (<div className="flex items-center gap-1.5">
                        <div className="w-8 h-1.5 rounded-full overflow-hidden" style={{ background: C.blueL }}>
                          <div className="h-full rounded-full" style={{ width: `${req.automationPct}%`, background: req.automationPct >= 80 ? C.green : req.automationPct >= 50 ? C.orange : C.red }}/>
                        </div>
                        <span className="text-[10px]" style={{ ...mono, color: C.slate500 }}>{req.automationPct}%</span>
                      </div>)}
                  </div>

                  <span className="text-[11px]" style={{ color: C.slate400 }}>{req.lastUpdated}</span>
                </div>

                {/* Expanded row */}
                {isOpen && (<div className="px-8 py-4 flex items-start gap-8" style={{ background: `${cfg.fg}08`, borderBottom: `1px solid ${cfg.fg}20` }}>
                    <div className="flex-1 space-y-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Requirement Detail</p>
                        <p className="text-sm font-medium" style={{ color: C.slate800 }}>{req.title}</p>
                        <p className="text-[11px] mt-1" style={{ color: C.slate500 }}>
                          Module: <strong style={{ color: MODULE_COLORS[req.module] }}>{req.module}</strong> ·
                          Priority: <strong style={{ color: PRIORITY_COLOR[req.priority].fg }}>{req.priority}</strong> ·
                          Last updated {req.lastUpdated}
                        </p>
                      </div>
                      {/* Module coverage stats */}
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate400 }}>{req.module} module overview</p>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                        { label: "Total Reqs", value: modCases.length, color: C.slate700 },
                        { label: "Covered", value: modCases.filter((r) => r.status === "Covered").length, color: C.green },
                        { label: "Partial", value: modCases.filter((r) => r.status === "Partial").length, color: C.orange },
                        { label: "Missing", value: modCases.filter((r) => r.status === "Missing").length, color: C.red },
                    ].map((k) => (<div key={k.label} className="rounded-xl p-2.5 flex items-center gap-2" style={{ background: `${k.color}10`, border: `1px solid ${k.color}20` }}>
                              <div>
                                <p className="text-sm font-bold leading-none" style={{ ...mono, color: k.color }}>{k.value}</p>
                                <p className="text-[10px] mt-0.5" style={{ color: C.slate500 }}>{k.label}</p>
                              </div>
                            </div>))}
                        </div>
                        <div className="mt-2.5 h-2 rounded-full overflow-hidden" style={{ background: C.blueL }}>
                          <div className="h-full rounded-full" style={{ width: `${modCovPct}%`, background: modCovPct >= 80 ? C.green : modCovPct >= 60 ? C.orange : C.red }}/>
                        </div>
                        <p className="text-[10px] mt-1" style={{ color: C.slate400 }}>{req.module} module coverage: {modCovPct}%</p>
                      </div>
                    </div>
                    <div className="shrink-0 space-y-3 min-w-[160px]">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Coverage Status</p>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: cfg.bg, color: cfg.fg }}>
                          <span className="w-2 h-2 rounded-full" style={{ background: cfg.fg }}/>
                          {cfg.label}
                        </span>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Test Cases</p>
                        <p className="text-sm font-bold" style={{ ...mono, color: req.testCases === 0 ? C.red : C.slate700 }}>{req.testCases === 0 ? "None yet" : req.testCases}</p>
                      </div>
                      <button className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90 w-full justify-center" style={{ background: C.green }}>
                        <Sparkles size={11}/> Generate Tests
                      </button>
                    </div>
                  </div>)}
              </div>);
        })}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <p className="text-xs" style={{ color: C.slate400 }}>
            <BookOpen size={11} className="inline mr-1"/>
            Showing {filtered.length} of 921 requirements · Full dataset in Requirements module
          </p>
          <button className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90" style={{ background: C.blue }}>
            Open Traceability Matrix <ArrowRight size={12}/>
          </button>
        </div>
      </div>
    </>);
}
// ─── Coverage Detail Panel ────────────────────────────────────────────────────
function CoverageDetailPanel({ sliceLabel, onClose }) {
    const detail = REQ_DETAIL[sliceLabel];
    const [search, setSearch] = useState("");
    const [filterModule, setFilterModule] = useState("All");
    const [filterPriority, setFilterPriority] = useState("All");
    if (!detail)
        return null;
    const modules = ["All", ...Array.from(new Set(detail.reqs.map((r) => r.module)))];
    const priorities = ["All", "P0", "P1", "P2", "P3"];
    const filtered = detail.reqs.filter((r) => {
        const matchSearch = r.title.toLowerCase().includes(search.toLowerCase()) || r.id.toLowerCase().includes(search.toLowerCase());
        const matchModule = filterModule === "All" || r.module === filterModule;
        const matchPri = filterPriority === "All" || r.priority === filterPriority;
        return matchSearch && matchModule && matchPri;
    });
    const totalCases = detail.reqs.reduce((s, r) => s + r.testCases, 0);
    return (<>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{
            width: "min(860px, 90vw)",
            background: C.white,
            boxShadow: "-4px 0 40px rgba(15,23,42,0.18)",
            animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)",
        }}>
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(60px); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
          @keyframes fadeUp {
            from { transform: translateY(8px); opacity: 0; }
            to   { transform: translateY(0);   opacity: 1; }
          }
        `}</style>

        {/* Panel Header */}
        <div className="flex items-start justify-between px-8 pt-7 pb-6 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${detail.color}15`, color: detail.color }}>
              {detail.icon}
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-0.5">
                <h2 className="text-xl font-bold" style={{ color: C.slate900 }}>{detail.headline}</h2>
                <span className="text-sm font-semibold px-2.5 py-0.5 rounded-full text-white" style={{ background: detail.color }}>
                  {sliceLabel === "Covered" ? "71%" : sliceLabel === "Partially Covered" ? "18%" : "11%"}
                </span>
              </div>
              <p className="text-sm" style={{ color: C.slate500 }}>{detail.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Download size={12}/> Export CSV
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-blue-50" style={{ color: C.slate400 }}>
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 divide-x shrink-0" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}`, divideColor: C.slate200 }}>
          {detail.stats.map((s) => (<div key={s.label} className="px-6 py-4" style={{ animation: "fadeUp 0.3s ease both" }}>
              <p className="text-xs font-medium mb-1" style={{ color: C.slate500 }}>{s.label}</p>
              <p className="text-2xl font-bold" style={{ ...mono, color: s.positive === false ? C.red : s.positive ? C.green : C.slate900 }}>
                {s.value}
              </p>
              {s.delta && (<p className="text-[11px] mt-0.5" style={{ color: s.positive === false ? C.red : s.positive ? C.green : C.slate400 }}>{s.delta}</p>)}
            </div>))}
        </div>


        {/* Filters + Table */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Filters */}
          <div className="flex items-center gap-3 px-8 py-4 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
            <div className="flex items-center gap-2 flex-1 px-3 py-1.5 rounded-lg border" style={{ borderColor: C.sidebarBorder, background: C.blueL }}>
              <Search size={13} style={{ color: C.slate400 }}/>
              <input className="flex-1 text-xs bg-transparent outline-none" placeholder="Search requirements…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
            </div>
            <div className="flex items-center gap-1.5">
              <Filter size={12} style={{ color: C.slate400 }}/>
              <span className="text-xs" style={{ color: C.slate500 }}>Module:</span>
              {modules.map((m) => (<button key={m} onClick={() => setFilterModule(m)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterModule === m
                ? { background: detail.color, color: C.white }
                : { background: C.blueL, color: C.slate600 }}>
                  {m}
                </button>))}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs" style={{ color: C.slate500 }}>Priority:</span>
              {priorities.map((p) => (<button key={p} onClick={() => setFilterPriority(p)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterPriority === p
                ? { background: C.slate800, color: C.white }
                : { background: C.blueL, color: C.slate600 }}>
                  {p}
                </button>))}
            </div>
            <span className="text-xs shrink-0" style={{ color: C.slate400 }}>{filtered.length} of {detail.reqs.length}</span>
          </div>

          {/* Table header */}
          <div className="grid px-8 py-2.5 text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ gridTemplateColumns: "80px 1fr 90px 60px 80px 70px 70px", color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}` }}>
            <span>ID</span>
            <span>Requirement</span>
            <span>Module</span>
            <span>Priority</span>
            <span>Test Cases</span>
            <span>Automation</span>
            <span>Updated</span>
          </div>

          {/* Table rows */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (<div className="flex flex-col items-center justify-center h-40">
                <Info size={24} style={{ color: C.slate300 }}/>
                <p className="text-sm mt-2" style={{ color: C.slate400 }}>No requirements match your filters</p>
              </div>) : (filtered.map((req, i) => (<div key={req.id} className="grid items-center px-8 py-3.5 transition-colors cursor-pointer hover:bg-blue-50 group" style={{
                gridTemplateColumns: "80px 1fr 90px 60px 80px 70px 70px",
                borderBottom: `1px solid ${C.slate50}`,
                animationDelay: `${i * 20}ms`,
            }}>
                  <div className="flex items-center gap-1.5">
                    <Hash size={10} style={{ color: C.slate300 }}/>
                    <span className="text-[11px] font-medium" style={{ ...mono, color: detail.color }}>{req.id}</span>
                  </div>

                  <div className="pr-4 min-w-0">
                    <p className="text-xs font-medium truncate group-hover:text-blue-600 transition-colors" style={{ color: C.slate800 }}>
                      {req.title}
                    </p>
                  </div>

                  <div>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md" style={{ background: `${MODULE_COLORS[req.module]}15`, color: MODULE_COLORS[req.module] }}>
                      {req.module}
                    </span>
                  </div>

                  <div>
                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: PRIORITY_COLOR[req.priority].bg, color: PRIORITY_COLOR[req.priority].fg }}>
                      {req.priority}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <FlaskConical size={11} style={{ color: C.slate400 }}/>
                    <span className="text-xs" style={{ ...mono, color: req.testCases === 0 ? C.red : C.slate700 }}>
                      {req.testCases === 0 ? "None" : req.testCases}
                    </span>
                  </div>

                  <div>
                    {req.automationPct === 0 ? (<span className="text-[11px]" style={{ color: C.slate300 }}>—</span>) : (<div className="flex items-center gap-1.5">
                        <div className="w-10 h-1.5 rounded-full overflow-hidden" style={{ background: C.blueL }}>
                          <div className="h-full rounded-full" style={{ width: `${req.automationPct}%`, background: req.automationPct >= 80 ? C.green : req.automationPct >= 50 ? C.orange : C.red }}/>
                        </div>
                        <span className="text-[10px]" style={{ ...mono, color: C.slate500 }}>{req.automationPct}%</span>
                      </div>)}
                  </div>

                  <span className="text-[11px]" style={{ color: C.slate400 }}>{req.lastUpdated}</span>
                </div>)))}
          </div>

          {/* Panel Footer */}
          <div className="px-8 py-4 flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
            <p className="text-xs" style={{ color: C.slate500 }}>
              <BookOpen size={12} className="inline mr-1.5" style={{ color: C.slate400 }}/>
              Showing sample data · Full traceability in Requirements module
            </p>
            <button className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90" style={{ background: detail.color }}>
              Open in Requirements <ArrowRight size={12}/>
            </button>
          </div>
        </div>
      </div>
    </>);
}
const MODULES = [
    { name: "Login", cov: 94, auto: 88, risk: "Low" },
    { name: "Payment", cov: 61, auto: 45, risk: "High" },
    { name: "Checkout", cov: 78, auto: 67, risk: "Medium" },
    { name: "Customer", cov: 83, auto: 74, risk: "Low" },
    { name: "Reports", cov: 52, auto: 31, risk: "Critical" },
];
const WEEKS = ["Jun 2", "Jun 9", "Jun 16", "Jun 23", "Jun 30"];
const T_BRD = [3, 5, 4, 7, 6];
const T_CASES = [89, 142, 118, 231, 187];
const T_SCRPT = [67, 108, 92, 178, 154];
const ALERTS = [
    { lvl: "critical", msg: "Payment Module coverage critically low at 61%", time: "2h ago" },
    { lvl: "error", msg: "3 critical requirements have no test coverage", time: "4h ago" },
    { lvl: "warning", msg: "18 automation scripts pending review since Jun 28", time: "6h ago" },
    { lvl: "warning", msg: "Automation coverage dropped 4% this sprint", time: "1d ago" },
    { lvl: "info", msg: "AI generation complete for Checkout module — 312 cases", time: "1d ago" },
];
// ─── Shared primitives ────────────────────────────────────────────────────────
function Card({ children, className = "", onClick, }) {
    return (<div onClick={onClick} className={`rounded-2xl shadow-sm ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" : ""} ${className}`} style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
      {children}
    </div>);
}
function CardHeader({ title, sub, action }) {
    return (<div className="flex items-start justify-between mb-5">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>);
}
function DrillLink({ label = "View details", color = C.blue }) {
    return (<button className="flex items-center gap-1 text-xs font-medium hover:gap-1.5 transition-all" style={{ color }}>
      {label} <ChevronRight size={11}/>
    </button>);
}
function MiniBar({ pct, color }) {
    return (<div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: C.blueL }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }}/>
      </div>
      <span className="text-[11px] w-7 text-right shrink-0" style={{ ...mono, color: C.slate600 }}>{pct}%</span>
    </div>);
}
function RiskPill({ risk }) {
    const map = {
        Low: { bg: C.greenL, fg: C.green },
        Medium: { bg: C.orangeL, fg: C.orange },
        High: { bg: C.redL, fg: C.red },
        Critical: { bg: C.blueL, fg: C.blue },
    };
    const s = map[risk];
    return (<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: s.bg, color: s.fg }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.fg }}/>
      {risk}
    </span>);
}
const NAV = [
    { icon: LayoutDashboard, label: "Dashboard", page: "dashboard" },
    { icon: DollarSign, label: "Finances", page: "finances" },
];
// ─── Header chrome: menus, notifications, command palette ─────────────────────
/** Closes a popover on outside-click or Escape. */
function useDismiss(onClose) {
    const ref = useRef(null);
    useEffect(() => {
        const onPointer = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                onClose();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("mousedown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);
    return ref;
}

function Popover({ onClose, children, side = "right", width = 300, below = true }) {
    const ref = useDismiss(onClose);
    return (<div ref={ref} className="absolute z-[60] rounded-xl overflow-hidden" style={{
            [side]: 0,
            [below ? "top" : "bottom"]: "calc(100% + 8px)",
            width,
            background: C.white,
            border: `1px solid ${C.sidebarBorder}`,
            boxShadow: "0 14px 40px rgba(15,23,42,0.16)",
        }}>
      {children}
    </div>);
}

function MenuItem({ icon, label, sub, onClick, tone = C.slate700 }) {
    return (<button onClick={onClick} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-blue-50" style={{ color: tone }}>
      <span className="shrink-0" style={{ color: tone }}>{icon}</span>
      <span className="min-w-0">
        <span className="block text-xs font-medium truncate">{label}</span>
        {sub ? <span className="block text-[10px] truncate" style={{ color: C.slate400 }}>{sub}</span> : null}
      </span>
    </button>);
}

/** Real account menu for the signed-in TestSuite AI user. */
function ProfileMenu({ currentUser, onClose, onExit, onLogout, side = "right", below = true }) {
    return (<Popover onClose={onClose} side={side} below={below} width={260}>
      <div className="px-3 py-3 flex items-center gap-2.5" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: C.blue }}>{userInitials(currentUser)}</div>
        <div className="min-w-0">
          <p className="text-xs font-semibold truncate" style={{ color: C.slate800 }}>{userDisplayName(currentUser)}</p>
          <p className="text-[10px] truncate" style={{ color: C.slate500 }}>{currentUser?.email || userRoleLabel(currentUser)}</p>
        </div>
      </div>
      <div className="py-1">
        <MenuItem icon={<ShieldCheck size={14}/>} label={userRoleLabel(currentUser)} sub="Role in this workspace"/>
        <MenuItem icon={<ArrowLeft size={14}/>} label="Back to workspace" onClick={() => { onClose(); onExit?.(); }}/>
        {onLogout ? <MenuItem icon={<LogOut size={14}/>} label="Sign out" tone={C.red} onClick={() => { onClose(); onLogout(); }}/> : null}
      </div>
    </Popover>);
}

/** Settings menu — only actions that actually do something here. */
function SettingsMenu({ onClose, onExit, side = "left", below = false }) {
    const testSuite = useTestSuiteData();
    const coverage = useCoverage();
    return (<Popover onClose={onClose} side={side} below={below} width={280}>
      <div className="px-3 py-2.5" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.slate400 }}>Data sources</p>
      </div>
      <div className="px-3 py-2.5 space-y-2" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
        {[
            { label: "TestSuite AI", live: testSuite.live, note: testSuite.error, detail: `${testSuite.testCases.total} test cases · ${testSuite.automationScripts.total} scripts` },
            { label: "Coverage API", live: coverage.live, note: coverage.error, detail: `${coverage.summary.totalRequirements} requirements` },
        ].map((src) => (<div key={src.label} className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium" style={{ color: C.slate700 }}>{src.label}</p>
            <p className="text-[10px] truncate" style={{ color: C.slate400 }}>{src.live ? src.detail : (src.note || "Unavailable")}</p>
          </div>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" style={src.live ? { background: C.greenL, color: C.green } : { background: C.slate100, color: C.slate500 }}>{src.live ? "Live" : "Demo"}</span>
        </div>))}
      </div>
      <div className="py-1">
        <MenuItem icon={<RefreshCw size={14}/>} label="Refresh dashboard data" sub="Re-fetch from both sources" onClick={() => { testSuite.refresh(); coverage.refresh(); onClose(); }}/>
        <MenuItem icon={<ArrowLeft size={14}/>} label="Back to workspace" onClick={() => { onClose(); onExit?.(); }}/>
      </div>
    </Popover>);
}

/** Alerts derived from the real data — no invented notifications. */
function useDashboardAlerts() {
    const { testCases, automationScripts, traceability, live } = useTestSuiteData();
    return useMemo(() => {
        if (!live)
            return [];
        const alerts = [];
        if (automationScripts.runs.failed > 0) {
            alerts.push({
                id: "failed-runs",
                tone: C.red,
                icon: <XCircle size={13}/>,
                title: `${automationScripts.runs.failed} failed automation run${automationScripts.runs.failed === 1 ? "" : "s"}`,
                body: `${automationScripts.runs.passRate}% pass rate across ${automationScripts.runs.total} runs`,
                panel: "scripts",
            });
        }
        if (traceability.notTraced > 0) {
            alerts.push({
                id: "untraced",
                tone: C.orange,
                icon: <GitBranch size={13}/>,
                title: `${traceability.notTraced} BRD${traceability.notTraced === 1 ? "" : "s"} without automation`,
                body: "Test cases exist but no script has been generated",
                panel: "traceability",
            });
        }
        if (testCases.review > 0) {
            alerts.push({
                id: "review",
                tone: C.orange,
                icon: <Eye size={13}/>,
                title: `${testCases.review} test case${testCases.review === 1 ? "" : "s"} awaiting review`,
                body: "Sections still pending approval",
                panel: "testcases",
            });
        }
        if (testCases.manual > 0) {
            alerts.push({
                id: "manual",
                tone: C.blue,
                icon: <Users size={13}/>,
                title: `${testCases.manual} manually added test case${testCases.manual === 1 ? "" : "s"}`,
                body: "Edited after AI generation",
                panel: "testcases",
            });
        }
        return alerts;
    }, [testCases, automationScripts, traceability, live]);
}

function NotificationsMenu({ onClose, onOpenPanel }) {
    const alerts = useDashboardAlerts();
    return (<Popover onClose={onClose} width={330}>
      <div className="px-3 py-2.5 flex items-center justify-between" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.slate400 }}>Alerts</p>
        <span className="text-[10px] font-bold" style={{ color: C.slate500 }}>{alerts.length}</span>
      </div>
      {alerts.length === 0 ? (<p className="px-3 py-5 text-xs text-center" style={{ color: C.slate400 }}>Nothing needs attention.</p>) : (<div className="max-h-[320px] overflow-y-auto">
        {alerts.map((alert) => (<button key={alert.id} onClick={() => { onClose(); onOpenPanel(alert.panel); }} className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-blue-50" style={{ borderBottom: `1px solid ${C.slate50}` }}>
          <span className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${alert.tone}15`, color: alert.tone }}>{alert.icon}</span>
          <span className="min-w-0">
            <span className="block text-xs font-medium" style={{ color: C.slate800 }}>{alert.title}</span>
            <span className="block text-[10px] mt-0.5" style={{ color: C.slate500 }}>{alert.body}</span>
          </span>
        </button>))}
      </div>)}
    </Popover>);
}

/**
 * Without this, an expired session looks identical to "you have no data" —
 * every real-data tile reads 0 and there is nothing on screen to explain why.
 */
function DataSourceBanner() {
    const { live, loading, error, refresh } = useTestSuiteData();
    if (loading || live)
        return null;
    const isAuth = /sign in|401|auth/i.test(String(error || ""));
    return (<div className="flex items-center gap-3 rounded-xl px-4 py-2.5 mb-3" style={{ background: C.orangeL, border: "1px solid #FDE68A" }}>
      <AlertTriangle size={15} className="shrink-0" style={{ color: C.orange }}/>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold" style={{ color: C.slate800 }}>
          {isAuth ? "Session expired — dashboard data not loaded" : "Could not reach the TestSuite AI API"}
        </p>
        <p className="text-[11px] mt-0.5" style={{ color: C.slate600 }}>
          {isAuth
            ? "Every figure below is showing zero because the request was rejected, not because the data is gone. Sign in again to restore it."
            : `${error || "Request failed"} — figures show zero rather than stale or invented numbers.`}
        </p>
      </div>
      <button onClick={refresh} className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg shrink-0 text-white transition-opacity hover:opacity-90" style={{ background: C.orange }}>
        <RefreshCw size={11}/> Retry
      </button>
    </div>);
}

/** One top-bar filter control: a labelled trigger plus a single-select list. */
function FilterSelect({ label, value, options, onSelect, open, onToggle, onClose, width = 300 }) {
    const active = options.find((o) => o.id === value);
    const isDefault = value === "all";
    return (<div className="relative">
      <button onClick={onToggle} aria-haspopup="listbox" aria-expanded={open} title={`${label}: ${active?.name ?? value}`} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors hover:bg-blue-50 max-w-[230px]" style={{
            borderColor: isDefault ? C.sidebarBorder : C.blue,
            color: isDefault ? C.slate700 : C.blue,
            background: open || !isDefault ? C.blueL : undefined,
        }}>
        <span className="truncate">{active?.name ?? value}</span>
        <ChevronDown size={12} className="shrink-0" style={{ color: C.slate400, transform: open ? "rotate(180deg)" : "none" }}/>
      </button>
      {open && (<Popover onClose={onClose} side="left" width={width}>
        <div className="px-3 py-2" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.slate400 }}>{label}</p>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1" role="listbox">
          {options.map((option) => {
            const selected = option.id === value;
            return (<button key={option.id} role="option" aria-selected={selected} onClick={() => { onSelect(option.id); onClose(); }} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-blue-50" style={{ background: selected ? C.blueL : undefined }}>
              <span className="min-w-0">
                <span className="block text-xs font-medium truncate" style={{ color: selected ? C.blue : C.slate700 }}>{option.name}</span>
                {option.meta ? <span className="block text-[10px] truncate" style={{ color: C.slate400 }}>{option.meta}</span> : null}
              </span>
              {selected ? <CheckCircle2 size={13} className="shrink-0" style={{ color: C.blue }}/> : null}
            </button>);
          })}
        </div>
      </Popover>)}
    </div>);
}

/** The three top-bar controls: BRD, module and time range. */
function DashboardFilters() {
    const { filters, setFilter, resetFilters, options, testCases, automationScripts } = useTestSuiteData();
    const [open, setOpen] = useState(null);
    const close = useCallback(() => setOpen(null), []);
    const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

    const brdOptions = options.brds.map((b) => {
        if (b.id === "all")
            return { id: "all", name: "All BRDs", meta: `${options.brds.length - 1} documents combined` };
        const doc = testCases.items.filter((t) => t.brdId === b.id).length;
        return { id: b.id, name: b.name, meta: `${doc} test cases` };
    });
    const moduleOptions = options.modules.map((m) => (m === "all"
        ? { id: "all", name: "All modules", meta: `${options.modules.length - 1} sections` }
        : { id: m, name: m, meta: `${testCases.items.filter((t) => t.module === m).length} test cases` }));
    const rangeOptions = options.ranges.map((r) => ({ id: r.id, name: r.label }));

    const dirty = filters.brdId !== "all" || filters.module !== "all" || filters.range !== "all";

    return (<div className="flex items-center gap-2 ml-4 min-w-0">
      <FilterSelect label="Source BRD" value={filters.brdId} options={brdOptions} onSelect={(v) => setFilter("brdId", v)} open={open === "brd"} onToggle={() => toggle("brd")} onClose={close} width={340}/>
      <FilterSelect label="Module / Section" value={filters.module} options={moduleOptions} onSelect={(v) => setFilter("module", v)} open={open === "module"} onToggle={() => toggle("module")} onClose={close} width={340}/>
      <FilterSelect label="Time range" value={filters.range} options={rangeOptions} onSelect={(v) => setFilter("range", v)} open={open === "range"} onToggle={() => toggle("range")} onClose={close} width={220}/>
      {dirty && (<button onClick={resetFilters} title="Clear all filters" className="flex items-center gap-1 text-[11px] px-2 py-1.5 rounded-lg font-medium transition-colors hover:bg-blue-50 shrink-0" style={{ color: C.slate500 }}>
        <X size={11}/> Clear
      </button>)}
      <span className="text-[11px] shrink-0 hidden xl:inline" style={{ color: C.slate400 }}>
        {testCases.total.toLocaleString()} cases · {automationScripts.total} scripts
      </span>
    </div>);
}

/** ⌘K palette over the real test cases, scripts and BRDs. */
function CommandPalette({ onClose, onOpenPanel }) {
    const { testCases, automationScripts, traceability } = useTestSuiteData();
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const match = (text) => !q || String(text || "").toLowerCase().includes(q);
        const groups = [];

        const cases = testCases.items.filter((t) => match(t.title) || match(t.id) || match(t.module)).slice(0, 6);
        if (cases.length)
            groups.push({ label: "Test cases", items: cases.map((t) => ({ key: `tc-${t.id}-${t.module}`, title: t.title || t.id, meta: `${t.id} · ${t.module}`, tone: t.type === "Negative" ? C.red : C.green, icon: <FlaskConical size={13}/>, panel: "testcases" })) });

        const scripts = automationScripts.items.filter((s) => match(s.name) || match(s.suite) || match(s.brdName)).slice(0, 5);
        if (scripts.length)
            groups.push({ label: "Automation scripts", items: scripts.map((s) => ({ key: `sc-${s.id}`, title: s.name, meta: `${s.suite} · ${s.status}`, tone: s.status === "passed" ? C.green : s.status === "failed" ? C.red : C.slate500, icon: <Code2 size={13}/>, panel: "scripts" })) });

        const brds = traceability.documents.filter((d) => match(d.name)).slice(0, 5);
        if (brds.length)
            groups.push({ label: "BRDs", items: brds.map((d) => ({ key: `br-${d.id}`, title: d.name, meta: `${d.scripts} scripts · ${d.tests} tests · ${d.status}`, tone: TRACE_STATUS_COLOR[d.status] ?? C.slate500, icon: <FileText size={13}/>, panel: "traceability" })) });

        return groups;
    }, [query, testCases, automationScripts, traceability]);

    const flat = useMemo(() => results.flatMap((g) => g.items), [results]);
    useEffect(() => { setCursor(0); }, [query]);

    const choose = useCallback((item) => {
        if (!item)
            return;
        onClose();
        onOpenPanel(item.panel);
    }, [onClose, onOpenPanel]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); choose(flat[cursor]); }
        else if (e.key === "Escape") { onClose(); }
    };

    let index = -1;
    return (<>
      <div className="fixed inset-0 z-[80]" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed z-[90] left-1/2 top-[12vh] -translate-x-1/2 w-[min(640px,92vw)] rounded-2xl overflow-hidden" style={{ background: C.white, boxShadow: "0 24px 64px rgba(15,23,42,0.28)" }}>
        <div className="flex items-center gap-2.5 px-4 py-3.5" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <Search size={15} style={{ color: C.slate400 }}/>
          <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown} placeholder="Search test cases, scripts and BRDs…" className="flex-1 text-sm bg-transparent outline-none" style={{ color: C.slate800 }}/>
          <kbd className="text-[10px] border rounded px-1.5 py-0.5" style={{ borderColor: C.sidebarBorder, color: C.slate400 }}>Esc</kbd>
        </div>
        <div className="max-h-[52vh] overflow-y-auto">
          {flat.length === 0 ? (<p className="px-4 py-8 text-sm text-center" style={{ color: C.slate400 }}>No matches.</p>) : results.map((group) => (<div key={group.label}>
            <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.slate400 }}>{group.label}</p>
            {group.items.map((item) => {
                index += 1;
                const active = index === cursor;
                const myIndex = index;
                return (<button key={item.key} onMouseEnter={() => setCursor(myIndex)} onClick={() => choose(item)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left" style={{ background: active ? C.blueL : "transparent" }}>
                  <span className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${item.tone}15`, color: item.tone }}>{item.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium truncate" style={{ color: C.slate800 }}>{item.title}</span>
                    <span className="block text-[10px] truncate" style={{ ...mono, color: C.slate400 }}>{item.meta}</span>
                  </span>
                  {active ? <CornerDownLeft size={12} style={{ color: C.slate400 }}/> : null}
                </button>);
            })}
          </div>))}
        </div>
        <div className="px-4 py-2.5 flex items-center gap-3 text-[10px]" style={{ background: C.blueL, borderTop: `1px solid ${C.sidebarBorder}`, color: C.slate500 }}>
          <span>↑↓ navigate</span><span>↵ open</span><span className="ml-auto">{flat.length} result{flat.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </>);
}
// ─── Signed-in user ───────────────────────────────────────────────────────────
// The prototype hardcoded "Sarah Reeves / QA Director". Inside TestSuite AI the
// same slots show whoever is actually logged in, falling back to the prototype's
// placeholders only when the app is rendered without a user.
const userDisplayName = (user) => String(user?.name || user?.email || "").trim() || "Sarah Reeves";
const userRoleLabel = (user) => {
    const role = String(user?.role || "").trim();
    if (!role)
        return "QA Director";
    return role
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};
const userInitials = (user) => {
    const parts = userDisplayName(user).replace(/@.*$/, "").split(/[\s._-]+/).filter(Boolean);
    if (!parts.length)
        return "TS";
    return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
};
/** "Sarah Reeves" → "Sarah R." for the compact top-nav chip. */
const userShortName = (user) => {
    const parts = userDisplayName(user).split(/\s+/).filter(Boolean);
    if (parts.length < 2)
        return parts[0] || "User";
    return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}.`;
};
function Sidebar({ activePage, onNavigate, currentUser, onExit, onLogout }) {
    const [menu, setMenu] = useState(null);
    const closeMenu = useCallback(() => setMenu(null), []);
    // No overflow-hidden on the rail: the Settings and profile popovers have to
    // escape its 220px width. The nav below keeps its own scroll container.
    return (<aside className="hidden lg:flex flex-col w-[220px] shrink-0 h-screen sticky top-0 z-30" style={{ background: C.sidebarBg, borderRight: `1px solid ${C.sidebarBorder}`, boxShadow: "2px 0 16px rgba(37,99,235,0.06)" }}>
      {/* Logo */}
      <div className="px-5 py-5 flex items-center gap-3" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.blue }}>
          <Bot size={15} className="text-white"/>
        </div>
        <div>
          <p className="text-sm font-bold tracking-tight leading-none" style={{ color: C.slate800 }}>TestSuite AI</p>
          <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>QA Reimagined. Smarter.</p>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ icon: Icon, label, page }) => {
            const active = activePage === page;
            return (<button key={label} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-colors text-left" style={active
                    ? { background: C.blueL, color: C.blue, border: `1px solid ${C.blueM}` }
                    : { color: C.sidebarText, border: "1px solid transparent" }} onClick={() => onNavigate(page)} onMouseEnter={(e) => { if (!active) {
                e.currentTarget.style.background = C.blueL;
                e.currentTarget.style.color = C.blue;
            } }} onMouseLeave={(e) => { if (!active) {
                e.currentTarget.style.background = "";
                e.currentTarget.style.color = C.sidebarText;
            } }}>
            <Icon size={15}/>
            {label}
          </button>);
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 pb-4" style={{ borderTop: `1px solid ${C.sidebarBorder}`, paddingTop: 12 }}>
        {/* The dashboard replaces the app header, so this is the way back out. */}
        <button onClick={onExit} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-left mb-1 transition-colors" style={{ color: C.sidebarText }} onMouseEnter={(e) => { e.currentTarget.style.background = C.blueL; e.currentTarget.style.color = C.blue; }} onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.color = C.sidebarText; }}>
          <ArrowLeft size={15}/> Back to workspace
        </button>
        <div className="relative">
          <button onClick={() => setMenu(menu === "settings" ? null : "settings")} aria-haspopup="menu" aria-expanded={menu === "settings"} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-left mb-2 transition-colors" style={menu === "settings" ? { background: C.blueL, color: C.blue } : { color: C.sidebarText }} onMouseEnter={(e) => { e.currentTarget.style.background = C.blueL; e.currentTarget.style.color = C.blue; }} onMouseLeave={(e) => { if (menu !== "settings") { e.currentTarget.style.background = ""; e.currentTarget.style.color = C.sidebarText; } }}>
            <Settings size={15}/> Settings
          </button>
          {menu === "settings" && <SettingsMenu onClose={closeMenu} onExit={onExit}/>}
        </div>
        <div className="relative">
          <button onClick={() => setMenu(menu === "profile" ? null : "profile")} aria-haspopup="menu" aria-expanded={menu === "profile"} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-opacity hover:opacity-90" style={{ background: C.blueL }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: C.blue }}>{userInitials(currentUser)}</div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate" style={{ color: C.slate800 }}>{userDisplayName(currentUser)}</p>
              <p className="text-[10px] truncate" style={{ color: C.slate500 }}>{userRoleLabel(currentUser)}</p>
            </div>
            <ChevronDown size={12} className="shrink-0" style={{ color: C.slate400, transform: menu === "profile" ? "rotate(180deg)" : "none" }}/>
          </button>
          {menu === "profile" && <ProfileMenu currentUser={currentUser} onClose={closeMenu} onExit={onExit} onLogout={onLogout} side="left" below={false}/>}
        </div>
      </div>
    </aside>);
}
// ─── Top Nav ──────────────────────────────────────────────────────────────────
function TopNav({ activePage, currentUser, onExit, onLogout, onOpenPanel }) {
    const pageLabel = activePage === "finances" ? "Finances" : "Dashboard";
    const [menu, setMenu] = useState(null);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const closeMenu = useCallback(() => setMenu(null), []);
    const alerts = useDashboardAlerts();

    // ⌘K / Ctrl+K opens the palette from anywhere on the dashboard.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    return (<header className="sticky top-0 z-20 px-6 h-14 flex items-center gap-4 shrink-0" style={{ background: C.white, borderBottom: `1px solid ${C.sidebarBorder}`, boxShadow: "0 1px 8px rgba(37,99,235,0.05)" }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <span style={{ color: C.slate400 }}>Dashboard</span>
        <span style={{ color: C.slate300 }}>/</span>
        <span className="font-semibold" style={{ color: C.slate800 }}>{pageLabel}</span>
      </div>

      <DashboardFilters />

      <div className="flex-1"/>

      <button onClick={() => setPaletteOpen(true)} aria-label="Search test cases, scripts and BRDs" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs hover:border-slate-300 transition-colors" style={{ borderColor: C.sidebarBorder, color: C.slate400, background: C.blueL }}>
        <Search size={13}/>
        <span className="hidden sm:inline">Search…</span>
        <kbd className="hidden sm:inline ml-1 text-[10px] border rounded px-1" style={{ borderColor: C.sidebarBorder }}>⌘K</kbd>
      </button>

      <div className="relative">
        <button onClick={() => setMenu(menu === "alerts" ? null : "alerts")} aria-haspopup="menu" aria-expanded={menu === "alerts"} aria-label={`Alerts: ${alerts.length}`} className="relative w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: menu === "alerts" ? C.blue : C.slate500, background: menu === "alerts" ? C.blueL : undefined }}>
          <Bell size={16}/>
          {alerts.length > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full border-2 border-white" style={{ background: C.red }}/>}
        </button>
        {menu === "alerts" && <NotificationsMenu onClose={closeMenu} onOpenPanel={onOpenPanel}/>}
      </div>

      <div className="relative">
        <button onClick={() => setMenu(menu === "profile" ? null : "profile")} aria-haspopup="menu" aria-expanded={menu === "profile"} className="flex items-center gap-2 pl-3 py-1" style={{ borderLeft: `1px solid ${C.sidebarBorder}` }}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: C.blue }}>{userInitials(currentUser)}</div>
          <span className="text-xs font-medium hidden sm:inline" style={{ color: C.slate700 }}>{userShortName(currentUser)}</span>
          <ChevronDown size={12} style={{ color: C.slate400, transform: menu === "profile" ? "rotate(180deg)" : "none" }}/>
        </button>
        {menu === "profile" && <ProfileMenu currentUser={currentUser} onClose={closeMenu} onExit={onExit} onLogout={onLogout}/>}
      </div>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} onOpenPanel={onOpenPanel}/>}
    </header>);
}
const TEST_CASES = [
    { id: "TC-001", title: "Valid login with correct credentials", module: "Login", type: "Functional", status: "Ready", priority: "P0", source: "AI", steps: 5, automated: true, createdAt: "Jul 01", updatedAt: "Jul 22" },
    { id: "TC-002", title: "Login fails with wrong password (3 attempts)", module: "Login", type: "Negative", status: "Ready", priority: "P0", source: "AI", steps: 7, automated: true, createdAt: "Jul 01", updatedAt: "Jul 22" },
    { id: "TC-003", title: "MFA TOTP code accepted within 30-second window", module: "Login", type: "Functional", status: "Ready", priority: "P0", source: "AI", steps: 8, automated: true, createdAt: "Jul 02", updatedAt: "Jul 21" },
    { id: "TC-004", title: "Session expires after 30 min inactivity", module: "Login", type: "Functional", status: "Ready", priority: "P1", source: "Manual", steps: 4, automated: true, createdAt: "Jul 02", updatedAt: "Jul 20" },
    { id: "TC-005", title: "OAuth Google login creates account on first use", module: "Login", type: "Integration", status: "Review", priority: "P1", source: "AI", steps: 9, automated: false, createdAt: "Jul 03", updatedAt: "Jul 18" },
    { id: "TC-006", title: "Brute force lockout after 10 failed attempts", module: "Login", type: "Edge Case", status: "Draft", priority: "P1", source: "AI", steps: 12, automated: false, createdAt: "Jul 05", updatedAt: "Jul 15" },
    { id: "TC-007", title: "Successful card payment for single item", module: "Payment", type: "Functional", status: "Ready", priority: "P0", source: "AI", steps: 10, automated: true, createdAt: "Jul 01", updatedAt: "Jul 23" },
    { id: "TC-008", title: "Payment fails on expired card", module: "Payment", type: "Negative", status: "Ready", priority: "P0", source: "AI", steps: 8, automated: true, createdAt: "Jul 01", updatedAt: "Jul 22" },
    { id: "TC-009", title: "3DS2 challenge flow completes successfully", module: "Payment", type: "Integration", status: "Blocked", priority: "P0", source: "AI", steps: 14, automated: false, createdAt: "Jul 08", updatedAt: "Jul 20" },
    { id: "TC-010", title: "Partial refund on multi-item order", module: "Payment", type: "Functional", status: "Review", priority: "P1", source: "Manual", steps: 11, automated: false, createdAt: "Jul 10", updatedAt: "Jul 19" },
    { id: "TC-011", title: "PayPal wallet checkout redirect and return", module: "Payment", type: "Integration", status: "Blocked", priority: "P1", source: "AI", steps: 13, automated: false, createdAt: "Jul 12", updatedAt: "Jul 17" },
    { id: "TC-012", title: "Currency conversion rate applied at checkout", module: "Payment", type: "Functional", status: "Draft", priority: "P2", source: "AI", steps: 6, automated: false, createdAt: "Jul 14", updatedAt: "Jul 14" },
    { id: "TC-013", title: "Add item to cart, update quantity, remove", module: "Checkout", type: "Functional", status: "Ready", priority: "P0", source: "AI", steps: 9, automated: true, createdAt: "Jul 02", updatedAt: "Jul 22" },
    { id: "TC-014", title: "Coupon code reduces price correctly", module: "Checkout", type: "Functional", status: "Ready", priority: "P1", source: "AI", steps: 7, automated: true, createdAt: "Jul 03", updatedAt: "Jul 21" },
    { id: "TC-015", title: "Coupon field hidden by modal on mobile — bug", module: "Checkout", type: "Edge Case", status: "Blocked", priority: "P1", source: "Manual", steps: 5, automated: false, createdAt: "Jul 16", updatedAt: "Jul 24" },
    { id: "TC-016", title: "Guest checkout without account creation", module: "Checkout", type: "Functional", status: "Ready", priority: "P1", source: "AI", steps: 12, automated: true, createdAt: "Jul 04", updatedAt: "Jul 20" },
    { id: "TC-017", title: "Out-of-stock item blocked at checkout", module: "Checkout", type: "Negative", status: "Ready", priority: "P1", source: "AI", steps: 5, automated: true, createdAt: "Jul 05", updatedAt: "Jul 19" },
    { id: "TC-018", title: "Checkout performance <2s on 3G connection", module: "Checkout", type: "Performance", status: "Draft", priority: "P2", source: "AI", steps: 4, automated: false, createdAt: "Jul 18", updatedAt: "Jul 18" },
    { id: "TC-019", title: "Customer profile updates saved correctly", module: "Customer", type: "Functional", status: "Ready", priority: "P1", source: "AI", steps: 6, automated: true, createdAt: "Jul 06", updatedAt: "Jul 22" },
    { id: "TC-020", title: "GDPR deletion removes all PII data", module: "Customer", type: "Functional", status: "Draft", priority: "P0", source: "Manual", steps: 15, automated: false, createdAt: "Jul 20", updatedAt: "Jul 22" },
    { id: "TC-021", title: "Wishlist persists across sessions", module: "Customer", type: "Regression", status: "Ready", priority: "P2", source: "AI", steps: 5, automated: true, createdAt: "Jul 07", updatedAt: "Jul 21" },
    { id: "TC-022", title: "Sales report CSV exports correct data", module: "Reports", type: "Functional", status: "Ready", priority: "P2", source: "AI", steps: 7, automated: true, createdAt: "Jul 08", updatedAt: "Jul 20" },
    { id: "TC-023", title: "Dashboard chart loads within 3 seconds", module: "Reports", type: "Performance", status: "Blocked", priority: "P2", source: "AI", steps: 3, automated: false, createdAt: "Jul 15", updatedAt: "Jul 23" },
    { id: "TC-024", title: "Scheduled report delivered via email", module: "Reports", type: "Integration", status: "Draft", priority: "P2", source: "AI", steps: 8, automated: false, createdAt: "Jul 18", updatedAt: "Jul 18" },
];
const TC_STATUS_CFG = {
    Ready: { bg: C.greenL, fg: C.green },
    Draft: { bg: C.blueL, fg: C.blue },
    Review: { bg: C.orangeL, fg: C.orange },
    Blocked: { bg: C.redL, fg: C.red },
    Deprecated: { bg: C.slate100, fg: C.slate500 },
};
const TC_TYPE_COLOR = {
    Functional: C.blue,
    "Edge Case": C.orange,
    Negative: C.red,
    Integration: C.blue,
    Performance: "#0891B2",
    Regression: C.slate600,
};
function TestCasesPanel({ onClose }) {
    const [search, setSearch] = useState("");
    const [filterModule, setFilterModule] = useState("All");
    const [filterSource, setFilterSource] = useState("All");
    const [filterType, setFilterType] = useState("All");
    const [expandedId, setExpandedId] = useState(null);
    // Real generated test cases from TestSuite AI's own backend.
    const { testCases, loading } = useTestSuiteData();
    const rows = testCases.items;
    const modules = ["All", ...Array.from(new Set(rows.map((t) => t.module)))];
    const sources = ["All", "AI", "Manual"];
    const types = ["All", "Positive", "Negative"];
    const total = testCases.total;
    const positiveCount = testCases.positive;
    const negativeCount = testCases.negative;
    const aiCount = testCases.aiGenerated;
    const manualCount = testCases.manual;
    const filtered = rows.filter((t) => {
        const q = search.toLowerCase();
        const typeMatch = filterType === "All" || (filterType === "Negative" ? t.type === "Negative" : t.type !== "Negative");
        return ((t.title.toLowerCase().includes(q) || t.id.toLowerCase().includes(q) || t.module.toLowerCase().includes(q)) &&
            (filterModule === "All" || t.module === filterModule) &&
            (filterSource === "All" || t.source === filterSource) &&
            typeMatch);
    });
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(1040px, 94vw)", background: C.white, boxShadow: "-4px 0 40px rgba(15,23,42,0.18)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-5 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: C.blueL }}>
              <FlaskConical size={18} style={{ color: C.blue }}/>
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-0.5">
                <h2 className="text-lg font-bold tracking-tight" style={{ color: C.slate900 }}>TEST CASES GENERATED</h2>
                <span className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md text-white" style={{ background: C.blue }}>
                  <Sparkles size={9}/> AI-Powered
                </span>
              </div>
              <p className="text-xs" style={{ color: C.slate500 }}>{total.toLocaleString()} test cases · {testCases.sections} section{testCases.sections === 1 ? "" : "s"} across {new Set(rows.map((t) => t.brdId)).size} BRD{new Set(rows.map((t) => t.brdId)).size === 1 ? "" : "s"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Sparkles size={11} style={{ color: C.blue }}/> Generate More
            </button>
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Download size={11}/> Export
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Summary stat strip */}
        {(() => {
            const pct = (n) => (total ? `${Math.round((n / total) * 100)}%` : "0%");
            const summaryStats = [
                { label: "Positive", value: String(positiveCount), color: C.green, icon: <CheckCircle2 size={13}/>, sub: pct(positiveCount) },
                { label: "Negative", value: String(negativeCount), color: C.red, icon: <XCircle size={13}/>, sub: pct(negativeCount) },
                { label: "Approved", value: String(testCases.approved), color: C.green, icon: <CheckCheck size={13}/>, sub: pct(testCases.approved) },
                { label: "In Review", value: String(testCases.review), color: C.orange, icon: <AlertCircle size={13}/>, sub: pct(testCases.review) },
                { label: "AI Source", value: String(aiCount), color: C.blue, icon: <Bot size={13}/>, sub: `${pct(aiCount)} AI` },
                { label: "Manual", value: String(manualCount), color: C.slate600, icon: <Users size={13}/>, sub: `${pct(manualCount)} manual` },
            ];
            return (<div className="grid grid-cols-6 divide-x shrink-0" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
              {summaryStats.map((s) => (<div key={s.label} className="px-4 py-3 flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${s.color}18`, color: s.color }}>{s.icon}</div>
                  <div>
                    <p className="text-base font-bold leading-none" style={{ ...mono, color: s.color }}>{s.value}</p>
                    <p className="text-[10px] mt-0.5 font-semibold" style={{ color: C.slate600 }}>{s.label}</p>
                    <p className="text-[10px]" style={{ color: C.slate400 }}>{s.sub}</p>
                  </div>
                </div>))}
            </div>);
        })()}

        {/* Filters */}
        {/* Unified filter bar */}
        <div className="flex items-center gap-0 px-8 py-0 shrink-0 min-w-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-3 border-r shrink-0" style={{ borderColor: C.sidebarBorder }}>
            <Search size={13} style={{ color: C.slate400 }}/>
            <input className="text-xs bg-transparent outline-none w-40" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
          </div>
          {/* Module — real section titles can be long, so the list scrolls. */}
          <div className="flex items-center gap-1.5 px-4 py-3 border-r min-w-0 overflow-x-auto" style={{ borderColor: C.sidebarBorder }}>
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1 shrink-0" style={{ color: C.slate400 }}>Module</span>
            {modules.map((m) => (<button key={m} onClick={() => setFilterModule(m)} title={m} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 truncate max-w-[150px]" style={filterModule === m ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{m}</button>))}
          </div>
          {/* Source */}
          <div className="flex items-center gap-1.5 px-4 py-3 border-r shrink-0" style={{ borderColor: C.sidebarBorder }}>
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1" style={{ color: C.slate400 }}>Source</span>
            {sources.map((s) => (<button key={s} onClick={() => setFilterSource(s)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterSource === s ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{s}</button>))}
          </div>
          {/* Type */}
          <div className="flex items-center gap-1.5 px-4 py-3 shrink-0">
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1" style={{ color: C.slate400 }}>Type</span>
            {types.map((t) => (<button key={t} onClick={() => setFilterType(t)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterType === t ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{t}</button>))}
          </div>
          <span className="text-xs ml-auto pr-4 shrink-0" style={{ color: C.slate400 }}>{filtered.length} / {total}</span>
        </div>

        {/* Table header */}
        <div className="grid px-8 py-2.5 text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ gridTemplateColumns: "110px 1fr 150px 80px 52px 68px 80px 1fr", color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <span>ID</span><span>Test Case</span><span>Module</span>
          <span>Type</span><span>Steps</span><span>Source</span><span>Status</span><span>Source BRD</span>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (<div className="flex flex-col items-center justify-center h-40">
              <Info size={22} style={{ color: C.slate300 }}/>
              <p className="text-sm mt-2" style={{ color: C.slate400 }}>{loading ? "Loading test cases…" : "No test cases match your filters"}</p>
            </div>) : filtered.map((tc, idx) => {
            const isNeg = tc.type === "Negative";
            const isOpen = expandedId === `${tc.id}-${idx}`;
            const moduleColor = SUITE_COLOR[tc.module] ?? C.blue;
            return (<div key={`${tc.id}-${idx}`}>
                <div className="grid px-8 items-center cursor-pointer hover:bg-blue-50 transition-colors group" style={{ gridTemplateColumns: "110px 1fr 150px 80px 52px 68px 80px 1fr", paddingTop: 13, paddingBottom: 13, borderBottom: `1px solid ${C.blueL}`, background: isOpen ? C.blueL : undefined }} onClick={() => setExpandedId(isOpen ? null : `${tc.id}-${idx}`)}>
                  <span className="text-[11px] font-medium truncate pr-2" style={{ ...mono, color: C.slate400 }}>{tc.id}</span>

                  <div className="min-w-0 pr-3">
                    <p className="text-xs font-medium truncate group-hover:text-blue-600 transition-colors" style={{ color: C.slate800 }}>{tc.title}</p>
                  </div>

                  <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-md truncate" style={{ background: `${moduleColor}15`, color: moduleColor }} title={tc.module}>{tc.module}</span>

                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: isNeg ? C.redL : C.greenL, color: isNeg ? C.red : C.green }}>
                    {isNeg ? "Negative" : "Positive"}
                  </span>

                  <span className="text-[11px] font-semibold text-center" style={{ ...mono, color: C.slate500 }}>{tc.steps}</span>

                  <div className="flex items-center gap-1">
                    {tc.source === "AI" ? (<span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ background: C.blueL, color: C.blue }}>
                        <Bot size={9}/> AI
                      </span>) : (<span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md" style={{ background: C.blueL, color: C.slate500 }}>Manual</span>)}
                  </div>

                  {/* Review status from the BRD section's approval state */}
                  <div>
                    {tc.status === "Approved" ? (<span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: C.greenL, color: C.green }}>
                        <CheckCircle2 size={9}/> Approved
                      </span>) : tc.status === "Rejected" ? (<span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: C.redL, color: C.red }}>
                        <XCircle size={9}/> Rejected
                      </span>) : (<span className="text-[11px] px-1.5 py-0.5 rounded-md font-medium" style={{ background: C.orangeL, color: C.orange }}>{tc.status}</span>)}
                  </div>

                  <div className="min-w-0 pr-2">
                    <p className="text-[11px] leading-snug truncate" style={{ color: C.slate500 }} title={tc.brdName}>{tc.brdName}</p>
                  </div>
                </div>

                {/* Expanded row */}
                {isOpen && (<div className="px-8 py-4 flex items-start gap-8" style={{ background: `${C.blueL}60`, borderBottom: `1px solid ${C.blueL}` }}>
                    <div className="flex-1 space-y-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Test Case Detail</p>
                        <p className="text-sm font-medium" style={{ color: C.slate800 }}>{tc.title}</p>
                        <p className="text-[11px] mt-1" style={{ color: C.slate500 }}>
                          Module: <strong style={{ color: SUITE_COLOR[tc.module] ?? C.blue }}>{tc.module}</strong> ·
                          {tc.steps} steps · {tc.brdName} § {tc.sectionNumber}
                          {tc.role ? <> · Role: <strong>{tc.role}</strong></> : null}
                          {tc.flow ? <> · Flow: {tc.flow}</> : null}
                        </p>
                      </div>
                      {/* Module breakdown: Positive / Negative / AI / Manual */}
                      <div className="grid grid-cols-4 gap-2 mt-1">
                        {(() => {
                        const modCases = rows.filter((t) => t.module === tc.module);
                        const pos = modCases.filter((t) => t.type !== "Negative").length;
                        const neg = modCases.filter((t) => t.type === "Negative").length;
                        const ai = modCases.filter((t) => t.source === "AI").length;
                        const man = modCases.filter((t) => t.source === "Manual").length;
                        return [
                            { label: "Positive", value: pos, color: C.green, icon: <CheckCircle2 size={11}/> },
                            { label: "Negative", value: neg, color: C.red, icon: <XCircle size={11}/> },
                            { label: "AI", value: ai, color: C.blue, icon: <Bot size={11}/> },
                            { label: "Manual", value: man, color: C.blue, icon: <Users size={11}/> },
                        ].map((kpi) => (<div key={kpi.label} className="rounded-xl p-2.5 flex items-center gap-2" style={{ background: `${kpi.color}12`, border: `1px solid ${kpi.color}20` }}>
                              <span style={{ color: kpi.color }}>{kpi.icon}</span>
                              <div>
                                <p className="text-sm font-bold leading-none" style={{ ...mono, color: kpi.color }}>{kpi.value}</p>
                                <p className="text-[10px] mt-0.5" style={{ color: C.slate500 }}>{kpi.label}</p>
                              </div>
                            </div>));
                    })()}
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.slate400 }}>Test steps</p>
                        <div className="space-y-1">
                          {(tc.stepsList || []).slice(0, 6).map((step, i) => (<div key={i} className="flex items-start gap-2">
                              <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5" style={{ background: C.blueL, color: C.blue }}>{i + 1}</span>
                              <span className="text-[11px]" style={{ color: C.slate600 }}>{step}</span>
                            </div>))}
                          {(tc.stepsList || []).length > 6 && <p className="text-[11px] pl-6" style={{ color: C.slate400 }}>+ {tc.stepsList.length - 6} more steps…</p>}
                        </div>
                      </div>
                      {tc.expectedResult && (<div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Expected result</p>
                        <p className="text-[11px]" style={{ color: C.slate600 }}>{tc.expectedResult}</p>
                      </div>)}
                    </div>
                    <div className="shrink-0 space-y-3 min-w-[170px]">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Automation</p>
                        <div className="flex items-center gap-1.5">
                          {tc.automated
                        ? <><CheckCircle2 size={13} style={{ color: C.green }}/><span className="text-xs font-medium" style={{ color: C.green }}>Script exists</span></>
                        : <><AlertCircle size={13} style={{ color: C.orange }}/><span className="text-xs font-medium" style={{ color: C.orange }}>Not yet automated</span></>}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Source</p>
                        <p className="text-xs font-medium" style={{ color: tc.source === "AI" ? C.blue : C.slate600 }}>{tc.source === "AI" ? "🤖 AI Generated" : "✍️ Manual"}</p>
                      </div>
                      <button className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90" style={{ background: C.blue }}>
                        <Code2 size={11}/> Generate Script
                      </button>
                    </div>
                  </div>)}
              </div>);
        })}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <p className="text-xs" style={{ color: C.slate400 }}>
            <Bot size={11} className="inline mr-1" style={{ color: C.blue }}/>
            {aiCount.toLocaleString()} AI-generated · {manualCount.toLocaleString()} added manually · Showing {filtered.length.toLocaleString()} of {total.toLocaleString()}
          </p>
          <button className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90" style={{ background: C.blue }}>
            Open Test Management <ArrowRight size={12}/>
          </button>
        </div>
      </div>
    </>);
}
const AUTO_SCRIPTS = [
    { id: "AS-001", file: "test_auth_login_valid.py", suite: "Auth Service", status: "passed", duration: "1.2s", lastRun: "Jul 24, 09:41" },
    { id: "AS-002", file: "test_auth_mfa_totp.py", suite: "Auth Service", status: "passed", duration: "2.8s", lastRun: "Jul 24, 09:41" },
    { id: "AS-003", file: "test_auth_oauth_google.py", suite: "Auth Service", status: "passed", duration: "3.4s", lastRun: "Jul 24, 09:42" },
    { id: "AS-004", file: "test_payment_checkout_flow.py", suite: "Payment Flow", status: "failed", duration: "32.1s", failedStep: "assert_order_confirmation", failureReason: "AssertionError: expected 'Order confirmed' but got 'Payment pending' — gateway timeout after 30s", lastRun: "Jul 24, 09:44" },
    { id: "AS-005", file: "test_payment_card_validation.py", suite: "Payment Flow", status: "failed", duration: "8.7s", failedStep: "submit_card_details", failureReason: "TimeoutError: Stripe iframe did not load within 8000ms — possible CSP policy blocking external frame", lastRun: "Jul 24, 09:45" },
    { id: "AS-006", file: "test_payment_refund_partial.py", suite: "Payment Flow", status: "pending", duration: "—", failedStep: "setup_test_env", failureReason: "Docker container failed to start — port 5432 already in use", lastRun: "Jul 24, 09:46" },
    { id: "AS-007", file: "test_user_profile_update.py", suite: "User Profile", status: "passed", duration: "0.9s", lastRun: "Jul 24, 09:47" },
    { id: "AS-008", file: "test_user_address_book.py", suite: "User Profile", status: "passed", duration: "1.4s", lastRun: "Jul 24, 09:47" },
    { id: "AS-009", file: "test_checkout_discount_appl.py", suite: "Checkout", status: "failed", duration: "5.3s", failedStep: "click_apply_coupon", failureReason: "ElementNotInteractableError: coupon input field hidden by overlay modal on mobile viewport", lastRun: "Jul 24, 09:49" },
    { id: "AS-010", file: "test_checkout_inventory_check.py", suite: "Checkout", status: "passed", duration: "2.1s", lastRun: "Jul 24, 09:49" },
    { id: "AS-011", file: "test_checkout_guest_flow.py", suite: "Checkout", status: "passed", duration: "4.6s", lastRun: "Jul 24, 09:50" },
    { id: "AS-012", file: "test_notification_email_send.py", suite: "Notifications", status: "failed", duration: "12.0s", failedStep: "verify_email_delivery", failureReason: "AssertionError: SMTP relay returned 550 5.1.1 — recipient address rejected, sandbox domain not whitelisted", lastRun: "Jul 24, 09:51" },
    { id: "AS-013", file: "test_notification_push_mobile.py", suite: "Notifications", status: "skipped", duration: "—", lastRun: "Jul 24, 09:51" },
    { id: "AS-014", file: "test_smoke_homepage_load.py", suite: "Smoke", status: "pending", duration: "—", failedStep: "setup_test_env", failureReason: "Docker container failed to start — port 5432 already in use", lastRun: "Jul 24, 09:52" },
    { id: "AS-015", file: "test_smoke_search_results.py", suite: "Smoke", status: "passed", duration: "1.8s", lastRun: "Jul 24, 09:52" },
    { id: "AS-016", file: "test_reports_csv_export.py", suite: "Reports", status: "passed", duration: "3.2s", lastRun: "Jul 24, 09:53" },
    { id: "AS-017", file: "test_reports_dashboard_load.py", suite: "Reports", status: "failed", duration: "7.1s", failedStep: "wait_for_chart_render", failureReason: "TimeoutError: Recharts SVG not found in DOM after 5000ms — likely async data fetch still pending", lastRun: "Jul 24, 09:53" },
    { id: "AS-018", file: "test_customer_wishlist.py", suite: "Customer", status: "passed", duration: "1.1s", lastRun: "Jul 24, 09:54" },
];
const SUITE_COLOR = {
    "Auth Service": C.blue,
    "Payment Flow": C.red,
    "Checkout": C.orange,
    "User Profile": C.green,
    "Notifications": C.blueD,
    "Smoke": C.slate600,
    "Reports": "#0891B2",
    "Customer": "#16A34A",
};
const FLAKY_CASES = [
    {
        id: "AS-009",
        file: "test_checkout_discount_appl.py",
        suite: "Checkout",
        commit: "9f3c2a7",
        executions: 5,
        passed: 3,
        failed: 2,
        reason: "The coupon input is intermittently covered by the mobile overlay before its transition completes.",
        signal: "ElementNotInteractableError at click_apply_coupon",
    },
    {
        id: "AS-017",
        file: "test_reports_dashboard_load.py",
        suite: "Reports",
        commit: "9f3c2a7",
        executions: 4,
        passed: 2,
        failed: 2,
        reason: "Chart rendering races the asynchronous report-data request on cold-cache or slow-network runs.",
        signal: "TimeoutError waiting for the Recharts SVG",
    },
];
function StatusIcon({ status }) {
    if (status === "passed")
        return <CheckCircle2 size={16} style={{ color: C.green }}/>;
    if (status === "failed")
        return <XCircle size={16} style={{ color: C.red }}/>;
    if (status === "pending")
        return <Timer size={16} style={{ color: C.orange }}/>;
    return <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold" style={{ background: C.slate200, color: C.slate500 }}>–</span>;
}
function AutomationScriptsPanel({ onClose }) {
    const [search, setSearch] = useState("");
    const [filterSuite, setFilterSuite] = useState("All");
    const [filterStatus, setFilterStatus] = useState("All");
    const [expandedRow, setExpandedRow] = useState(null);
    // Real generated scripts + their latest Playwright run.
    const { automationScripts, loading } = useTestSuiteData();
    const scripts = automationScripts.items;
    const runs = automationScripts.runs;
    const suites = ["All", ...Array.from(new Set(scripts.map((s) => s.suite)))];
    const statuses = ["All", "passed", "failed", "not_run"];
    const total = automationScripts.total;
    const filtered = scripts.filter((s) => {
        const q = search.toLowerCase();
        const matchSearch = s.name.toLowerCase().includes(q) || s.suite.toLowerCase().includes(q) || String(s.brdName || "").toLowerCase().includes(q);
        const matchSuite = filterSuite === "All" || s.suite === filterSuite;
        const matchStatus = filterStatus === "All" || s.status === filterStatus;
        return matchSearch && matchSuite && matchStatus;
    });
    const statusBg = {
        passed: { bg: C.greenL, fg: C.green, label: "Passed" },
        failed: { bg: C.redL, fg: C.red, label: "Failed" },
        pending: { bg: C.orangeL, fg: C.orange, label: "Pending" },
        not_run: { bg: C.slate100, fg: C.slate500, label: "Not run" },
        skipped: { bg: C.slate100, fg: C.slate500, label: "Skipped" },
    };
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{
            width: "min(960px, 92vw)",
            background: C.white,
            boxShadow: "-4px 0 40px rgba(15,23,42,0.18)",
            animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)",
        }}>
        <style>{`
          @keyframes slideInRight { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-5 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: C.blueL }}>
              <Terminal size={18} style={{ color: C.blue }}/>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight" style={{ color: C.slate900 }}>AUTOMATION SCRIPTS EXECUTION</h2>
              <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>
                {total} generated script{total === 1 ? "" : "s"} · {runs.total} recorded run{runs.total === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Play size={11} style={{ color: C.green }}/> Re-run Failed
            </button>
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Download size={11}/> Export
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-5 divide-x shrink-0" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          {[
            { label: "Scripts", value: String(total), color: C.slate800, icon: <Terminal size={13}/> },
            { label: "Runs", value: String(runs.total), color: C.blue, icon: <Play size={13}/> },
            { label: "Passed", value: String(runs.passed), color: C.green, icon: <CheckCheck size={13}/> },
            { label: "Failed", value: String(runs.failed), color: C.red, icon: <XCircle size={13}/> },
            { label: "Pass Rate", value: `${runs.passRate}%`, color: runs.passRate >= 80 ? C.green : runs.passRate >= 60 ? C.orange : C.red, icon: <Activity size={13}/> },
        ].map((s) => (<div key={s.label} className="px-6 py-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${s.color}15`, color: s.color }}>{s.icon}</div>
              <div>
                <p className="text-xl font-bold leading-none" style={{ ...mono, color: s.color }}>{s.value}</p>
                <p className="text-[10px] mt-0.5 font-medium" style={{ color: C.slate400 }}>{s.label}</p>
              </div>
            </div>))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 px-8 py-3 mt-2 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border flex-1 max-w-xs" style={{ borderColor: C.sidebarBorder, background: C.blueL }}>
            <Search size={13} style={{ color: C.slate400 }}/>
            <input className="flex-1 text-xs bg-transparent outline-none" placeholder="Search scripts…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs" style={{ color: C.slate400 }}>Suite:</span>
            {suites.map((s) => (<button key={s} onClick={() => setFilterSuite(s)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterSuite === s ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>
                {s}
              </button>))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs" style={{ color: C.slate400 }}>Status:</span>
            {statuses.map((s) => (<button key={s} onClick={() => setFilterStatus(s)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors capitalize" style={filterStatus === s ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>
                {s}
              </button>))}
          </div>
          <span className="text-xs shrink-0 ml-auto" style={{ color: C.slate400 }}>{filtered.length} of {total}</span>
        </div>

        {/* Table header */}
        <div className="grid px-8 py-2.5 text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ gridTemplateColumns: "90px 1fr 140px 78px 120px 1fr", color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <span>Type</span>
          <span>Script</span>
          <span>Suite</span>
          <span>Status</span>
          <span>Test Case</span>
          <span>Failure Reason</span>
        </div>

        {/* Table rows */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (<div className="flex flex-col items-center justify-center h-40">
              <Info size={22} style={{ color: C.slate300 }}/>
              <p className="text-sm mt-2" style={{ color: C.slate400 }}>{loading ? "Loading scripts…" : "No automation scripts generated yet"}</p>
            </div>)}
          {filtered.map((script) => {
            const sb = statusBg[script.status] ?? statusBg.not_run;
            const isOpen = expandedRow === script.id;
            return (<div key={script.id}>
                <div className="grid px-8 items-start cursor-pointer transition-colors hover:bg-blue-50 group" style={{
                    gridTemplateColumns: "90px 1fr 140px 78px 120px 1fr",
                    borderBottom: `1px solid ${C.slate50}`,
                    paddingTop: 14, paddingBottom: 14,
                    background: isOpen ? C.slate50 : undefined,
                }} onClick={() => setExpandedRow(isOpen ? null : script.id)}>
                  {/* Framework */}
                  <span className="text-[11px] font-medium capitalize" style={{ ...mono, color: C.slate400 }}>{script.kind}</span>

                  {/* File */}
                  <div className="flex items-center gap-1.5 min-w-0 pr-3">
                    <FileText size={12} className="shrink-0" style={{ color: C.slate300 }}/>
                    <span className="text-xs font-medium truncate group-hover:text-blue-600 transition-colors" style={{ ...mono, color: C.blue }}>
                      {script.name}
                    </span>
                  </div>

                  {/* Suite */}
                  <div>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md" style={{ background: `${SUITE_COLOR[script.suite] ?? C.slate400}15`, color: SUITE_COLOR[script.suite] ?? C.slate500 }}>
                      {script.suite}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-1.5">
                    <StatusIcon status={script.status}/>
                    <span className="text-[11px] font-semibold capitalize" style={{ color: sb.fg }}>{sb.label}</span>
                  </div>

                  {/* Test case exercised by the latest run */}
                  <div className="pr-3">
                    {script.testCaseId ? (<span className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{ ...mono, background: C.blueL, color: C.blue }}>
                        {script.testCaseId}
                      </span>) : (<span style={{ color: C.slate300 }}>—</span>)}
                  </div>

                  {/* Failure reason */}
                  <div className="min-w-0">
                    {script.failureReason ? (<p className="text-[11px] leading-snug" style={{ color: C.red }}>
                        {isOpen ? script.failureReason : `${script.failureReason.slice(0, 72)}${script.failureReason.length > 72 ? "…" : ""}`}
                      </p>) : (<span style={{ color: C.slate300 }}>—</span>)}
                  </div>
                </div>

                {/* Expanded detail row */}
                {isOpen && script.status !== "passed" && (<div className="px-8 py-4 flex items-start gap-8" style={{ background: "#FFFBFB", borderBottom: `1px solid ${C.redL}` }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.slate400 }}>Full Error</p>
                      <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-all p-3 rounded-xl" style={{ ...mono, background: C.blueL, color: C.red, border: `1px solid ${C.blueM}` }}>
                        {script.failureReason ?? "No error details available."}
                      </pre>
                    </div>
                    <div className="shrink-0 space-y-3 min-w-[160px]">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Duration</p>
                        <p className="text-sm font-bold" style={{ ...mono, color: C.slate700 }}>{script.durationMs ? `${(script.durationMs / 1000).toFixed(1)}s` : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Runs recorded</p>
                        <p className="text-xs" style={{ color: C.slate600 }}>{script.runCount} · {script.screenshots} screenshots</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Source BRD</p>
                        <p className="text-xs" style={{ color: C.slate600 }}>{script.brdName}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Generated by</p>
                        <p className="text-xs" style={{ color: C.slate600 }}>{script.model || "—"} · {script.warnings} warning{script.warnings === 1 ? "" : "s"}</p>
                      </div>
                    </div>
                  </div>)}
              </div>);
        })}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <p className="text-xs" style={{ color: C.slate400 }}>
            <Clock size={11} className="inline mr-1"/> {runs.passed} passed · {runs.failed} failed across {runs.total} Playwright run{runs.total === 1 ? "" : "s"}
          </p>
        </div>
      </div>
    </>);
}
// ─── Flaky Test Rate Panel ────────────────────────────────────────────────────
function FlakyTestRatePanel({ onClose }) {
    const { flaky, automationScripts } = useTestSuiteData();
    const scriptFor = (brdId) => automationScripts.items.find((s) => s.brdId === brdId);
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(860px, 92vw)", background: C.white, boxShadow: "-4px 0 40px rgba(15,23,42,0.18)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        <div className="flex items-center justify-between px-8 pt-6 pb-5 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: C.orangeL }}>
              <Activity size={19} style={{ color: C.orange }}/>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight" style={{ color: C.slate900 }}>FLAKY TEST RATE</h2>
              <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>Playwright run history · {flaky.totalRuns} run{flaky.totalRuns === 1 ? "" : "s"} across {flaky.testsWithRuns} test case{flaky.testsWithRuns === 1 ? "" : "s"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
            <X size={16}/>
          </button>
        </div>

        <div className="grid grid-cols-3 divide-x shrink-0" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          {[
            { label: "Flaky tests", value: String(flaky.flakyTests), color: C.orange },
            { label: "Recorded executions", value: String(flaky.totalRuns), color: C.blue },
            { label: "Flaky test rate", value: `${flaky.rate}%`, color: C.orange },
        ].map((stat) => (<div key={stat.label} className="px-6 py-4">
              <p className="text-2xl font-bold leading-none" style={{ ...mono, color: stat.color }}>{stat.value}</p>
              <p className="text-[10px] mt-1 font-semibold" style={{ color: C.slate500 }}>{stat.label}</p>
            </div>))}
        </div>

        <div className="mx-8 mt-5 rounded-xl px-4 py-3" style={{ background: C.orangeL, border: "1px solid #FDE68A" }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.orange }}>Formula</p>
          <p className="text-xs mt-1" style={{ color: C.slate700 }}>
            Test cases whose recorded Playwright runs contain both a pass and a fail ÷ test cases that have been run at all × 100.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold" style={{ color: C.slate900 }}>Flagged flaky cases</h3>
            <span className="text-[11px]" style={{ color: C.slate500 }}>Both pass and fail recorded for the same test case</span>
          </div>
          <div className="space-y-3">
            {flaky.items.length === 0 && (<p className="text-sm py-8 text-center" style={{ color: C.slate400 }}>
              No test case has both passed and failed — nothing flaky in the recorded runs.
            </p>)}
            {flaky.items.map((test) => {
                const script = scriptFor(test.brdId);
                return (<div key={`${test.brdId}-${test.testCaseId}`} className="rounded-2xl p-4" style={{ border: `1px solid ${C.sidebarBorder}`, background: C.white }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-md" style={{ ...mono, color: C.orange, background: C.orangeL }}>{test.testCaseId}</span>
                      {script ? <span className="text-xs font-semibold" style={{ ...mono, color: C.blue }}>{script.name}</span> : null}
                      {script ? <span className="text-[10px] font-medium px-2 py-0.5 rounded-md" style={{ color: C.blue, background: C.blueL }}>{script.suite}</span> : null}
                    </div>
                    <p className="text-xs mt-3 leading-relaxed" style={{ color: C.slate700 }}>
                      This test case flipped outcome across its recorded runs — {test.passed} passed and {test.failed} failed on the same script.
                    </p>
                  </div>
                  <span className="text-[10px] font-medium shrink-0 px-2 py-1 rounded-md" style={{ ...mono, color: C.slate500, background: C.blueL }}>{test.flipRate}% flip</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[
                { label: "Executions", value: test.runs, color: C.slate700, bg: C.blueL },
                { label: "Passed", value: test.passed, color: C.green, bg: C.greenL },
                { label: "Failed", value: test.failed, color: C.red, bg: C.redL },
            ].map((run) => (<div key={run.label} className="rounded-lg px-3 py-2" style={{ background: run.bg }}>
                      <p className="text-lg font-bold leading-none" style={{ ...mono, color: run.color }}>{run.value}</p>
                      <p className="text-[10px] mt-1" style={{ color: C.slate500 }}>{run.label}</p>
                    </div>))}
                </div>
                {script?.failureReason ? (<div className="mt-3 flex items-start gap-2 text-[11px]" style={{ color: C.orange }}>
                  <AlertTriangle size={13} className="shrink-0 mt-0.5"/>
                  <span><strong>Latest failure:</strong> {script.failureReason}</span>
                </div>) : null}
              </div>);
            })}
          </div>
        </div>

        <div className="px-8 py-4 shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <p className="text-xs" style={{ color: C.slate500 }}>A test is flagged when its stored Playwright runs contain both a passed and a failed outcome for the same test case id. Retries count as executions.</p>
        </div>
      </div>
    </>);
}
// ─── ROW 1 — KPI Cards ────────────────────────────────────────────────────────
function KpiCard({ icon, value, label, sub, bar, color = C.blue, onClick }) {
    return (<Card className="p-4 flex flex-col justify-between group" onClick={onClick}>
      <div className="flex items-start justify-between">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}15` }}>
          {icon}
        </div>
        <ChevronRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" style={{ color }}/>
      </div>
      <div className="mt-2">
        <p className="text-2xl font-bold tracking-tight leading-none" style={{ ...mono, color: C.slate900 }}>{value}</p>
        <p className="text-[11px] font-semibold mt-1 leading-tight" style={{ color: C.slate700 }}>{label}</p>
        <p className="text-[10px] mt-0.5 leading-tight" style={{ color: C.slate400 }}>{sub}</p>
      </div>
      {bar !== undefined && (<div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: `${color}20` }}>
          <div className="h-full rounded-full" style={{ width: `${bar}%`, background: color }}/>
        </div>)}
    </Card>);
}
function ReleaseReadiness() {
    // The end of the real delivery funnel: sections that are generated,
    // reviewed, scripted, and whose latest run passed.
    const { pipeline } = useTestSuiteData();
    const pct = pipeline.releaseReadinessPct;
    const ready = pipeline.stages.find((s) => s.id === "passing")?.count ?? 0;
    return <KpiCard icon={<ShieldCheck size={15} style={{ color: C.blue }}/>} value={`${pct}%`} label="Release Readiness" sub={`${ready} of ${pipeline.totalSections} sections reviewed, scripted and passing`} bar={pct}/>;
}
function ReqCoverage() {
    // BRD *section* coverage: this product has no requirements entity, so the
    // unit of coverage is the BRD section that test cases are generated from.
    const { sectionCoverage } = useTestSuiteData();
    const { coveragePct, breakdown, totalSections } = sectionCoverage;
    return (<KpiCard icon={<Target size={15} style={{ color: C.blue }}/>} value={`${coveragePct}%`} label="BRD Sections Covered" sub={`${breakdown.Covered.toLocaleString()} / ${totalSections.toLocaleString()} sections have test cases`} bar={coveragePct}/>);
}
function TestCasesKPI({ onOpen }) {
    const { testCases } = useTestSuiteData();
    const aiPct = testCases.total ? Math.round((testCases.aiGenerated / testCases.total) * 100) : 0;
    return <KpiCard icon={<FlaskConical size={15} style={{ color: C.blue }}/>} value={testCases.total.toLocaleString()} label="Test Cases Generated" sub={`${aiPct}% AI-generated across ${testCases.sections} section${testCases.sections === 1 ? "" : "s"}`} bar={aiPct} onClick={onOpen}/>;
}
function AutoScriptsKPI({ onOpen }) {
    const { automationScripts } = useTestSuiteData();
    const { runs } = automationScripts;
    return <KpiCard icon={<Code2 size={15} style={{ color: C.blue }}/>} value={automationScripts.total.toLocaleString()} label="Automation Scripts" sub={runs.total ? `${runs.passRate}% pass rate over ${runs.total} run${runs.total === 1 ? "" : "s"}` : "No runs recorded yet"} bar={runs.passRate} onClick={onOpen}/>;
}
function FlakyTestRateKPI() {
    const [open, setOpen] = useState(false);
    const { flaky } = useTestSuiteData();
    // Rate is over tests that have actually been run — with a small sample the
    // sub-label says so rather than implying a fleet-wide number.
    const sub = flaky.testsWithRuns
        ? `${flaky.flakyTests} of ${flaky.testsWithRuns} executed test${flaky.testsWithRuns === 1 ? "" : "s"} both passed and failed`
        : "No automation runs recorded yet";
    return (<>
      <KpiCard icon={<Activity size={15} style={{ color: C.orange }}/>} value={`${flaky.rate}%`} label="Flaky Test Rate" sub={sub} bar={flaky.rate} color={C.orange} onClick={() => setOpen(true)}/>
      {open && <FlakyTestRatePanel onClose={() => setOpen(false)}/>}
    </>);
}
const CRITICAL_ISSUES = [
    { id: "ISS-001", title: "3DS2 payment flow throws 500 on retry after timeout", module: "Payment", severity: "P0", status: "Open", type: "Regression", assignee: "Priya M.", reported: "Jul 22", description: "After a gateway timeout, retrying the 3DS2 authentication flow causes an unhandled 500 error. Affects ~12% of payment sessions on mobile.", failedTest: "TC-009 · test_payment_checkout_flow.py" },
    { id: "ISS-002", title: "GDPR data deletion leaves PII in audit log table", module: "Customer", severity: "P0", status: "In Progress", type: "Compliance", assignee: "Rahul S.", reported: "Jul 21", description: "The right-to-erasure endpoint wipes the user profile but does not purge rows from the audit_log table. Violates GDPR Article 17 retention rules.", failedTest: "TC-020 · No automation yet" },
    { id: "ISS-003", title: "Stripe iframe blocked by CSP — card entry unusable", module: "Payment", severity: "P0", status: "Open", type: "Functional", assignee: "Wei L.", reported: "Jul 23", description: "A strict Content-Security-Policy header prevents the Stripe iframe from loading in Chrome 126+. Users cannot enter card details.", failedTest: "TC-008 · test_payment_card_validation.py" },
    { id: "ISS-004", title: "Fraud detection scoring API returns 503 in EU region", module: "Payment", severity: "P1", status: "Open", type: "Integration", assignee: "Priya M.", reported: "Jul 20", description: "The third-party fraud scoring service is unreachable from EU-West-1. All EU checkouts skip fraud checks silently, creating financial exposure.", failedTest: "REQ-207 · No test case mapped" },
    { id: "ISS-005", title: "Coupon modal overlaps input field on 375px viewport", module: "Checkout", severity: "P1", status: "In Progress", type: "UI / Layout", assignee: "Amara K.", reported: "Jul 24", description: "On iPhone SE and similar narrow viewports, the promotional modal renders over the coupon code input, making it impossible to apply discounts.", failedTest: "TC-015 · test_checkout_discount_appl.py" },
    { id: "ISS-006", title: "Session token not invalidated after password reset", module: "Login", severity: "P1", status: "Open", type: "Security", assignee: "Rahul S.", reported: "Jul 19", description: "Existing session tokens remain valid after a password reset. An attacker who has captured a token can continue to impersonate the user.", failedTest: "REQ-003 · No automation yet" },
    { id: "ISS-007", title: "Reports dashboard SVG not rendered — data fetch race", module: "Reports", severity: "P1", status: "Open", type: "Performance", assignee: "Wei L.", reported: "Jul 24", description: "Recharts SVG is mounted before async data resolves, leaving an empty chart. Reproducible 100% on slow 3G connections or cold cache loads.", failedTest: "TC-023 · test_reports_dashboard_load.py" },
];
const ISSUE_STATUS_CFG = {
    "Open": { bg: C.redL, fg: C.red },
    "In Progress": { bg: C.blueL, fg: C.blue },
    "Resolved": { bg: C.greenL, fg: C.green },
    "Won't Fix": { bg: "#F1F5F9", fg: "#64748B" },
};
const ISSUE_TYPE_COLOR = {
    Regression: C.red, Compliance: "#7C3AED", Functional: C.blue,
    Integration: C.blueD, "UI / Layout": C.orange, Security: "#B91C1C", Performance: C.slate500,
};
function CriticalIssuesPanel({ onClose }) {
    const [search, setSearch] = useState("");
    const [filterKind, setFilterKind] = useState("All");
    const [expandedId, setExpandedId] = useState(null);
    // Real failures only: failed Playwright runs and failed generation jobs,
    // grouped by error signature. There is no severity/assignee data in the
    // product, so those columns are gone rather than invented.
    const { criticalIssues, loading } = useTestSuiteData();
    const issues = criticalIssues.items;
    const kinds = ["All", ...Array.from(new Set(issues.map((i) => i.kind)))];
    const filtered = issues.filter((i) => {
        const q = search.toLowerCase();
        return ((i.title.toLowerCase().includes(q) || i.brds.join(" ").toLowerCase().includes(q)) &&
            (filterKind === "All" || i.kind === filterKind));
    });
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(1020px, 94vw)", background: C.white, boxShadow: "-4px 0 40px rgba(15,23,42,0.18)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(60px); opacity:0 } to { transform: translateX(0); opacity:1 } }`}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-5 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: C.redL }}>
              <AlertTriangle size={18} style={{ color: C.red }}/>
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-0.5">
                <h2 className="text-lg font-bold tracking-tight" style={{ color: C.slate900 }}>OPEN FAILURES</h2>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-md text-white" style={{ background: C.red }}>
                  {criticalIssues.total} distinct
                </span>
              </div>
              <p className="text-xs" style={{ color: C.slate500 }}>Grouped by error signature · {criticalIssues.runFailures} failed run{criticalIssues.runFailures === 1 ? "" : "s"} · {criticalIssues.generationFailures} failed generation{criticalIssues.generationFailures === 1 ? "" : "s"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
            <X size={16}/>
          </button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 divide-x shrink-0" style={{ background: C.blueL, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          {[
            { label: "Distinct failures", value: String(criticalIssues.total), color: C.slate800, icon: <AlertTriangle size={13}/>, sub: "Unique error signatures" },
            { label: "Run failures", value: String(criticalIssues.runFailures), color: C.red, icon: <XCircle size={13}/>, sub: "Playwright executions" },
            { label: "Generation failures", value: String(criticalIssues.generationFailures), color: C.orange, icon: <Bot size={13}/>, sub: "Test-case generation jobs" },
        ].map((s) => (<div key={s.label} className="px-5 py-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${s.color}15`, color: s.color }}>{s.icon}</div>
              <div>
                <p className="text-xl font-bold leading-none" style={{ ...mono, color: s.color }}>{s.value}</p>
                <p className="text-[10px] mt-0.5 font-semibold" style={{ color: C.slate600 }}>{s.label}</p>
                <p className="text-[10px]" style={{ color: C.slate400 }}>{s.sub}</p>
              </div>
            </div>))}
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-0 px-8 py-0 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          <div className="flex items-center gap-2 px-3 py-3 border-r" style={{ borderColor: C.sidebarBorder }}>
            <Search size={13} style={{ color: C.slate400 }}/>
            <input className="text-xs bg-transparent outline-none w-44" placeholder="Search failures…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
          </div>
          <div className="flex items-center gap-1.5 px-4 py-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1" style={{ color: C.slate400 }}>Source</span>
            {kinds.map((k) => (<button key={k} onClick={() => setFilterKind(k)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterKind === k ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{k}</button>))}
          </div>
          <span className="text-xs ml-auto pr-4 shrink-0" style={{ color: C.slate400 }}>{filtered.length} / {issues.length}</span>
        </div>

        {/* Table header */}
        <div className="grid px-8 py-2.5 text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ gridTemplateColumns: "130px 1fr 90px 1fr", color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <span>Source</span><span>Error</span><span>Occurrences</span><span>Affected BRD</span>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (<div className="flex flex-col items-center justify-center h-40">
            <CheckCircle2 size={22} style={{ color: C.green }}/>
            <p className="text-sm mt-2" style={{ color: C.slate400 }}>{loading ? "Loading failures…" : "No failures recorded."}</p>
          </div>)}
          {filtered.map((issue) => {
            const isOpen = expandedId === issue.id;
            const tone = issue.kind === "Generation" ? C.orange : C.red;
            return (<div key={issue.id}>
                <div className="grid px-8 items-center cursor-pointer hover:bg-blue-50 transition-colors group" style={{ gridTemplateColumns: "130px 1fr 90px 1fr", paddingTop: 14, paddingBottom: 14, borderBottom: `1px solid ${C.blueL}`, background: isOpen ? C.blueL : undefined }} onClick={() => setExpandedId(isOpen ? null : issue.id)}>
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md justify-self-start" style={{ background: `${tone}15`, color: tone }}>{issue.kind}</span>
                  <div className="min-w-0 pr-4">
                    <p className="text-xs font-medium truncate group-hover:text-blue-600 transition-colors" style={{ color: C.slate800 }}>{issue.title}</p>
                  </div>
                  <span className="text-[11px] font-bold" style={{ ...mono, color: tone }}>{issue.occurrences}x</span>
                  <span className="text-[11px] truncate pr-2" style={{ color: C.slate500 }}>{issue.brds.join(", ")}</span>
                </div>

                {isOpen && (<div className="px-8 py-5 flex items-start gap-8" style={{ background: "#FEF6F6", borderBottom: `1px solid ${C.redL}` }}>
                    <div className="flex-1 space-y-4 min-w-0">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate400 }}>Error</p>
                        <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words p-3 rounded-xl" style={{ ...mono, background: C.white, color: tone, border: `1px solid ${C.sidebarBorder}` }}>{issue.title}</pre>
                      </div>
                      {issue.testCases.length > 0 && (<div>
                          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.slate400 }}>Affected test cases</p>
                          <div className="flex flex-wrap gap-1.5">
                            {issue.testCases.map((tc) => (<span key={tc} className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg" style={{ ...mono, background: C.blueL, color: C.blue }}>
                              <FlaskConical size={11}/> {tc}
                            </span>))}
                          </div>
                        </div>)}
                    </div>
                    <div className="shrink-0 space-y-3 min-w-[180px]">
                      {[
                        { label: "Occurrences", value: `${issue.occurrences}` },
                        { label: "Source", value: issue.kind },
                        { label: "Last seen", value: String(issue.lastSeen || "").slice(0, 16).replace("T", " ") || "—" },
                      ].map((k) => (<div key={k.label} className="rounded-xl p-2.5" style={{ background: C.blueL }}>
                        <p className="text-[9px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: C.slate400 }}>{k.label}</p>
                        <p className="text-xs font-bold" style={{ color: C.slate700 }}>{k.value}</p>
                      </div>))}
                      <div className="rounded-xl p-2.5" style={{ background: C.blueL }}>
                        <p className="text-[9px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: C.slate400 }}>Affected BRDs</p>
                        <p className="text-xs font-bold" style={{ color: C.slate700 }}>{issue.brds.join(", ") || "—"}</p>
                      </div>
                    </div>
                  </div>)}
              </div>);
        })}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <p className="text-xs" style={{ color: C.slate400 }}>
            <AlertTriangle size={11} className="inline mr-1" style={{ color: C.red }}/>
            Derived from failed Playwright runs and failed generation jobs. Identical errors are grouped into one row.
          </p>
        </div>
      </div>
    </>);
}
function CriticalIssuesKPI() {
    const [open, setOpen] = useState(false);
    const { criticalIssues } = useTestSuiteData();
    const sub = criticalIssues.total
        ? `${criticalIssues.runFailures} run · ${criticalIssues.generationFailures} generation failures`
        : "No failures recorded";
    return (<>
      <KpiCard icon={<AlertTriangle size={15} style={{ color: C.red }}/>} value={String(criticalIssues.total)} label="Open Failures" sub={sub} color={C.red} onClick={() => setOpen(true)}/>
      {open && <CriticalIssuesPanel onClose={() => setOpen(false)}/>}
    </>);
}
// ─── ROW 2 Widget 1 — Release Funnel ─────────────────────────────────────────
const SOURCE_TILE_STYLE = {
    brd: { icon: FileText, color: C.blue },
    images: { icon: ImageIcon, color: C.orange },
    excel: { icon: FileText, color: C.green },
};
const FUNNEL_STAGE_COLOR = [C.blue, C.purple, C.orange, C.green, C.green];

function ReleaseFunnel() {
    const [hov, setHov] = useState(null);
    const stageIcons = [FileText, ListChecks, Eye, Code2, Rocket];
    // Real ingested artifacts and the real delivery funnel, both from TestSuite AI.
    const { inputSources, pipeline, live } = useTestSuiteData();
    const sourceTiles = inputSources.tiles.map((tile) => ({
        ...tile,
        icon: (SOURCE_TILE_STYLE[tile.id] || {}).icon || FileText,
        color: (SOURCE_TILE_STYLE[tile.id] || {}).color || C.blue,
        isLive: live,
    }));
    const totalFiles = inputSources.totalArtifacts;
    const stages = pipeline.stages.map((stage, i) => ({
        ...stage,
        color: FUNNEL_STAGE_COLOR[i] || C.blue,
    }));
    return (<Card className="p-3 flex flex-col">
      <div className="rounded-xl p-3" style={{ background: "linear-gradient(135deg,#FBFAFF,#F4F7FF)", border: `1px solid ${C.blueM}` }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-1.5"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.purple }}>Input Sources</span><span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: C.purpleL, color: C.purple }}>Testing Lifecycle - IP</span><LiveBadge /><Info size={11} style={{ color: C.slate400 }}/></div>
            <p className="text-[10px] mt-1" style={{ color: C.slate500 }}>Requirement artifacts ingested from connected sources</p>
          </div>
          <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 shrink-0" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: C.purpleL }}><FolderKanban size={11} style={{ color: C.purple }}/></div>
            <div><p className="text-sm font-bold leading-none" style={{ ...mono, color: C.slate900 }}>{totalFiles}</p><p className="text-[9px] mt-0.5" style={{ color: C.slate500 }}>Files</p></div>
          </div>
        </div>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(sourceTiles.length, 1)}, minmax(0, 1fr))` }}>
          {sourceTiles.map(({ id, label, icon: Icon, count, color, state, detail, isLive }) => (<div key={id} className="rounded-lg p-2" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }} title={detail}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center mb-1.5" style={{ background: `${color}15` }}><Icon size={14} style={{ color }}/></div>
              <p className="text-[9px] font-semibold leading-tight min-h-5" style={{ color: C.slate700 }}>{label}</p>
              <p className="text-base leading-none mt-1 font-bold" style={{ ...mono, color }}>{count}</p>
              <p className="text-[9px] mt-1 flex items-center gap-1" style={{ color: isLive && count > 0 ? C.green : C.slate400 }}>
                <CheckCircle2 size={9}/> {state}
              </p>
            </div>))}
        </div>
      </div>

      <div className="flex justify-center py-1"><div className="flex flex-col items-center"><div className="w-px h-2" style={{ background: C.purple }}/><ArrowRight size={11} className="rotate-90" style={{ color: C.purple }}/></div></div>

      <div className="space-y-1.5">
        {stages.map((stage, i) => {
            const isHov = hov === i;
            const Icon = stageIcons[i];
            return (<div key={`funnel-${i}`}>
              <button className="w-full text-left" onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}>
                <div className="grid grid-cols-[26px_34px_minmax(0,1fr)_52px_74px_38px] gap-2 items-center rounded-xl px-3 py-2.5 transition-all" style={{ background: isHov ? `${stage.color}18` : `${stage.color}0D`, border: `1px solid ${stage.color}26`, boxShadow: isHov ? `0 0 0 2px ${stage.color}20` : "none" }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: stage.color, color: C.white }}>{i + 1}</span>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stage.color}15` }}><Icon size={16} style={{ color: stage.color }}/></span>
                  <span className="min-w-0"><span className="block text-[11px] font-bold truncate" style={{ color: C.slate800 }}>{stage.label}</span><span className="block text-[9px] truncate mt-0.5" style={{ color: C.slate500 }}>{stage.sub}</span></span>
                  <span className="text-right"><span className="block text-sm font-bold leading-none" style={{ ...mono, color: stage.color }}>{stage.count}</span><span className="text-[9px]" style={{ color: C.slate500 }}>Total</span></span>
                  <span className="min-w-0"><div className="h-1.5 rounded-full overflow-hidden" style={{ background: `${stage.color}24` }}><div className="h-full rounded-full" style={{ width: `${stage.pct}%`, background: stage.color }}/></div><span className="block text-[9px] mt-1 truncate" style={{ color: C.slate500 }}>of all sections</span></span>
                  <span className="text-[10px] font-bold text-center px-1 py-0.5 rounded-full" style={{ background: `${stage.color}15`, color: stage.color }}>{stage.pct}%</span>
                </div>
              </button>
              {i < stages.length - 1 && <div className="flex justify-center -my-0.5"><div className="w-px h-2 border-l border-dashed" style={{ borderColor: C.slate300 }}/></div>}
            </div>);
        })}
      </div>
    </Card>);
}
// ─── ROW 2 Widget 2 — Donut Chart ────────────────────────────────────────────
function ReqDonut() {
    const [hov, setHov] = useState(null);
    const [workspaceStatus, setWorkspaceStatus] = useState(null);
    const { sectionCoverage } = useTestSuiteData();
    const summary = { coveragePct: sectionCoverage.coveragePct, totalRequirements: sectionCoverage.totalSections };
    // Slices carry status, not colour — the palette stays a UI concern.
    const slices = sectionCoverage.slices.map((s) => ({ ...s, color: COV_STATUS_CFG[s.status].fg }));
    const SIZE = 130, CX = SIZE / 2, CY = SIZE / 2, OR = 54, IR = 36, GAP = 0.04;
    const total = slices.reduce((s, d) => s + d.pct, 0) || 1;
    let theta = -Math.PI / 2;
    const arcs = slices.map((d, idx) => {
        const sweep = (d.pct / total) * Math.PI * 2;
        const sa = theta + GAP, ea = theta + sweep - GAP;
        theta += sweep;
        const lg = sweep - 2 * GAP > Math.PI ? 1 : 0;
        const cos = Math.cos, sin = Math.sin;
        return {
            idx, color: d.color, pct: d.pct, count: d.count, label: d.label,
            path: [
                `M ${CX + OR * cos(sa)} ${CY + OR * sin(sa)}`,
                `A ${OR} ${OR} 0 ${lg} 1 ${CX + OR * cos(ea)} ${CY + OR * sin(ea)}`,
                `L ${CX + IR * cos(ea)} ${CY + IR * sin(ea)}`,
                `A ${IR} ${IR} 0 ${lg} 0 ${CX + IR * cos(sa)} ${CY + IR * sin(sa)} Z`,
            ].join(" "),
        };
    });
    const active = hov !== null ? slices[hov] : null;
    return (<>
      <Card className="p-4 flex flex-col">
        <CardHeader title="BRD Section Coverage" sub={`${summary.totalRequirements.toLocaleString()} sections across ${sectionCoverage.documents.length} BRD${sectionCoverage.documents.length === 1 ? "" : "s"} · Click a slice for details`} action={<button onClick={(e) => { e.stopPropagation(); setWorkspaceStatus("All"); }} className="flex items-center gap-1 text-xs font-medium hover:gap-1.5 transition-all" style={{ color: C.blue }}>
              View all <ChevronRight size={11}/>
            </button>}/>
        <div className="flex items-center gap-6 flex-1">
          <div className="relative shrink-0 group/donut" style={{ width: SIZE, height: SIZE }}>
            <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: "block" }}>
              {arcs.map((arc) => (<path key={`donut-arc-${arc.idx}`} d={arc.path} fill={arc.color} opacity={hov === null || hov === arc.idx ? 1 : 0.2} style={{
                cursor: "pointer",
                transition: "opacity 0.15s, transform 0.15s",
                transformOrigin: `${CX}px ${CY}px`,
                transform: hov === arc.idx ? "scale(1.05)" : "scale(1)",
            }} onMouseEnter={() => setHov(arc.idx)} onMouseLeave={() => setHov(null)} onClick={() => setWorkspaceStatus(arc.label)}/>))}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              {active ? (<>
                  <span className="text-lg font-bold" style={{ ...mono, color: active.color }}>{active.pct}%</span>
                  <span className="text-[10px]" style={{ color: C.slate500 }}>{active.label}</span>
                  <span className="text-[10px] font-medium" style={{ ...mono, color: C.slate600 }}>{active.count}</span>
                  <span className="text-[9px] mt-0.5 opacity-70" style={{ color: active.color }}>click to explore</span>
                </>) : (<>
                  <span className="text-xl font-bold" style={{ ...mono, color: C.slate900 }}>{summary.coveragePct}%</span>
                  <span className="text-[10px]" style={{ color: C.slate400 }}>Covered</span>
                </>)}
            </div>
          </div>

          <div className="flex-1 space-y-3">
            {slices.map((d, i) => (<button key={`dl-${i}`} className="w-full text-left rounded-xl p-2.5 -mx-2.5 transition-all hover:shadow-sm" style={{
                background: hov === i ? `${d.color}10` : "transparent",
                border: `1px solid ${hov === i ? d.color + "30" : "transparent"}`,
            }} onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} onClick={() => setWorkspaceStatus(d.label)}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }}/>
                    <span className="text-xs font-medium" style={{ color: hov === i ? C.slate900 : C.slate600 }}>{d.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px]" style={{ ...mono, color: C.slate500 }}>{d.count}</span>
                    <span className="text-xs font-bold" style={{ ...mono, color: d.color }}>{d.pct}%</span>
                    <ChevronRight size={11} style={{ color: d.color, opacity: hov === i ? 1 : 0, transition: "opacity 0.1s" }}/>
                  </div>
                </div>
                <MiniBar pct={d.pct} color={d.color}/>
              </button>))}
            <div className="pt-2 border-t" style={{ borderColor: C.slate100 }}>
              <p className="text-[11px] flex items-center gap-1" style={{ color: C.slate500 }}>
                <Info size={11}/> A section counts as covered once it has test cases and no AI-flagged gaps.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {workspaceStatus && <RequirementMappingWorkspace initialStatus={workspaceStatus} onClose={() => setWorkspaceStatus(null)}/>}
    </>);
}
// ─── Requirement Coverage Mapping Workspace ──────────────────────────────────
function RequirementMappingWorkspace({ initialStatus, onClose }) {
    // Real BRD section coverage, including the AI-flagged gap text that explains
    // why a section is only partially covered.
    const { sectionCoverage, inputSources, testCases, loading } = useTestSuiteData();
    const docs = sectionCoverage.documents;
    const [selectedBrdId, setSelectedBrdId] = useState(docs[0]?.id ?? "");
    const [search, setSearch] = useState("");
    const statusFilter = { Covered: "Covered", "Partially Covered": "Partial", Missing: "Missing" }[initialStatus];
    const selectedDoc = docs.find((doc) => doc.id === selectedBrdId) ?? docs[0];

    // Sections of the selected BRD, classified the same way the donut is.
    const sectionsForDoc = useMemo(() => {
        if (!selectedDoc) return [];
        const bySection = new Map();
        for (const c of testCases.items) {
            if (c.brdId !== selectedDoc.id) continue;
            const cur = bySection.get(c.sectionNumber) || { number: c.sectionNumber, title: c.module, cases: 0 };
            cur.cases += 1;
            bySection.set(c.sectionNumber, cur);
        }
        const gapBySection = new Map(
            sectionCoverage.gaps.filter((g) => g.brdId === selectedDoc.id).map((g) => [g.sectionNumber, g])
        );
        return [...bySection.values()].map((sec) => {
            const gap = gapBySection.get(sec.number);
            return {
                ...sec,
                status: gap ? "Partial" : "Covered",
                gaps: gap?.gaps ?? [],
                suggested: gap?.suggestedAdditional ?? 0,
            };
        }).sort((a, b) => a.number - b.number);
    }, [selectedDoc, testCases.items, sectionCoverage.gaps]);

    const rows = sectionsForDoc.filter((sec) =>
        (!statusFilter || statusFilter === "Missing" || sec.status === statusFilter) &&
        (sec.title || "").toLowerCase().includes(search.toLowerCase()));

    const slices = sectionCoverage.slices.map((s) => ({ ...s, color: COV_STATUS_CFG[s.status].fg }));
    const SIZE = 116, CX = 58, CY = 58, OR = 48, IR = 31, GAP = 0.04;
    let theta = -Math.PI / 2;
    const arcs = slices.map((slice) => {
        const sweep = (slice.pct / 100) * Math.PI * 2;
        const start = theta + GAP, end = theta + sweep - GAP;
        theta += sweep;
        const large = sweep - 2 * GAP > Math.PI ? 1 : 0;
        return { ...slice, path: `M ${CX + OR * Math.cos(start)} ${CY + OR * Math.sin(start)} A ${OR} ${OR} 0 ${large} 1 ${CX + OR * Math.cos(end)} ${CY + OR * Math.sin(end)} L ${CX + IR * Math.cos(end)} ${CY + IR * Math.sin(end)} A ${IR} ${IR} 0 ${large} 0 ${CX + IR * Math.cos(start)} ${CY + IR * Math.sin(start)} Z` };
    });

    return (<div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: C.pageBg }}>
      <div className="min-h-full p-5">
        <div className="max-w-[1440px] mx-auto">
          <header className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: C.blueL }}><Target size={17} style={{ color: C.blue }}/></div>
              <div>
                <h1 className="text-lg font-bold" style={{ color: C.slate900 }}>BRD Section Coverage</h1>
                <p className="text-[11px] mt-0.5" style={{ color: C.slate500 }}>Home <ChevronRight size={10} className="inline"/> Section coverage and AI-flagged gaps</p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center border" style={{ borderColor: C.sidebarBorder, color: C.slate500 }}><X size={16}/></button>
          </header>

          {/* Ingested artifacts */}
          <div className="rounded-2xl p-4 mb-3" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold" style={{ color: C.slate900 }}>Input Sources</p>
              <span className="text-[11px]" style={{ color: C.slate500 }}>{inputSources.totalArtifacts} total artifacts</span>
            </div>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.max(inputSources.tiles.length, 1)}, minmax(0,1fr))` }}>
              {inputSources.tiles.map((t) => (<div key={t.id} className="rounded-xl px-3 py-2.5" style={{ background: C.pageBg, border: `1px solid ${C.sidebarBorder}` }}>
                <p className="text-[10px] font-semibold" style={{ color: C.slate600 }}>{t.label}</p>
                <p className="text-xl font-bold leading-none mt-1" style={{ ...mono, color: C.blue }}>{t.count}</p>
                <p className="text-[10px] mt-1" style={{ color: C.slate400 }}>{t.detail}</p>
              </div>))}
            </div>
          </div>

          {/* Coverage summary */}
          <div className="rounded-2xl p-4 mb-3 flex items-center gap-6" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
              <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                {arcs.map((a) => <path key={a.label} d={a.path} fill={a.color}/>)}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold" style={{ ...mono, color: C.slate900 }}>{sectionCoverage.coveragePct}%</span>
                <span className="text-[10px]" style={{ color: C.slate400 }}>Covered</span>
              </div>
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              {slices.map((sl) => (<div key={sl.label}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="flex items-center gap-2" style={{ color: C.slate600 }}><span className="w-2.5 h-2.5 rounded-full" style={{ background: sl.color }}/>{sl.label}</span>
                  <span style={{ ...mono, color: sl.color }}>{sl.count} · {sl.pct}%</span>
                </div>
                <MiniBar pct={sl.pct} color={sl.color}/>
              </div>))}
            </div>
            <div className="shrink-0 text-right">
              <p className="text-3xl font-bold" style={{ ...mono, color: C.blue }}>{sectionCoverage.totalSections}</p>
              <p className="text-[11px]" style={{ color: C.slate500 }}>Total sections</p>
              <p className="text-lg font-bold mt-2" style={{ ...mono, color: C.green }}>{testCases.total.toLocaleString()}</p>
              <p className="text-[11px]" style={{ color: C.slate500 }}>Test cases generated</p>
            </div>
          </div>

          {/* Drill down by BRD */}
          <div className="grid grid-cols-[230px_1fr] gap-3">
            <div className="rounded-2xl p-3" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
              <p className="text-xs font-bold mb-1" style={{ color: C.slate800 }}>Drill down by BRD</p>
              <p className="text-[10px] mb-3" style={{ color: C.slate500 }}>Select a BRD to view its sections</p>
              <div className="space-y-1.5">
                {docs.map((doc) => {
                    const active = doc.id === selectedDoc?.id;
                    return (<button key={doc.id} onClick={() => setSelectedBrdId(doc.id)} className="w-full text-left rounded-lg px-2.5 py-2 flex items-center justify-between gap-2" style={{ background: active ? C.blueL : "transparent", border: `1px solid ${active ? C.blue : "transparent"}` }}>
                      <span className="text-[11px] truncate" style={{ color: active ? C.blue : C.slate600 }} title={doc.name}>{doc.name}</span>
                      <span className="text-[10px] font-bold shrink-0" style={{ ...mono, color: doc.coveragePct >= 70 ? C.green : doc.coveragePct >= 30 ? C.orange : C.red }}>{doc.coveragePct}%</span>
                    </button>);
                })}
                {docs.length === 0 && <p className="text-[11px]" style={{ color: C.slate400 }}>{loading ? "Loading…" : "No BRDs"}</p>}
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
              <div className="px-4 py-3 flex items-center justify-between gap-3" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: C.slate900 }}>{selectedDoc?.name ?? "—"}</p>
                  <p className="text-[11px]" style={{ color: C.slate500 }}>
                    {selectedDoc ? `${selectedDoc.covered} covered · ${selectedDoc.partial} partial · ${selectedDoc.missing} with no test cases` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border shrink-0" style={{ borderColor: C.sidebarBorder }}>
                  <Search size={12} style={{ color: C.slate400 }}/>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sections" className="text-xs outline-none bg-transparent w-40"/>
                </div>
              </div>

              <div className="grid px-4 py-2 text-[10px] font-semibold uppercase tracking-wider" style={{ gridTemplateColumns: "70px 1fr 80px 90px 1fr", background: C.blueL, color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}` }}>
                <span>Section</span><span>Title</span><span>Cases</span><span>Status</span><span>AI-flagged gaps</span>
              </div>

              {rows.length === 0 ? (<p className="px-4 py-8 text-sm text-center" style={{ color: C.slate400 }}>
                {loading ? "Loading…" : "No generated sections match."}
              </p>) : rows.map((sec) => (<div key={sec.number} className="grid px-4 py-3 items-start text-xs" style={{ gridTemplateColumns: "70px 1fr 80px 90px 1fr", borderBottom: `1px solid ${C.blueL}` }}>
                <span style={{ ...mono, color: C.blue }}>§ {sec.number}</span>
                <span className="pr-3 truncate" style={{ color: C.slate800 }} title={sec.title}>{sec.title}</span>
                <span style={{ ...mono, color: C.slate600 }}>{sec.cases}</span>
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md justify-self-start" style={{ background: sec.status === "Covered" ? C.greenL : C.orangeL, color: sec.status === "Covered" ? C.green : C.orange }}>{sec.status}</span>
                <span className="min-w-0 pr-2" style={{ color: C.slate600 }}>
                  {sec.gaps.length === 0 ? <span style={{ color: C.slate300 }}>—</span> : (<>
                    <span className="block text-[11px] leading-snug">{sec.gaps[0]}</span>
                    {sec.gaps.length > 1 && <span className="block text-[10px] mt-1" style={{ color: C.slate400 }}>+ {sec.gaps.length - 1} more</span>}
                    {sec.suggested > 0 && <span className="block text-[10px] mt-1" style={{ color: C.orange }}>{sec.suggested} additional cases suggested</span>}
                  </>)}
                </span>
              </div>))}
            </div>
          </div>
        </div>
      </div>
    </div>);
}

// ─── ROW 2 Widget 3 — Module-Wise Defect Distribution ────────────────────────
const MODULE_DEFECTS = [
    { name: "Payments", open: 8, color: C.red, detail: "Gateway retries and card-entry failures" },
    { name: "Checkout", open: 5, color: C.orange, detail: "Coupon, inventory, and guest-flow defects" },
    { name: "API Services", open: 4, color: C.blue, detail: "Contract and async response failures" },
    { name: "Login / Auth", open: 4, color: C.purple, detail: "Session, MFA, and OAuth defects" },
];
function ModuleDefectPanel({ module, onClose }) {
    const cases = [
        { tc: "TC-015", defect: "Coupon modal blocks input", severity: "P1", status: "In Progress", script: "TS-009", source: "Shopping Cart & Checkout BRD" },
        { tc: "TC-017", defect: "Inventory response timing", severity: "P1", status: "Open", script: "TS-010", source: "Shopping Cart & Checkout BRD" },
        { tc: "TC-023", defect: "Dashboard API render timeout", severity: "P2", status: "Open", script: "TS-017", source: "Reports & Analytics BRD" },
    ].slice(0, module.open > 5 ? 3 : 2);
    return <><div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,.45)" }} onClick={onClose}/><div className="fixed right-0 top-0 bottom-0 z-50 w-[min(900px,94vw)] overflow-auto" style={{ background: C.white, boxShadow: "-4px 0 40px rgba(15,23,42,.18)" }}><div className="flex items-center justify-between px-7 py-5" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}><div><h2 className="text-lg font-bold" style={{ color: C.slate900 }}>{module.name} Defect Drill-down</h2><p className="text-xs mt-1" style={{ color: C.slate500 }}>{module.open} defects · linked test cases, scripts, and source requirements</p></div><button onClick={onClose} className="w-8 h-8 rounded-lg border" style={{ borderColor: C.sidebarBorder }}><X size={16}/></button></div><div className="p-7"><div className="grid grid-cols-3 gap-3 mb-5">{[{ label: "Module", value: module.name }, { label: "Defects", value: String(module.open) }, { label: "Hotspot", value: module.detail }].map((x) => <div key={x.label} className="rounded-xl p-4" style={{ background: C.blueL }}><p className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>{x.label}</p><p className="text-sm font-bold mt-2" style={{ color: x.label === "Defects" ? module.color : C.slate800 }}>{x.value}</p></div>)}</div><div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.sidebarBorder}` }}><div className="grid grid-cols-[80px_1fr_65px_100px_90px_1fr] px-4 py-3 text-[10px] font-semibold uppercase" style={{ background: C.blueL, color: C.slate400 }}><span>Test Case</span><span>Defect</span><span>Severity</span><span>Status</span><span>Script</span><span>Requirement / Source</span></div>{cases.map((c) => <div key={c.tc} className="grid grid-cols-[80px_1fr_65px_100px_90px_1fr] px-4 py-3 text-xs" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}><span style={{ ...mono, color: C.blue }}>{c.tc}</span><span style={{ color: C.slate700 }}>{c.defect}</span><span style={{ color: C.red }}>{c.severity}</span><span style={{ color: C.orange }}>{c.status}</span><span style={{ ...mono, color: C.green }}>{c.script}</span><span style={{ color: C.slate500 }}>{c.source}</span></div>)}</div></div></div></>;
}
const DEFECT_PALETTE = [C.red, C.orange, C.blue, C.purple, C.blueD, C.slate600];

function ModuleDefectDistribution() {
    const [hovered, setHovered] = useState(null);
    const [selected, setSelected] = useState(0);
    const [open, setOpen] = useState(false);
    // Real failures per module: failed Playwright runs + failed generation jobs.
    const { moduleDefects, loading } = useTestSuiteData();
    const modules = moduleDefects.modules.map((m, i) => ({ ...m, color: DEFECT_PALETTE[i % DEFECT_PALETTE.length] }));
    const total = moduleDefects.totalDefects;
    const activeIndex = Math.min(hovered ?? selected, Math.max(modules.length - 1, 0));
    const active = modules[activeIndex];
    const SIZE = 142, CX = SIZE / 2, CY = SIZE / 2, OR = 58, IR = 37, GAP = 0.045;
    let theta = -Math.PI / 2;
    const arcs = modules.map((module, index) => {
        const sweep = (module.count / (total || 1)) * Math.PI * 2;
        const sa = theta + GAP, ea = theta + sweep - GAP;
        theta += sweep;
        const largeArc = sweep - 2 * GAP > Math.PI ? 1 : 0;
        const cos = Math.cos, sin = Math.sin;
        return {
            ...module,
            index,
            path: [
                `M ${CX + OR * cos(sa)} ${CY + OR * sin(sa)}`,
                `A ${OR} ${OR} 0 ${largeArc} 1 ${CX + OR * cos(ea)} ${CY + OR * sin(ea)}`,
                `L ${CX + IR * cos(ea)} ${CY + IR * sin(ea)}`,
                `A ${IR} ${IR} 0 ${largeArc} 0 ${CX + IR * cos(sa)} ${CY + IR * sin(sa)} Z`,
            ].join(" "),
        };
    });
    return (<>
    <Card className="p-4 flex flex-col">
      <CardHeader title="Failures by Module" sub={total ? `${total} recorded failure${total === 1 ? "" : "s"} · Click a module to inspect` : "No failures recorded"} action={total ? <button onClick={() => setOpen(true)} className="text-xs font-medium" style={{ color: C.red }}>Open failures <ChevronRight size={11} className="inline"/></button> : null}/>
      {modules.length === 0 ? (<div className="flex-1 flex flex-col items-center justify-center py-8">
        <CheckCircle2 size={22} style={{ color: loading ? C.slate300 : C.green }}/>
        <p className="text-sm mt-2" style={{ color: C.slate400 }}>{loading ? "Loading…" : "No failed runs or generations"}</p>
      </div>) : (<>
      <div className="flex items-center gap-5 flex-1">
        <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            {arcs.map((arc) => (<path key={arc.name} d={arc.path} fill={arc.color} className="cursor-pointer transition-opacity" style={{ opacity: activeIndex === arc.index ? 1 : 0.56 }} onMouseEnter={() => setHovered(arc.index)} onMouseLeave={() => setHovered(null)} onClick={() => { setSelected(arc.index); setOpen(true); }}/>))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xl font-bold" style={{ ...mono, color: active.color }}>{active.count}</span>
            <span className="text-[10px]" style={{ color: C.slate500 }}>failures</span>
            <span className="text-[10px] font-semibold mt-0.5" style={{ color: active.color }}>{active.pct}%</span>
          </div>
        </div>

        <div className="flex-1 space-y-1.5 min-w-0">
          {modules.map((module, index) => {
            const isActive = activeIndex === index;
            return (<button key={module.name} className="w-full flex items-center justify-between text-left rounded-lg px-2.5 py-2 transition-colors" style={{ background: isActive ? `${module.color}12` : "transparent" }} onMouseEnter={() => setHovered(index)} onMouseLeave={() => setHovered(null)} onClick={() => { setSelected(index); setOpen(true); }}>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: module.color }}/>
                  <span className="text-xs font-medium truncate" style={{ color: isActive ? C.slate900 : C.slate600 }} title={module.name}>{module.name}</span>
                </span>
                <span className="text-xs font-bold shrink-0 ml-2" style={{ ...mono, color: module.color }}>{module.count} · {module.pct}%</span>
              </button>);
        })}
        </div>
      </div>
      <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}>
        <p className="text-[11px] flex items-center gap-1.5" style={{ color: active.color }}>
          <AlertTriangle size={12}/> <strong>{active.name}</strong> accounts for {active.pct}% of recorded failures.
        </p>
      </div>
      </>)}
    </Card>{open && <CriticalIssuesPanel onClose={() => setOpen(false)}/>}</>);
}
// ─── ROW 3 Widget 1 — AI Generation Health ────────────────────────────────────
function AIHealth() {
    // Four rates, each measured: how reliably generation succeeds, how far the
    // pipeline has been carried, and how long the models actually take.
    const { aiHealth, loading } = useTestSuiteData();
    const avgSeconds = aiHealth.avgGenerationMs ? aiHealth.avgGenerationMs / 1000 : 0;
    const metrics = [
        {
            label: "Test Case Generation Rate",
            value: `${aiHealth.successRate}%`,
            color: aiHealth.successRate >= 90 ? C.green : aiHealth.successRate >= 70 ? C.orange : C.red,
            barW: aiHealth.successRate,
            note: `${aiHealth.succeeded} of ${aiHealth.generations} generation jobs succeeded`,
        },
        {
            label: "Test Script Generation Rate",
            value: `${aiHealth.scriptGenerationRate}%`,
            color: aiHealth.scriptGenerationRate >= 70 ? C.green : aiHealth.scriptGenerationRate >= 30 ? C.orange : C.red,
            barW: aiHealth.scriptGenerationRate,
            note: `${aiHealth.scriptedSections} of ${aiHealth.sectionsWithCases} generated sections have a script`,
        },
        {
            label: "Automation Rate",
            value: `${aiHealth.automationRate}%`,
            color: aiHealth.automationRate >= 70 ? C.green : aiHealth.automationRate >= 30 ? C.orange : C.red,
            barW: aiHealth.automationRate,
            note: `${aiHealth.automatedCases.toLocaleString()} test cases sit in a scripted section`,
        },
        {
            label: "Avg Generation Time",
            value: avgSeconds ? `${avgSeconds.toFixed(1)}s` : "—",
            color: C.blueD,
            // 180s is treated as the top of the scale.
            barW: Math.min(100, (avgSeconds / 180) * 100),
            note: aiHealth.timingSamples
                ? `measured over ${aiHealth.timingSamples} timed generation${aiHealth.timingSamples === 1 ? "" : "s"}`
                : "no timing recorded yet",
        },
    ];
    return (<Card className="p-4 flex flex-col">
      <CardHeader title="AI Generation Health" sub={aiHealth.generations ? `${aiHealth.generations} generation jobs · ${aiHealth.failed} failed` : "No generation jobs recorded"} action={<span className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md text-white" style={{ background: C.blue }}>
            <Sparkles size={9}/> AI
          </span>}/>
      <div className="space-y-4 flex-1">
        {metrics.map((m) => (<div key={m.label} className="w-full text-left">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium" style={{ color: C.slate600 }}>{m.label}</span>
              <span className="text-xs font-bold" style={{ ...mono, color: m.color }}>{m.value}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: C.blueL }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(100, m.barW))}%`, background: m.color }}/>
            </div>
            <p className="text-[10px] mt-1" style={{ color: C.slate400 }}>{m.note}</p>
          </div>))}
      </div>
      <div className="mt-4 pt-3 flex items-center gap-2 flex-wrap" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}>
        {loading ? (<span className="text-[11px]" style={{ color: C.slate400 }}>Loading…</span>) : (<>
          <CheckCircle2 size={13} style={{ color: aiHealth.failed ? C.orange : C.green }}/>
          <span className="text-[11px]" style={{ color: C.slate500 }}>
            {aiHealth.models.length ? aiHealth.models.map((m) => `${m.name} (${m.calls})`).join(" · ") : "No model telemetry recorded"}
          </span>
        </>)}
      </div>
    </Card>);
}

// ─── ROW 3 Widget 2 — Generation Trend ───────────────────────────────────────
function GenTrend() {
    const [hovIdx, setHovIdx] = useState(null);
    const W = 400, H = 130;
    const PAD = { t: 10, r: 12, b: 24, l: 36 };
    const cW = W - PAD.l - PAD.r, cH = H - PAD.t - PAD.b;
    const n = WEEKS.length;
    const allVals = [...T_CASES, ...T_SCRPT, ...T_BRD.map((v) => v * 10)];
    const maxV = Math.max(...allVals);
    const toX = (i) => PAD.l + (i / (n - 1)) * cW;
    const toY = (v) => PAD.t + cH - (v / maxV) * cH;
    const series = [
        { label: "Test Cases", data: T_CASES, color: C.blue },
        { label: "Scripts", data: T_SCRPT, color: C.blueD },
        { label: "BRDs ×10", data: T_BRD.map((v) => v * 10), color: C.slate400 },
    ];
    const pts = (data) => data.map((v, i) => `${toX(i)},${toY(v)}`).join(" ");
    const gridYs = [0.25, 0.5, 0.75, 1].map((r) => PAD.t + cH * (1 - r));
    return (<Card className="p-4 flex flex-col">
      <CardHeader title="Generation Trend" sub="Last 30 days · Weekly" action={<DrillLink label="Analytics"/>}/>
      {/* Legend */}
      <div className="flex items-center gap-4 mb-3">
        {series.map((s) => (<div key={`leg-${s.label}`} className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded-full" style={{ background: s.color }}/>
            <span className="text-[10px]" style={{ color: C.slate500 }}>{s.label}</span>
          </div>))}
      </div>
      <div className="flex-1">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: "block", overflow: "visible" }}>
          {gridYs.map((y, gi) => (<line key={`gy-${gi}`} x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke={C.slate100} strokeWidth={1}/>))}
          {gridYs.map((y, gi) => (<text key={`gyt-${gi}`} x={PAD.l - 5} y={y + 3} textAnchor="end" fill={C.slate400} fontSize={8} fontFamily="DM Mono, monospace">
              {Math.round(maxV * [0.25, 0.5, 0.75, 1][gi])}
            </text>))}
          {series.map((s) => (<polyline key={`pl-${s.label}`} points={pts(s.data)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"/>))}
          {series.map((s) => s.data.map((v, i) => (<circle key={`dot-${s.label}-${i}`} cx={toX(i)} cy={toY(v)} r={hovIdx === i ? 4.5 : 3} fill="white" stroke={s.color} strokeWidth={2} style={{ cursor: "pointer", transition: "r 0.1s" }} onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}/>)))}
          {WEEKS.map((w, i) => (<text key={`xt-${i}`} x={toX(i)} y={H - 6} textAnchor="middle" fill={C.slate400} fontSize={8} fontFamily="DM Mono, monospace">{w}</text>))}
          {hovIdx !== null && (() => {
            const tx = toX(hovIdx) + 6;
            const ty = Math.min(...series.map((s) => toY(s.data[hovIdx]))) - 8;
            return (<g style={{ pointerEvents: "none" }}>
                <line x1={toX(hovIdx)} y1={PAD.t} x2={toX(hovIdx)} y2={H - PAD.b} stroke={C.slate200} strokeWidth={1} strokeDasharray="3,2"/>
                <rect x={tx} y={ty - 50} width={96} height={55} rx={8} fill="white" stroke={C.slate200} strokeWidth={1} filter="drop-shadow(0 2px 8px rgba(0,0,0,0.08))"/>
                <text x={tx + 8} y={ty - 36} fill={C.slate900} fontSize={9} fontFamily="DM Mono, monospace" fontWeight={600}>{WEEKS[hovIdx]}</text>
                <text x={tx + 8} y={ty - 22} fill={C.blue} fontSize={8} fontFamily="DM Mono, monospace">Cases: {T_CASES[hovIdx]}</text>
                <text x={tx + 8} y={ty - 10} fill={C.blue} fontSize={8} fontFamily="DM Mono, monospace">Scripts: {T_SCRPT[hovIdx]}</text>
                <text x={tx + 8} y={ty + 2} fill={C.green} fontSize={8} fontFamily="DM Mono, monospace">BRDs: {T_BRD[hovIdx]}</text>
              </g>);
        })()}
        </svg>
      </div>
    </Card>);
}
// ─── ROW 3 Widget 3 — Business Impact ────────────────────────────────────────
const BIZ = [
    { label: "Manual Hours Saved", value: "1,247", unit: "hrs", icon: Clock, color: C.blue, bg: C.blueL, trend: "+18% MoM" },
    { label: "Estimated Cost Saved", value: "$186K", unit: "", icon: DollarSign, color: C.blue, bg: C.blueL, trend: "+$21K sprint" },
    { label: "Automation Coverage", value: "75", unit: "%", icon: Zap, color: C.blueD, bg: "#E8EFFD", trend: "↑ from 71%" },
    { label: "AI Productivity Gain", value: "3.4", unit: "×", icon: Bot, color: C.blueD, bg: "#E8EFFD", trend: "vs. manual" },
];
// ─── Finances ─────────────────────────────────────────────────────────────────
// TestSuite AI records no money: no rates, no prices, no timesheets. So this
// page multiplies quantities it *did* measure by rates the user supplies, and
// says so. Change a rate and every figure below recomputes.
const FINANCE_RATE_STORAGE_KEY = "testsuite_dashboard_finance_rates";
const DEFAULT_FINANCE_RATES = {
    qaHourlyRate: 45,        // currency units per hour
    minutesPerCase: 12,      // manual authoring time per test case
    minutesPerRun: 8,        // manual execution time per test run
    llmCostPerHour: 9,       // model cost per hour of API time
};

const FINANCE_RATE_FIELDS = [
    { key: "qaHourlyRate", label: "QA hourly rate", prefix: "$", hint: "Loaded cost of one QA engineer per hour" },
    { key: "minutesPerCase", label: "Minutes to author a case", prefix: "", hint: "How long writing one test case takes by hand" },
    { key: "minutesPerRun", label: "Minutes to execute a run", prefix: "", hint: "How long one manual execution takes" },
    { key: "llmCostPerHour", label: "LLM cost per API hour", prefix: "$", hint: "Model spend per hour of generation time" },
];

function useFinanceRates() {
    const [rates, setRates] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(FINANCE_RATE_STORAGE_KEY) || "null");
            return stored ? { ...DEFAULT_FINANCE_RATES, ...stored } : DEFAULT_FINANCE_RATES;
        }
        catch {
            return DEFAULT_FINANCE_RATES;
        }
    });
    const update = useCallback((key, value) => {
        setRates((prev) => {
            const next = { ...prev, [key]: Number(value) || 0 };
            try {
                localStorage.setItem(FINANCE_RATE_STORAGE_KEY, JSON.stringify(next));
            }
            catch { /* persistence is best-effort */ }
            return next;
        });
    }, []);
    const reset = useCallback(() => {
        setRates(DEFAULT_FINANCE_RATES);
        try {
            localStorage.removeItem(FINANCE_RATE_STORAGE_KEY);
        }
        catch { /* persistence is best-effort */ }
    }, []);
    return { rates, update, reset };
}

const money = (value) => {
    const n = Number(value) || 0;
    if (Math.abs(n) >= 1000)
        return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(n < 100 ? 2 : 0)}`;
};

function useFinanceModel(rates) {
    const { costBasis } = useTestSuiteData();
    return useMemo(() => {
        const authoringHours = (costBasis.testCases * rates.minutesPerCase) / 60;
        const executionHours = (costBasis.automationRuns * rates.minutesPerRun) / 60;
        const manualHours = authoringHours + executionHours;
        const manualCost = manualHours * rates.qaHourlyRate;
        const llmHours = costBasis.llmApiMs / 3600000;
        const llmCost = llmHours * rates.llmCostPerHour;
        return {
            basis: costBasis,
            authoringHours, executionHours, manualHours, manualCost,
            llmHours, llmCost,
            netSaved: manualCost - llmCost,
            roiPct: manualCost > 0 ? Math.round(((manualCost - llmCost) / manualCost) * 100) : 0,
        };
    }, [costBasis, rates]);
}

function FinanceAssumptions({ rates, update, reset }) {
    return (<Card className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[15px] font-bold" style={{ color: C.slate900 }}>Your rate assumptions</p>
          <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>
            TestSuite AI records no costs. These four inputs are yours — every figure on this page is derived from them and from measured counts.
          </p>
        </div>
        <button onClick={reset} className="text-[11px] font-medium px-2.5 py-1.5 rounded-lg border shrink-0" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>Reset</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {FINANCE_RATE_FIELDS.map((f) => (<label key={f.key} className="rounded-xl p-3 block" style={{ background: C.blueL, border: `1px solid ${C.blueM}` }}>
          <span className="block text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.slate500 }}>{f.label}</span>
          <span className="flex items-center gap-1">
            {f.prefix && <span className="text-sm font-bold" style={{ ...mono, color: C.blue }}>{f.prefix}</span>}
            <input type="number" min="0" step="0.5" value={rates[f.key]} onChange={(e) => update(f.key, e.target.value)} className="w-full bg-transparent outline-none text-lg font-bold" style={{ ...mono, color: C.blue }}/>
          </span>
          <span className="block text-[10px] mt-1" style={{ color: C.slate400 }}>{f.hint}</span>
        </label>))}
      </div>
    </Card>);
}

function CostBreakdown({ model }) {
    const items = [
        { label: "Manual effort avoided", value: money(model.manualCost), hint: `${Math.round(model.manualHours).toLocaleString()} engineer hours at your rate`, color: C.green, bg: C.greenL, sign: "+" },
        { label: "LLM cost", value: money(model.llmCost), hint: `${model.llmHours.toFixed(2)} h of measured API time`, color: C.orange, bg: C.orangeL, sign: "−" },
        { label: "Test cases generated", value: model.basis.testCases.toLocaleString(), hint: "Measured — not an estimate", color: C.blue, bg: C.blueL, sign: "" },
        { label: "Automation runs", value: String(model.basis.automationRuns), hint: "Measured Playwright executions", color: C.purple, bg: C.purpleL, sign: "" },
    ];
    return (<Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[15px] font-bold" style={{ color: C.slate900 }}>Cost Breakdown</p>
          <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>Measured quantities valued at your rates.</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.slate400 }}>Net estimated saved</p>
          <p className="text-3xl font-bold leading-none mt-1" style={{ ...mono, color: C.green }}>{money(model.netSaved)}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((c) => (<div key={c.label} className="rounded-2xl p-4" style={{ background: c.bg }}>
          <p className="text-xs font-semibold" style={{ color: C.slate600 }}>{c.label}</p>
          <p className="text-2xl font-bold leading-none mt-2" style={{ ...mono, color: c.color }}>{c.sign}{c.value}</p>
          <p className="text-[11px] mt-2" style={{ color: C.slate500 }}>{c.hint}</p>
        </div>))}
      </div>
      <p className="text-[11px] mt-4" style={{ color: C.slate500 }}>
        {money(model.manualCost)} manual effort avoided − {money(model.llmCost)} LLM cost = <strong style={{ color: C.green }}>{money(model.netSaved)} net</strong>
      </p>
    </Card>);
}

function ManualVsAIComparison({ model }) {
    const cols = [
        {
            title: "Engineer hours",
            rows: [
                ["Manual", `${Math.round(model.manualHours).toLocaleString()} hrs`, C.red],
                ["TestSuite AI", `${model.llmHours.toFixed(2)} hrs`, C.green],
            ],
            saved: `Saved: ${Math.round(model.manualHours - model.llmHours).toLocaleString()} hrs`,
        },
        {
            title: "Cost",
            rows: [
                ["Manual", money(model.manualCost), C.red],
                ["TestSuite AI", money(model.llmCost), C.green],
            ],
            saved: `Saved: ${money(model.netSaved)}`,
        },
        {
            title: "Test cases produced",
            rows: [
                ["Manual", model.basis.testCases.toLocaleString(), C.slate600],
                ["TestSuite AI", model.basis.testCases.toLocaleString(), C.green],
            ],
            saved: "Same coverage",
        },
    ];
    return (<Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[15px] font-bold" style={{ color: C.slate900 }}>Manual Testing vs TestSuite AI</p>
          <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>Same output, valued both ways.</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.slate400 }}>ROI / Savings</p>
          <p className="text-3xl font-bold leading-none mt-1" style={{ ...mono, color: C.green }}>{model.roiPct}%</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {cols.map((col) => (<div key={col.title} className="rounded-2xl p-4" style={{ background: C.blueL }}>
          <p className="text-xs font-semibold mb-2" style={{ color: C.slate700 }}>{col.title}</p>
          {col.rows.map(([k, v, tone]) => (<div key={k} className="flex items-center justify-between py-1">
            <span className="text-xs" style={{ color: C.slate600 }}>{k}</span>
            <span className="text-xs font-bold" style={{ ...mono, color: tone }}>{v}</span>
          </div>))}
          <p className="text-xs font-semibold mt-2 pt-2" style={{ color: C.green, borderTop: `1px solid ${C.blueM}` }}>{col.saved}</p>
        </div>))}
      </div>
    </Card>);
}

function FinancePanel({ onClose }) {
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(840px, 92vw)", background: C.white, boxShadow: "-4px 0 40px rgba(15,23,42,0.18)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        <div className="flex items-center justify-between px-8 pt-6 pb-5 shrink-0" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: C.greenL }}>
              <DollarSign size={19} style={{ color: C.green }}/>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight" style={{ color: C.slate900 }}>FINANCE PANEL</h2>
              <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>Business impact · Release 2.4.1</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}><X size={16}/></button>
        </div>

        <div className="p-8 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            {BIZ.map(({ label, value, unit, icon: Icon, color, bg, trend }) => (<div key={label} className="rounded-2xl p-5" style={{ background: bg, border: `1px solid ${color}22` }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-4" style={{ background: C.white }}><Icon size={17} style={{ color }}/></div>
                <p className="text-3xl font-bold leading-none" style={{ ...mono, color: C.slate900 }}>{value}<span className="text-base font-medium ml-1" style={{ color: C.slate500 }}>{unit}</span></p>
                <p className="text-sm font-semibold mt-2" style={{ color: C.slate700 }}>{label}</p>
                <p className="text-xs mt-1" style={{ color }}>{trend}</p>
              </div>))}
          </div>

          <div className="mt-6 rounded-2xl p-5" style={{ background: C.greenL, border: "1px solid #A7F3D0" }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.green }}>Financial summary</p>
            <p className="text-sm mt-2 leading-relaxed" style={{ color: C.slate700 }}>AI-assisted test generation and automation have saved an estimated $186K this release, while reducing manual effort by 1,247 hours across three teams.</p>
          </div>
        </div>

        <div className="px-8 py-4 mt-auto flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
          <span className="text-xs" style={{ color: C.slate500 }}>Verified Jul 23, 2026 · Finance data refreshed per release</span>
          <button className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg text-white" style={{ background: C.blue }}><Download size={11}/> Export summary</button>
        </div>
      </div>
    </>);
}
function BusinessImpact() {
    const [open, setOpen] = useState(false);
    return (<>
      <Card className="p-4 flex flex-col" onClick={() => setOpen(true)}>
        <CardHeader title="Business Impact" sub="AI-driven value this release" action={<DrillLink label="Finance panel"/>}/>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
          {BIZ.map(({ label, value, unit, icon: Icon, color, bg, trend }) => (<div key={label} className="p-4 rounded-xl border text-left" style={{ background: bg, borderColor: `${color}20` }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: "white" }}><Icon size={14} style={{ color }}/></div>
              <p className="text-xl font-bold tracking-tight leading-none" style={{ ...mono, color: C.slate900 }}>{value}<span className="text-sm font-medium ml-0.5" style={{ color: C.slate500 }}>{unit}</span></p>
              <p className="text-[11px] font-medium mt-1.5" style={{ color: C.slate600 }}>{label}</p>
              <p className="text-[10px] mt-0.5" style={{ color }}>{trend}</p>
            </div>))}
        </div>
        <div className="mt-4 pt-4 flex items-center gap-2" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}><Users size={12} style={{ color: C.slate400 }}/><span className="text-[11px]" style={{ color: C.slate400 }}>Across 3 teams · Click to open Finance panel</span></div>
      </Card>
      {open && <FinancePanel onClose={() => setOpen(false)}/>}
    </>);
}
// ─── BOTTOM ROW — Executive Alerts ────────────────────────────────────────────
const ALERT_CFG = {
    critical: { bg: C.blueL, border: C.blueM, dot: C.blue, label: "Critical" },
    error: { bg: C.redL, border: "#FECACA", dot: C.red, label: "Error" },
    warning: { bg: C.orangeL, border: "#FDE68A", dot: C.orange, label: "Warning" },
    info: { bg: C.blueL, border: C.blueM, dot: C.blue, label: "Info" },
};
function ExecAlerts() {
    return (<Card className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: C.slate800 }}>Executive Alerts</h3>
          <p className="text-xs mt-0.5" style={{ color: C.slate400 }}>Priority issues requiring attention</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: C.red }}>
            2 Critical
          </span>
          <DrillLink label="View all alerts"/>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {ALERTS.map((alert, i) => {
            const cfg = ALERT_CFG[alert.lvl];
            return (<button key={`alert-${i}`} className="flex items-start gap-3 p-3.5 rounded-xl border text-left hover:shadow-sm transition-all group" style={{ background: cfg.bg, borderColor: cfg.border }}>
              <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: cfg.dot }}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: cfg.dot }}>{cfg.label}</span>
                  <span className="text-[10px]" style={{ color: C.slate400 }}>· {alert.time}</span>
                </div>
                <p className="text-xs font-medium leading-snug" style={{ color: C.slate700 }}>{alert.msg}</p>
              </div>
              <ChevronRight size={12} className="shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.slate400 }}/>
            </button>);
        })}
      </div>
    </Card>);
}
const BRD_DOCS = [
    { id: "BRD-001", name: "User Authentication & Security", module: "Login", requirements: 18, testCases: 52, scripts: 48, coverage: 94, status: "Fully Traced", owner: "Rahul S.", uploaded: "Jul 10" },
    { id: "BRD-002", name: "Payment Processing & Fraud Detection", module: "Payment", requirements: 24, testCases: 68, scripts: 52, coverage: 72, status: "Partial", owner: "Priya M.", uploaded: "Jul 8" },
    { id: "BRD-003", name: "Shopping Cart & Checkout Flow", module: "Checkout", requirements: 16, testCases: 41, scripts: 28, coverage: 65, status: "Partial", owner: "Amara K.", uploaded: "Jul 12" },
    { id: "BRD-004", name: "Customer Profile Management", module: "Customer", requirements: 12, testCases: 22, scripts: 8, coverage: 38, status: "Missing", owner: "Wei L.", uploaded: "Jul 15" },
    { id: "BRD-005", name: "Reports & Analytics Dashboard", module: "Reports", requirements: 10, testCases: 18, scripts: 0, coverage: 22, status: "Missing", owner: "Rahul S.", uploaded: "Jul 18" },
];
const BRD_STATUS_CFG = {
    "Fully Traced": { bg: C.greenL, fg: C.green, dot: C.green },
    "Partial": { bg: C.orangeL, fg: C.orange, dot: C.orange },
    "Missing": { bg: C.redL, fg: C.red, dot: C.red },
};
// ─── BRD Dashboard Panel ──────────────────────────────────────────────────────
function BRDPanel({ onClose }) {
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [expandedBrd, setExpandedBrd] = useState(null);
    const [expandedReq, setExpandedReq] = useState(null);
    const filtered = BRD_DOCS.filter((b) => {
        const q = search.toLowerCase();
        return ((b.name.toLowerCase().includes(q) || b.id.toLowerCase().includes(q)) &&
            (filterStatus === "All" || b.status === filterStatus));
    });
    const total = BRD_DOCS.length;
    const fully = BRD_DOCS.filter((b) => b.status === "Fully Traced").length;
    const partial = BRD_DOCS.filter((b) => b.status === "Partial").length;
    const missing = BRD_DOCS.filter((b) => b.status === "Missing").length;
    const avgCov = Math.round(BRD_DOCS.reduce((s, b) => s + b.coverage, 0) / total);
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.35)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(960px,96vw)", background: C.pageBg, boxShadow: "-4px 0 40px rgba(15,23,42,0.14)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight{from{transform:translateX(56px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-7 py-4 shrink-0" style={{ background: C.white, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: C.blueL }}>
              <FileText size={16} style={{ color: C.blue }}/>
            </div>
            <div>
              <h2 className="text-sm font-bold" style={{ color: C.slate900 }}>BRD Traceability</h2>
              <p className="text-[11px]" style={{ color: C.slate500 }}>Business Requirement Documents · Release 2.4.1</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border hover:bg-blue-50 transition-colors" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Download size={11}/> Export
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
              <X size={15}/>
            </button>
          </div>
        </div>

        {/* ── Summary KPIs ── */}
        <div className="grid grid-cols-5 gap-3 px-7 pt-5 pb-3 shrink-0">
          {[
            { label: "Overall", val: `${avgCov}%`, color: avgCov >= 80 ? C.green : avgCov >= 60 ? C.orange : C.red, sub: "Avg traceability" },
            { label: "Total BRDs", val: String(total), color: C.blue, sub: "This release" },
            { label: "Fully Traced", val: String(fully), color: C.green, sub: "All linked" },
            { label: "Partial", val: String(partial), color: C.orange, sub: "Incomplete" },
            { label: "Not Traced", val: String(missing), color: C.red, sub: "No coverage" },
        ].map((k) => (<div key={k.label} className="rounded-2xl px-4 py-3 text-center" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
              <p className="text-2xl font-bold leading-none" style={{ ...mono, color: k.color }}>{k.val}</p>
              <p className="text-[11px] font-semibold mt-1" style={{ color: C.slate700 }}>{k.label}</p>
              <p className="text-[10px] mt-0.5" style={{ color: C.slate400 }}>{k.sub}</p>
            </div>))}
        </div>

        {/* ── Search + Filter ── */}
        <div className="flex items-center gap-0 mx-7 mb-4 rounded-xl overflow-hidden shrink-0" style={{ border: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          <div className="flex items-center gap-2 px-4 py-2.5 flex-1 border-r" style={{ borderColor: C.sidebarBorder }}>
            <Search size={13} style={{ color: C.slate400 }}/>
            <input className="text-xs bg-transparent outline-none flex-1" placeholder="Search BRD by name or ID…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
          </div>
          <div className="flex items-center gap-1.5 px-4 py-2.5 shrink-0">
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1" style={{ color: C.slate400 }}>Status</span>
            {["All", "Fully Traced", "Partial", "Not Traced"].map((s) => (<button key={s} onClick={() => setFilterStatus(s)} className="text-[11px] px-2.5 py-0.5 rounded-lg font-medium transition-colors" style={filterStatus === s
                ? { background: C.blue, color: C.white }
                : { background: C.blueL, color: C.slate600 }}>
                {s}
              </button>))}
          </div>
        </div>

        {/* ── BRD List ── */}
        <div className="flex-1 overflow-y-auto px-7 pb-6 space-y-2">
          {filtered.length === 0 && (<div className="flex flex-col items-center justify-center h-40">
              <Info size={22} style={{ color: C.slate300 }}/>
              <p className="text-sm mt-2" style={{ color: C.slate400 }}>No BRDs match your filters</p>
            </div>)}

          {filtered.map((brd) => {
            const cfg = BRD_STATUS_CFG[brd.status];
            const isOpen = expandedBrd === brd.id;
            const modReqs = ALL_REQUIREMENTS.filter((r) => r.module === brd.module);
            const covColor = brd.coverage >= 80 ? C.green : brd.coverage >= 50 ? C.orange : C.red;
            return (<div key={brd.id} className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${isOpen ? C.blue + "60" : C.sidebarBorder}`, background: C.white }}>

                {/* ── BRD Row ── */}
                <button className="w-full flex items-center gap-4 px-5 py-4 hover:bg-blue-50 transition-colors text-left" style={{ background: isOpen ? C.blueL : C.white }} onClick={() => { setExpandedBrd(isOpen ? null : brd.id); setExpandedReq(null); }}>
                  {/* Icon */}
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${covColor}12` }}>
                    <FileText size={15} style={{ color: covColor }}/>
                  </div>

                  {/* Name + meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold" style={{ ...mono, color: C.slate400 }}>{brd.id}</span>
                      <p className="text-sm font-semibold truncate" style={{ color: C.slate900 }}>{brd.name}</p>
                    </div>
                    <p className="text-[11px] mt-0.5" style={{ color: C.slate400 }}>
                      {brd.requirements} reqs · {brd.testCases} tests · {brd.scripts} scripts · {brd.owner} · {brd.uploaded}
                    </p>
                  </div>

                  {/* Coverage bar */}
                  <div className="flex items-center gap-2 shrink-0 w-36">
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: C.blueL }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${brd.coverage}%`, background: covColor }}/>
                    </div>
                    <span className="text-xs font-bold w-8 text-right" style={{ color: covColor }}>{brd.coverage}%</span>
                  </div>

                  {/* Status badge */}
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0 flex items-center gap-1.5" style={{ background: cfg.bg, color: cfg.fg }}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.dot }}/>
                    {brd.status}
                  </span>

                  <ChevronRight size={14} style={{ color: C.slate300, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}/>
                </button>

                {/* ── Expanded: Requirements ── */}
                {isOpen && (<div style={{ borderTop: `1px solid ${C.sidebarBorder}` }}>
                    <div className="px-5 py-3" style={{ background: `${C.blue}03` }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: C.slate400 }}>
                        Requirements · {modReqs.length} found in {brd.module} module
                      </p>

                      <div className="space-y-1.5">
                        {modReqs.slice(0, 8).map((req) => {
                        const tcs = TEST_CASES.filter((t) => t.module === req.module).slice(0, 4);
                        const scripts = AUTO_SCRIPTS.filter((s) => s.suite.toLowerCase().includes(req.module.toLowerCase())).slice(0, 3);
                        const isReqOpen = expandedReq === req.id;
                        const covCfg = COV_STATUS_CFG[req.status] ?? COV_STATUS_CFG["Missing"];
                        return (<div key={req.id} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${isReqOpen ? C.blue + "50" : C.sidebarBorder}` }}>

                              {/* Requirement row */}
                              <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left" style={{ background: isReqOpen ? C.blueL : C.white }} onClick={() => setExpandedReq(isReqOpen ? null : req.id)}>
                                <span className="text-sm shrink-0">📋</span>
                                <span className="text-[10px] font-bold shrink-0" style={{ ...mono, color: C.blue }}>{req.id}</span>
                                <span className="text-xs flex-1 truncate font-medium" style={{ color: C.slate800 }}>{req.title}</span>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ background: covCfg.bg, color: covCfg.fg }}>{req.status}</span>
                                <div className="flex items-center gap-2 shrink-0 text-[10px]" style={{ color: C.slate400 }}>
                                  <span>🧪 {tcs.length}</span>
                                  <span>🤖 {scripts.length}</span>
                                </div>
                                <ChevronRight size={12} style={{ color: C.slate300, transform: isReqOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}/>
                              </button>

                              {/* Test Cases + Scripts drill-down */}
                              {isReqOpen && (<div className="grid grid-cols-2 gap-0" style={{ borderTop: `1px solid ${C.blueL}` }}>
                                  {/* Test Cases */}
                                  <div className="px-4 py-3 border-r" style={{ borderColor: C.blueL, background: `${C.blue}02` }}>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: C.slate400 }}>
                                      🧪 Test Cases
                                      <span className="px-1.5 py-0.5 rounded-full font-bold" style={{ background: C.blueL, color: C.blue }}>{tcs.length}</span>
                                    </p>
                                    {tcs.length === 0
                                    ? <p className="text-xs" style={{ color: C.red }}>No test cases mapped yet.</p>
                                    : tcs.map((tc) => (<div key={tc.id} className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: C.blueL }}>
                                          <span className="text-[10px] font-bold shrink-0" style={{ ...mono, color: C.blue }}>{tc.id}</span>
                                          <span className="text-[11px] flex-1 truncate" style={{ color: C.slate700 }}>{tc.title}</span>
                                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: tc.type === "Negative" ? C.redL : C.greenL, color: tc.type === "Negative" ? C.red : C.green }}>
                                            {tc.type === "Negative" ? "Neg" : "Pos"}
                                          </span>
                                        </div>))}
                                  </div>

                                  {/* Automation Scripts */}
                                  <div className="px-4 py-3" style={{ background: `${C.blue}02` }}>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: C.slate400 }}>
                                      🤖 Automation Scripts
                                      <span className="px-1.5 py-0.5 rounded-full font-bold" style={{ background: C.blueL, color: C.blue }}>{scripts.length}</span>
                                    </p>
                                    {scripts.length === 0
                                    ? <p className="text-xs" style={{ color: C.orange }}>No scripts yet — AI can generate these.</p>
                                    : scripts.map((sc) => {
                                        const ic = { passed: { c: C.green, t: "✓" }, failed: { c: C.red, t: "✗" }, pending: { c: C.orange, t: "~" }, skipped: { c: C.slate400, t: "—" } }[sc.status];
                                        return (<div key={sc.id} className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: C.blueL }}>
                                            <span className="font-bold text-sm shrink-0" style={{ color: ic.c }}>{ic.t}</span>
                                            <span className="text-[10px] flex-1 truncate" style={{ ...mono, color: C.slate600 }}>{sc.file}</span>
                                            <span className="text-[10px] shrink-0" style={{ color: C.slate400 }}>{sc.duration}</span>
                                          </div>);
                                    })}
                                  </div>
                                </div>)}
                            </div>);
                    })}

                        {modReqs.length > 8 && (<p className="text-xs pl-1 pt-1" style={{ color: C.slate400 }}>
                            + {modReqs.length - 8} more requirements in this BRD
                          </p>)}
                      </div>
                    </div>
                  </div>)}
              </div>);
        })}
        </div>

        {/* ── Footer ── */}
        <div className="px-7 py-3 flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          <p className="text-[11px]" style={{ color: C.slate400 }}>
            {BRD_DOCS.length} BRDs · {BRD_DOCS.reduce((s, b) => s + b.requirements, 0)} requirements · {BRD_DOCS.reduce((s, b) => s + b.testCases, 0)} test cases
          </p>
          <button className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity" style={{ background: C.blue }}>
            <Download size={11}/> Export Report
          </button>
        </div>
      </div>
    </>);
}
// ─── Test Script Traceability Matrix ─────────────────────────────────────────
const SCRIPT_TRACE_TREE = [
    { source: "Loan Origination System BRD v2.4", scripts: [{ id: "TS-001", cases: ["TC-001", "TC-002", "TC-003"] }, { id: "TS-002", cases: ["TC-004", "TC-005"] }] },
    { source: "Payment Gateway Integration BRD v1.1", scripts: [{ id: "TS-003", cases: ["TC-006", "TC-007"] }, { id: "TS-004", cases: ["TC-008", "TC-009"] }] },
    { source: "Reporting & Analytics BRD v1.0", scripts: [{ id: "TS-005", cases: ["TC-010", "TC-011"] }] },
];
function TestScriptTraceabilityMatrix({ onClose }) {
    const [openScript, setOpenScript] = useState("TS-001");
    const totalScripts = SCRIPT_TRACE_TREE.reduce((sum, source) => sum + source.scripts.length, 0);
    return <><div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,.45)" }} onClick={onClose}/><div className="fixed right-0 top-0 bottom-0 z-50 w-[min(980px,95vw)] overflow-auto" style={{ background: C.pageBg, boxShadow: "-4px 0 40px rgba(15,23,42,.18)" }}><div className="sticky top-0 z-10 flex items-center justify-between px-7 py-5" style={{ background: C.white, borderBottom: `1px solid ${C.sidebarBorder}` }}><div><h2 className="text-lg font-bold" style={{ color: C.slate900 }}>Test Script Traceability Matrix</h2><p className="text-xs mt-1" style={{ color: C.slate500 }}>Input Source → Test Script → Test Cases · Release 2.4.1</p></div><div className="flex items-center gap-3"><div className="text-right"><p className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>Total Test Scripts Generated</p><p className="text-2xl font-bold" style={{ ...mono, color: C.blue }}>{totalScripts}</p></div><button onClick={onClose} className="w-8 h-8 rounded-lg border" style={{ borderColor: C.sidebarBorder }}><X size={16}/></button></div></div><div className="p-7 space-y-4">{SCRIPT_TRACE_TREE.map((group) => <section key={group.source} className="rounded-xl overflow-hidden" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}><div className="px-5 py-3 flex items-center gap-2" style={{ background: C.blueL }}><FileText size={15} style={{ color: C.blue }}/><span className="text-sm font-bold" style={{ color: C.slate800 }}>{group.source}</span><span className="text-[10px] ml-auto" style={{ color: C.slate500 }}>Input Source / BRD</span></div><div className="p-4 space-y-2">{group.scripts.map((script) => <div key={script.id} className="rounded-lg" style={{ border: `1px solid ${C.sidebarBorder}` }}><button onClick={() => setOpenScript(openScript === script.id ? null : script.id)} className="w-full flex items-center justify-between px-4 py-3 text-left"><span className="flex items-center gap-2"><Code2 size={14} style={{ color: C.green }}/><span className="text-sm font-bold" style={{ ...mono, color: C.slate800 }}>{script.id}</span><span className="text-[11px]" style={{ color: C.slate500 }}>{script.cases.length} linked test cases</span></span><ChevronRight size={15} style={{ color: C.slate400, transform: openScript === script.id ? "rotate(90deg)" : "none" }}/></button>{openScript === script.id && <div className="px-5 pb-3 grid grid-cols-3 gap-2">{script.cases.map((tc) => <div key={tc} className="rounded-lg px-3 py-2 text-xs flex items-center gap-2" style={{ background: C.greenL, color: C.green }}><FlaskConical size={12}/> <span style={mono}>{tc}</span></div>)}</div>}</div>)}</div></section>)}</div></div></>;
}
// ─── Backward Traceability Widget ─────────────────────────────────────────────
function ReferenceStyleScriptMatrix({ onClose }) {
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("All");
    const [expanded, setExpanded] = useState("Loan Origination System BRD v2.4");
    const scripts = SCRIPT_TRACE_TREE.flatMap((group) => group.scripts);
    const visible = SCRIPT_TRACE_TREE.filter((group, index) => {
        const status = index === 0 ? "Fully Traced" : index === 1 ? "Partial" : "Not Traced";
        return (filter === "All" || filter === status) && group.source.toLowerCase().includes(query.toLowerCase());
    });
    const statusFor = (index) => index === 0 ? { label: "Fully Traced", color: C.green, pct: 94 } : index === 1 ? { label: "Partial", color: C.orange, pct: 72 } : { label: "Not Traced", color: C.red, pct: 38 };
    return <><div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,.45)" }} onClick={onClose}/><div className="fixed right-0 top-0 bottom-0 z-50 w-[min(1100px,96vw)] overflow-y-auto" style={{ background: C.pageBg, boxShadow: "-4px 0 40px rgba(15,23,42,.18)" }}><header className="sticky top-0 z-10 flex items-center justify-between px-7 py-4" style={{ background: C.white, borderBottom: `1px solid ${C.sidebarBorder}` }}><div className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: C.blueL }}><FileText size={17} style={{ color: C.blue }}/></div><div><h2 className="text-base font-bold" style={{ color: C.slate900 }}>Test Script Traceability Matrix</h2><p className="text-[11px] mt-0.5" style={{ color: C.slate500 }}>Input Sources → Test Scripts → Test Cases · Release 2.4.1</p></div></div><div className="flex items-center gap-2"><button className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: C.sidebarBorder, color: C.blue }}><Download size={11} className="inline mr-1"/> Export</button><button onClick={onClose} className="w-8 h-8" style={{ color: C.slate400 }}><X size={16}/></button></div></header><main className="p-5"><div className="grid grid-cols-5 gap-3 mb-3">{[{ v: "58%", l: "Overall", s: "Script traceability", c: C.red }, { v: String(scripts.length), l: "Total Scripts", s: "This release", c: C.blue }, { v: "3", l: "Fully Traced", s: "All linked", c: C.green }, { v: "1", l: "Partial", s: "Incomplete", c: C.orange }, { v: "1", l: "Not Traced", s: "No coverage", c: C.red }].map((stat) => <div key={stat.l} className="rounded-xl p-3 text-center" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}><p className="text-2xl font-bold" style={{ ...mono, color: stat.c }}>{stat.v}</p><p className="text-[11px] font-semibold mt-1" style={{ color: C.slate700 }}>{stat.l}</p><p className="text-[10px]" style={{ color: C.slate400 }}>{stat.s}</p></div>)}</div><div className="flex items-center gap-3 rounded-xl px-3 py-2 mb-4" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}><div className="flex items-center gap-2 flex-1"><Search size={13} style={{ color: C.slate400 }}/><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search source or script…" className="w-full text-xs outline-none"/></div><span className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>Status</span>{["All", "Fully Traced", "Partial", "Not Traced"].map((x) => <button key={x} onClick={() => setFilter(x)} className="text-[11px] px-2.5 py-1 rounded-full" style={filter === x ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{x}</button>)}</div><div className="space-y-2">{visible.map((group) => { const index = SCRIPT_TRACE_TREE.indexOf(group); const status = statusFor(index); const isOpen = expanded === group.source; return <section key={group.source} className="rounded-xl overflow-hidden" style={{ background: C.white, border: `1px solid ${isOpen ? C.blue : C.sidebarBorder}` }}><button onClick={() => setExpanded(isOpen ? "" : group.source)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" style={{ background: isOpen ? C.blueL : C.white }}><span className="flex items-center gap-3 min-w-0"><span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${status.color}15` }}><FileText size={16} style={{ color: status.color }}/></span><span><span className="block text-sm font-bold" style={{ color: C.slate800 }}>{group.source}</span><span className="block text-[11px] mt-0.5" style={{ color: C.slate500 }}>{group.scripts.length} scripts · {group.scripts.reduce((n, s) => n + s.cases.length, 0)} test cases · Input Source</span></span></span><span className="flex items-center gap-3"><span className="w-24 h-2 rounded-full overflow-hidden" style={{ background: C.slate100 }}><span className="block h-full rounded-full" style={{ width: `${status.pct}%`, background: status.color }}/></span><span className="text-xs font-bold" style={{ color: status.color }}>{status.pct}%</span><span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ background: `${status.color}15`, color: status.color }}>{status.label}</span><ChevronRight size={14} style={{ color: C.slate400, transform: isOpen ? "rotate(90deg)" : "none" }}/></span></button>{isOpen && <div className="px-5 pb-4" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}><p className="text-[10px] font-semibold uppercase tracking-wider mt-3 mb-2" style={{ color: C.slate400 }}>Test Scripts</p>{group.scripts.map((script) => <div key={script.id} className="rounded-lg px-3 py-2.5 mb-2" style={{ background: C.pageBg, border: `1px solid ${C.sidebarBorder}` }}><div className="flex items-center gap-2"><Code2 size={14} style={{ color: C.blue }}/><span className="text-xs font-bold" style={{ ...mono, color: C.slate800 }}>{script.id}</span><span className="text-[11px]" style={{ color: C.slate500 }}>{script.cases.length} mapped cases</span></div><div className="grid grid-cols-3 gap-2 mt-2">{script.cases.map((tc) => <span key={tc} className="rounded-md px-2 py-1.5 text-[11px]" style={{ background: C.greenL, color: C.green }}><FlaskConical size={11} className="inline mr-1"/>{tc}</span>)}</div></div>)}</div>}</section>; })}</div></main></div></>;
}
function MappedCasesDetail({ script, onClose }) {
    return <><div className="fixed inset-0 z-[60]" style={{ background: "rgba(15,23,42,.35)" }} onClick={onClose}/><div className="fixed inset-x-12 top-16 bottom-16 z-[70] overflow-auto rounded-2xl" style={{ background: C.white, boxShadow: "0 16px 48px rgba(15,23,42,.22)" }}><div className="flex items-center justify-between px-7 py-5" style={{ borderBottom: `1px solid ${C.sidebarBorder}` }}><div><p className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>Test Script</p><h3 className="text-lg font-bold mt-1" style={{ ...mono, color: C.slate900 }}>{script.id} · Mapped Test Cases</h3></div><button onClick={onClose} className="w-8 h-8 rounded-lg border" style={{ borderColor: C.sidebarBorder }}><X size={16}/></button></div><div className="p-7"><div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.sidebarBorder}` }}><div className="grid grid-cols-[100px_1fr_110px_160px] px-5 py-3 text-[10px] font-semibold uppercase" style={{ background: C.blueL, color: C.slate400 }}><span>Test Case</span><span>Mapped Scenario</span><span>Coverage</span><span>Automation Evidence</span></div>{script.cases.map((tc, index) => <div key={tc} className="grid grid-cols-[100px_1fr_110px_160px] items-center px-5 py-4 text-xs" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}><span style={{ ...mono, color: C.blue }}>{tc}</span><span style={{ color: C.slate700 }}>{["User login with valid credentials", "Login validation and error handling", "MFA expiry and session validation"][index % 3]}</span><span><span className="px-2 py-1 rounded-full text-[10px]" style={{ background: C.greenL, color: C.green }}>Covered</span></span><span className="flex items-center gap-2" style={{ color: C.green }}><CheckCircle2 size={13}/><span style={mono}>{script.id}</span> passing</span></div>)}</div><div className="mt-4 grid grid-cols-2 gap-3"><div className="rounded-xl p-4" style={{ background: C.blueL }}><p className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>Mapped Test Cases</p><p className="text-2xl font-bold mt-1" style={{ ...mono, color: C.blue }}>{script.cases.length}</p></div><div className="rounded-xl p-4" style={{ background: C.greenL }}><p className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>Automation Status</p><p className="text-sm font-bold mt-2" style={{ color: C.green }}>All mapped and passing</p></div></div></div></div></>;
}
const TRACE_STATUS_COLOR = {
    "Fully Traced": C.green,
    Partial: C.orange,
    "Not Traced": C.red,
};

function ScriptTraceabilityDrilldown({ onClose }) {
    // Real chain: BRD -> generated script -> the test cases of that section.
    const { traceability, loading } = useTestSuiteData();
    const documents = traceability.documents;
    const [expandedSource, setExpandedSource] = useState(documents[0]?.id || "");
    const [expandedScript, setExpandedScript] = useState(documents[0]?.scriptItems?.[0]?.id || "");
    const totalScripts = traceability.totalScripts;
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,.45)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 w-[min(1080px,96vw)] overflow-y-auto" style={{ background: C.pageBg, boxShadow: "-4px 0 40px rgba(15,23,42,.18)" }}>
        <header className="sticky top-0 z-10 flex items-center justify-between px-7 py-4" style={{ background: C.white, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: C.blueL }}><GitBranch size={17} style={{ color: C.blue }}/></div><div><h2 className="text-base font-bold" style={{ color: C.slate900 }}>Test Script Traceability Matrix</h2><p className="text-[11px] mt-0.5" style={{ color: C.slate500 }}>Input Source / BRD → Test Script → Test Cases</p></div></div>
          <div className="flex items-center gap-3"><div className="text-right"><p className="text-[10px] uppercase font-semibold" style={{ color: C.slate400 }}>Total Test Scripts Generated</p><p className="text-xl font-bold" style={{ ...mono, color: C.blue }}>{totalScripts}</p></div><button onClick={onClose} className="w-8 h-8 rounded-lg border" style={{ borderColor: C.sidebarBorder }}><X size={16}/></button></div>
        </header>
        <main className="p-5">
          <div className="grid grid-cols-5 gap-3 mb-4">{[
            { v: `${traceability.overallPct}%`, l: "Overall", s: "Script traceability", c: traceability.overallPct >= 70 ? C.green : traceability.overallPct >= 40 ? C.orange : C.red },
            { v: String(totalScripts), l: "Total Scripts", s: "Generated", c: C.blue },
            { v: String(traceability.fullyTraced), l: "Fully Traced", s: "All sections linked", c: C.green },
            { v: String(traceability.partial), l: "Partial", s: "Incomplete", c: C.orange },
            { v: String(traceability.notTraced), l: "Not Traced", s: "No scripts yet", c: C.red },
          ].map((s) => <div key={s.l} className="rounded-xl p-3 text-center" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}><p className="text-2xl font-bold" style={{ ...mono, color: s.c }}>{s.v}</p><p className="text-[11px] font-semibold mt-1" style={{ color: C.slate700 }}>{s.l}</p><p className="text-[10px]" style={{ color: C.slate400 }}>{s.s}</p></div>)}</div>

          {documents.length === 0 && (<div className="rounded-xl p-8 text-center" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <Info size={22} className="inline" style={{ color: C.slate300 }}/>
            <p className="text-sm mt-2" style={{ color: C.slate400 }}>{loading ? "Loading traceability…" : "No BRDs available yet"}</p>
          </div>)}

          <div className="space-y-2">{documents.map((doc) => {
            const isSourceOpen = expandedSource === doc.id;
            const sourceColor = TRACE_STATUS_COLOR[doc.status] ?? C.slate500;
            return (<section key={doc.id} className="rounded-xl overflow-hidden" style={{ background: C.white, border: `1px solid ${isSourceOpen ? C.blue : C.sidebarBorder}` }}>
              <button onClick={() => setExpandedSource(isSourceOpen ? "" : doc.id)} className="w-full flex items-center justify-between px-5 py-4 text-left" style={{ background: isSourceOpen ? C.blueL : C.white }}>
                <span className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${sourceColor}15` }}><FileText size={16} style={{ color: sourceColor }}/></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold truncate" style={{ color: C.slate800 }}>{doc.name}</span>
                    <span className="block text-[11px] mt-0.5" style={{ color: C.slate500 }}>{doc.scripts} script{doc.scripts === 1 ? "" : "s"} · {doc.tests} test case{doc.tests === 1 ? "" : "s"} · {doc.sections} section{doc.sections === 1 ? "" : "s"}</span>
                  </span>
                </span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="w-24 h-2 rounded-full overflow-hidden" style={{ background: C.slate100 }}><span className="block h-full rounded-full" style={{ width: `${doc.coveragePct}%`, background: sourceColor }}/></span>
                  <span className="text-xs font-bold" style={{ color: sourceColor }}>{doc.coveragePct}%</span>
                  <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ background: `${sourceColor}15`, color: sourceColor }}>{doc.status}</span>
                  <ChevronRight size={15} style={{ color: C.slate400, transform: isSourceOpen ? "rotate(90deg)" : "none" }}/>
                </span>
              </button>
              {isSourceOpen && (<div className="px-5 pb-4" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}>
                {doc.scriptItems.length === 0 ? (<p className="text-[11px] mt-3" style={{ color: C.slate400 }}>No automation scripts generated for this BRD yet — its {doc.tests} test cases are not yet backed by a script.</p>) : (<>
                <p className="text-[10px] uppercase font-semibold tracking-wider mt-3 mb-2" style={{ color: C.slate400 }}>Test Scripts · click a script to see mapped test cases</p>
                {doc.scriptItems.map((script) => {
                  const isScriptOpen = expandedScript === script.id;
                  const runColor = script.status === "passed" ? C.green : script.status === "failed" ? C.red : C.slate500;
                  return (<div key={script.id} className="rounded-xl overflow-hidden mb-2" style={{ border: `1px solid ${isScriptOpen ? C.blue : C.sidebarBorder}` }}>
                    <button onClick={() => setExpandedScript(isScriptOpen ? "" : script.id)} className="w-full flex items-center justify-between px-4 py-3 text-left" style={{ background: isScriptOpen ? "#F7FBFF" : C.white }}>
                      <span className="flex items-center gap-2 min-w-0">
                        <Code2 size={15} style={{ color: C.blue }} className="shrink-0"/>
                        <span className="text-xs font-bold truncate" style={{ ...mono, color: C.slate800 }}>{script.name}</span>
                        <span className="text-[11px] shrink-0" style={{ color: C.slate500 }}>{script.mappedCases.length} mapped test cases</span>
                      </span>
                      <ChevronRight size={14} style={{ color: C.slate400, transform: isScriptOpen ? "rotate(90deg)" : "none" }}/>
                    </button>
                    {isScriptOpen && (<div className="grid grid-cols-[1fr_260px]" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}>
                      <div className="p-4 max-h-[360px] overflow-y-auto">
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.blue }}><FlaskConical size={11} className="inline mr-1"/>Mapped Test Cases <span className="ml-1 px-1.5 py-0.5 rounded-full" style={{ background: C.blueL }}>{script.mappedCases.length}</span></p>
                        {script.mappedCases.map((tc, index) => (<div key={`${tc.id}-${index}`} className="flex items-center justify-between gap-3 py-2 text-[11px]" style={{ borderBottom: index < script.mappedCases.length - 1 ? `1px solid ${C.slate50}` : "none" }}>
                          <span className="min-w-0">
                            <span style={{ ...mono, color: C.blue }}>{tc.id}</span>
                            <span className="ml-3" style={{ color: C.slate700 }}>{tc.title}</span>
                          </span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: tc.type === "Negative" ? C.redL : C.greenL, color: tc.type === "Negative" ? C.red : C.green }}>{tc.type === "Negative" ? "Neg" : "Pos"}</span>
                        </div>))}
                      </div>
                      <div className="p-4" style={{ background: "#FBFDFF", borderLeft: `1px solid ${C.sidebarBorder}` }}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.blueD }}><Bot size={11} className="inline mr-1"/>Latest Run</p>
                        <div className="rounded-lg p-3 text-xs" style={{ background: `${runColor}12`, color: runColor }}>
                          {script.status === "passed" ? <CheckCircle2 size={13} className="inline mr-1"/> : script.status === "failed" ? <XCircle size={13} className="inline mr-1"/> : <Timer size={13} className="inline mr-1"/>}
                          <span style={mono}>{script.status === "not_run" ? "Not run" : script.status}</span>
                          <p className="text-[10px] mt-1">Section {script.sectionNumber}</p>
                        </div>
                        <p className="text-[10px] mt-3" style={{ color: C.slate500 }}>These test cases come from the same BRD section this script was generated from.</p>
                      </div>
                    </div>)}
                  </div>);
                })}</>)}
              </div>)}
            </section>);
          })}</div>
        </main>
      </div>
    </>);
}
function BRDWidget({ onOpen }) {
    const { traceability } = useTestSuiteData();
    const avgCov = traceability.overallPct;
    const totalScripts = traceability.totalScripts;
    return (<>
      <Card className="p-4 h-full flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.blueL }}>
            <GitBranch size={18} style={{ color: C.blue }}/>
          </div>
          <div>
            <p className="text-[15px] font-bold" style={{ color: C.slate900 }}>Traceability Matrix</p>
            <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>Test scripts mapped to input sources and test cases</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl px-5 py-5" style={{ background: C.blueL, border: `1px solid ${C.blueM}` }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.slate400 }}>Traceability Matrix</p>
            <p className="text-4xl font-bold leading-none mt-2" style={{ ...mono, color: C.blue }}>{avgCov}%</p>
            <div className="h-2 rounded-full overflow-hidden mt-4" style={{ background: C.blueM }}><div className="h-full rounded-full" style={{ width: `${avgCov}%`, background: C.blue }}/></div>
          </div>
          <div className="rounded-2xl px-5 py-5" style={{ background: C.greenL, border: "1px solid #A7F3D0" }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.green }}>Total Test Scripts</p>
            <p className="text-4xl font-bold leading-none mt-2" style={{ ...mono, color: C.green }}>{totalScripts}</p>
            <p className="text-[11px] mt-3" style={{ color: C.slate500 }}>{traceability.fullyTraced} fully traced · {traceability.notTraced} not traced</p>
          </div>
        </div>

        <button onClick={onOpen} className="w-full py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity" style={{ background: C.blue, color: C.white }}>View Backtracing Details</button>
      </Card>
    </>);
}
// ─── AI Insights Widget ───────────────────────────────────────────────────────
const AI_INSIGHTS = [
    {
        id: "ins-1",
        type: "risk",
        icon: "🔴",
        title: "Payment module failure spike detected",
        body: "3 regression scripts failed consecutively in the last 2 runs. Root cause traces to a recent API contract change in the checkout service. Recommend freezing new deployments to this module until TC-034 is resolved.",
        tags: ["Payment", "Regression", "P0"],
        time: "18 min ago",
    },
    {
        id: "ins-2",
        type: "positive",
        icon: "🟢",
        title: "Login module reached 100% automation coverage",
        body: "All 14 Login requirements now have linked test cases and passing scripts. AI generated 6 additional edge-case tests this sprint, including OTP expiry and concurrent session handling scenarios.",
        tags: ["Login", "Coverage", "AI-generated"],
        time: "1 hr ago",
    },
    {
        id: "ins-3",
        type: "recommendation",
        icon: "💡",
        title: "5 high-priority requirements still unscripted",
        body: "REQ-009, REQ-012, REQ-017, REQ-021, REQ-024 have test cases mapped but no automation scripts. AI can generate initial script skeletons — estimated 40 min of manual scripting effort saved.",
        tags: ["Automation Gap", "Customer", "Reports"],
        time: "3 hr ago",
    },
    {
        id: "ins-4",
        type: "trend",
        icon: "📈",
        title: "AI test generation velocity up 34% this sprint",
        body: "TestSuite AI generated 47 test cases this week vs. 35 last week. Positive/Negative ratio is balanced at 58/42. Manual review time per AI test dropped from 8 min to 5.2 min.",
        tags: ["AI Health", "Generation", "Trend"],
        time: "6 hr ago",
    },
    {
        id: "ins-5",
        type: "risk",
        icon: "🟡",
        title: "Checkout module has 2 flaky scripts",
        body: "SCR-045 and SCR-048 have intermittent failures over the last 5 runs (pass rate 60%). Likely timing-related — AI suggests adding explicit wait conditions on the cart total render event.",
        tags: ["Checkout", "Flaky", "P1"],
        time: "Yesterday",
    },
];
const INSIGHT_STYLE = {
    risk: { border: C.red, dot: C.red },
    positive: { border: C.green, dot: C.green },
    recommendation: { border: C.blue, dot: C.blue },
    trend: { border: C.orange, dot: C.orange },
};
function AIInsightsWidget() {
    const riskCount = AI_INSIGHTS.filter((i) => i.type === "risk").length;
    const recCount = AI_INSIGHTS.filter((i) => i.type === "recommendation").length;
    const posCount = AI_INSIGHTS.filter((i) => i.type === "positive" || i.type === "trend").length;
    const total = AI_INSIGHTS.length;
    return (<Card className="p-4 flex flex-col gap-3" style={{ height: BOTTOM_WIDGET_HEIGHT }}>
      {/* Title row */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.blueL }}>
          <Sparkles size={18} style={{ color: C.blue }}/>
        </div>
        <div>
          <p className="text-[15px] font-bold" style={{ color: C.slate900 }}>AI Insights</p>
          <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>{total} signals · updated 18 min ago</p>
        </div>
      </div>

      {/* Score block */}
      <div className="flex items-center gap-5 px-5 py-5 rounded-2xl" style={{ background: C.blueL, border: `1px solid ${C.blueM}` }}>
        <div className="shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.slate400 }}>Total Signals</p>
          <p className="text-5xl font-bold leading-none mt-1.5" style={{ ...mono, color: C.blue }}>{total}</p>
        </div>
        <div className="flex-1 space-y-2">
          {[
            { label: "Risks", val: riskCount },
            { label: "Tips", val: recCount },
        ].map((r) => (<div key={r.label} className="flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: C.blueM }}>
                <div className="h-full rounded-full" style={{ width: `${Math.round((r.val / total) * 100)}%`, background: C.blue }}/>
              </div>
              <span className="text-[11px] font-semibold shrink-0 w-14 text-right" style={{ color: C.slate500 }}>{r.val} {r.label}</span>
            </div>))}
        </div>
      </div>

      {/* Status tiles */}
      <div className="grid grid-cols-3 gap-3">
        {[
            { label: "Risks", val: riskCount, bg: C.blue, fg: C.white, border: C.blueD },
            { label: "Tips", val: recCount, bg: C.blueL, fg: C.blue, border: C.blueM },
            { label: "Wins", val: posCount, bg: C.slate100, fg: C.slate600, border: C.slate200 },
        ].map((s) => (<div key={s.label} className="rounded-xl px-3 py-5 text-center" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
            <p className="text-3xl font-bold leading-none" style={{ ...mono, color: s.fg }}>{s.val}</p>
            <p className="text-[11px] mt-2 font-medium" style={{ color: s.fg, opacity: 0.85 }}>{s.label}</p>
          </div>))}
      </div>

      {/* CTA */}
      <button className="w-full py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity" style={{ background: C.blue, color: C.white }}>
        View All Insights
      </button>
    </Card>);
}
// ─── Release Readiness Widget ────────────────────────────────────────────────
function ReleaseReadinessWidget() {
    const releaseReady = 68;
    return (<Card className="p-4 flex flex-col gap-3" style={{ height: BOTTOM_WIDGET_HEIGHT }}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.greenL }}>
          <Target size={18} style={{ color: C.green }}/>
        </div>
        <div>
          <p className="text-[15px] font-bold" style={{ color: C.slate900 }}>Release Readiness</p>
          <p className="text-xs mt-0.5" style={{ color: C.slate500 }}>Release 2.4.1 · UAT target</p>
        </div>
      </div>

      <div className="rounded-2xl px-5 py-5" style={{ background: C.greenL, border: "1px solid #A7F3D0" }}>
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.green }}>Release ready</p>
            <p className="text-5xl font-bold leading-none mt-1.5" style={{ ...mono, color: C.green }}>{releaseReady}%</p>
          </div>
          <span className="text-[11px] font-semibold text-right" style={{ color: C.green }}>On track for<br />UAT</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden mt-4" style={{ background: "#D1FAE5" }}>
          <div className="h-full rounded-full" style={{ width: `${releaseReady}%`, background: C.green }}/>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
            { label: "Critical issues", value: "7", color: C.red, bg: C.redL },
            { label: "Review pending", value: "18", color: C.orange, bg: C.orangeL },
        ].map((metric) => (<div key={metric.label} className="rounded-xl px-3 py-3" style={{ background: metric.bg }}>
            <p className="text-2xl font-bold leading-none" style={{ ...mono, color: metric.color }}>{metric.value}</p>
            <p className="text-[11px] mt-1.5 font-medium" style={{ color: metric.color }}>{metric.label}</p>
          </div>))}
      </div>

      <div className="rounded-xl px-3 py-2.5 flex items-center justify-between gap-3" style={{ background: C.orangeL, border: "1px solid #FDE68A" }}>
        <div>
          <p className="text-[11px] font-semibold" style={{ color: C.orange }}>Flaky Test Rate</p>
          <p className="text-[10px] mt-0.5" style={{ color: C.slate500 }}>
            {FLAKY_TEST_RATE.flakyTests} tests passed and failed on one commit · {FLAKY_TEST_RATE.automatedExecutions} executions
          </p>
        </div>
        <span className="text-2xl font-bold shrink-0" title="Flaky Test Rate = tests that both pass and fail on the same build commit ÷ total automated executions × 100" style={{ ...mono, color: C.orange }}>
          {FLAKY_TEST_RATE.rate}%
        </span>
      </div>

      <div className="mt-auto pt-3 flex items-center gap-2" style={{ borderTop: `1px solid ${C.sidebarBorder}` }}>
        <Clock size={13} style={{ color: C.slate500 }}/>
        <span className="text-[11px]" style={{ color: C.slate500 }}>Next gate: resolve P0 coverage gaps</span>
      </div>
    </Card>);
}
// ─── Traceability Matrix ──────────────────────────────────────────────────────
// Build linked rows: Requirement → Test Cases → Script → Execution
const TRACE_ROWS = ALL_REQUIREMENTS.map((req) => {
    const tcs = TEST_CASES.filter((t) => t.module === req.module);
    const scripts = AUTO_SCRIPTS.filter((s) => {
        const suiteMap = {
            Login: "Auth Service", Payment: "Payment Flow", Checkout: "Checkout",
            Customer: "Customer", Reports: "Reports",
        };
        return s.suite === (suiteMap[req.module] ?? req.module);
    });
    const passed = scripts.filter((s) => s.status === "passed").length;
    const failed = scripts.filter((s) => s.status === "failed").length;
    const execStatus = scripts.length === 0 ? "none"
        : failed > 0 ? "failed"
            : passed === scripts.length ? "passed"
                : "pending";
    return { req, tcs, scripts, passed, failed, execStatus };
});
const EXEC_CFG = {
    passed: { bg: C.greenL, fg: C.green, label: "Passed" },
    failed: { bg: C.redL, fg: C.red, label: "Failed" },
    pending: { bg: C.orangeL, fg: C.orange, label: "Pending" },
    none: { bg: C.blueL, fg: C.slate500, label: "No Script" },
};
// ─── AI Requirement Traceability — helpers ────────────────────────────────────
const MODULES_LIST = ["Login", "Payment", "Checkout", "Customer", "Reports"];
const MODULE_COLOR_MAP = {
    Login: "#6366F1", Payment: C.blue, Checkout: C.green, Customer: C.orange, Reports: "#8B5CF6",
};
function traceStats() {
    const total = TRACE_ROWS.length;
    const fully = TRACE_ROWS.filter((r) => r.execStatus === "passed").length;
    const partial = TRACE_ROWS.filter((r) => r.execStatus === "failed" || r.execStatus === "pending").length;
    const missing = TRACE_ROWS.filter((r) => r.execStatus === "none" || r.tcs.length === 0).length;
    const pct = Math.round((fully / total) * 100);
    return { total, fully, partial, missing, pct };
}
// Mini arc SVG donut — pure SVG, no libs
function TracingDonut({ fully, partial, missing, size = 120 }) {
    const total = fully + partial + missing;
    const CX = size / 2, CY = size / 2, OR = size * 0.42, IR = size * 0.28, GAP = 0.03;
    const slices = [
        { val: fully, color: C.green, label: "Fully Traced" },
        { val: partial, color: C.orange, label: "Partially Traced" },
        { val: missing, color: C.red, label: "Missing" },
    ];
    let theta = -Math.PI / 2;
    const arcs = slices.map((s) => {
        const sweep = (s.val / total) * Math.PI * 2;
        const sa = theta + GAP, ea = theta + sweep - GAP;
        theta += sweep;
        const lg = sweep - 2 * GAP > Math.PI ? 1 : 0;
        const cos = Math.cos, sin = Math.sin;
        return { ...s, path: `M${CX + OR * cos(sa)} ${CY + OR * sin(sa)} A${OR} ${OR} 0 ${lg} 1 ${CX + OR * cos(ea)} ${CY + OR * sin(ea)} L${CX + IR * cos(ea)} ${CY + IR * sin(ea)} A${IR} ${IR} 0 ${lg} 0 ${CX + IR * cos(sa)} ${CY + IR * sin(sa)} Z` };
    });
    const pct = Math.round((fully / total) * 100);
    return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {arcs.map((a) => <path key={a.label} d={a.path} fill={a.color} opacity={0.85}/>)}
      <text x={CX} y={CY - 6} textAnchor="middle" fontSize={size * 0.18} fontWeight={700} fill={C.slate900} fontFamily="DM Mono, monospace">{pct}%</text>
      <text x={CX} y={CY + 10} textAnchor="middle" fontSize={size * 0.085} fill={C.slate400} fontFamily="Inter, sans-serif">Traced</text>
    </svg>);
}
// ─── AI Requirement Traceability Dashboard (slide-over) ───────────────────────
function TraceabilityPanel({ onClose }) {
    const [search, setSearch] = useState("");
    const [filterModule, setFilterModule] = useState("All");
    const [filterStatus, setFilterStatus] = useState("All");
    const [expandedId, setExpandedId] = useState(null);
    const [activeRec, setActiveRec] = useState(null);
    const modules = ["All", ...MODULES_LIST];
    const statusOpts = ["All", "Fully Traced", "Partial", "Missing"];
    // derive row traceability status from execStatus
    const rowStatus = (r) => r.tcs.length === 0 || r.scripts.length === 0 ? "Missing"
        : r.execStatus === "passed" ? "Fully Traced"
            : "Partial";
    const { total: totalReqs, fully: allPassed, partial: anyPartial, missing: noScript } = traceStats();
    const filtered = TRACE_ROWS.filter((r) => {
        const q = search.toLowerCase();
        const st = rowStatus(r);
        return ((r.req.id.toLowerCase().includes(q) || r.req.title.toLowerCase().includes(q)) &&
            (filterModule === "All" || r.req.module === filterModule) &&
            (filterStatus === "All" || st === filterStatus));
    });
    // AI recommendations for missing coverage
    const AI_RECS = TRACE_ROWS.filter((r) => rowStatus(r) === "Missing").slice(0, 4).map((r) => ({
        id: r.req.id,
        title: r.req.title,
        module: r.req.module,
        action: r.tcs.length === 0
            ? "Generate test cases from BRD description"
            : "Generate automation script skeleton",
        effort: r.tcs.length === 0 ? "~12 min" : "~8 min",
    }));
    const STATUS_CFG_TRACE = {
        "Fully Traced": { bg: C.greenL, fg: C.green, dot: C.green, label: "Fully Traced" },
        "Partial": { bg: C.orangeL, fg: C.orange, dot: C.orange, label: "Partial" },
        "Missing": { bg: C.redL, fg: C.red, dot: C.red, label: "Missing" },
    };
    return (<>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)" }} onClick={onClose}/>
      <div className="fixed right-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden" style={{ width: "min(1120px, 96vw)", background: C.pageBg, boxShadow: "-4px 0 48px rgba(15,23,42,0.18)", animation: "slideInRight 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
        <style>{`@keyframes slideInRight{from{transform:translateX(60px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-8 py-4 shrink-0" style={{ background: C.white, borderBottom: `1px solid ${C.sidebarBorder}` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#2563EB)" }}>
              <GitBranch size={17} color="#fff"/>
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ color: C.slate900 }}>AI Requirement Traceability</h2>
              <p className="text-[11px]" style={{ color: C.slate500 }}>BRD → Requirements → Test Cases → Automation → Execution · Release 2.4.1</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors hover:bg-blue-50" style={{ borderColor: C.sidebarBorder, color: C.slate600 }}>
              <Download size={11}/> Export CSV
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors" style={{ color: C.slate400 }}>
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* ── Top KPI strip ── */}
        <div className="grid grid-cols-4 gap-3 px-8 py-4 shrink-0">
          {[
            { label: "Total Requirements", value: String(totalReqs), color: C.blue, icon: <BookOpen size={14}/>, sub: "In scope for this release" },
            { label: "Fully Traced", value: String(allPassed), color: C.green, icon: <CheckCheck size={14}/>, sub: "BRD → script → exec passed" },
            { label: "Partially Traced", value: String(anyPartial), color: C.orange, icon: <AlertCircle size={14}/>, sub: "Test cases but no passing run" },
            { label: "Missing Coverage", value: String(noScript), color: C.red, icon: <XCircle size={14}/>, sub: "No test cases or scripts" },
        ].map((k) => (<div key={k.label} className="flex items-center gap-3 rounded-2xl px-4 py-3" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${k.color}15`, color: k.color }}>{k.icon}</div>
              <div>
                <p className="text-2xl font-bold leading-none" style={{ ...mono, color: k.color }}>{k.value}</p>
                <p className="text-[10px] font-semibold mt-0.5" style={{ color: C.slate700 }}>{k.label}</p>
                <p className="text-[10px]" style={{ color: C.slate400 }}>{k.sub}</p>
              </div>
            </div>))}
        </div>

        {/* ── Middle: donut + pipeline + module coverage ── */}
        <div className="grid grid-cols-3 gap-3 px-8 pb-4 shrink-0">
          {/* Donut */}
          <div className="rounded-2xl flex items-center gap-5 px-5 py-4" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <TracingDonut fully={allPassed} partial={anyPartial} missing={noScript} size={110}/>
            <div className="space-y-2">
              {[
            { label: "Fully Traced", val: allPassed, color: C.green },
            { label: "Partially Traced", val: anyPartial, color: C.orange },
            { label: "Missing", val: noScript, color: C.red },
        ].map((d) => (<div key={d.label} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }}/>
                  <span className="text-[11px]" style={{ color: C.slate600 }}>{d.label}</span>
                  <span className="text-[11px] font-bold ml-auto" style={{ ...mono, color: d.color }}>{d.val}</span>
                </div>))}
            </div>
          </div>

          {/* Pipeline */}
          <div className="rounded-2xl px-5 py-4 flex flex-col justify-center" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: C.slate400 }}>BRD → Execution Pipeline</p>
            <div className="flex items-center gap-1">
              {[
            { icon: "📄", label: "Requirements", val: totalReqs, color: C.blue },
            { icon: "🧪", label: "Test Cases", val: TRACE_ROWS.reduce((s, r) => s + r.tcs.length, 0), color: "#6366F1" },
            { icon: "🤖", label: "Scripts", val: TRACE_ROWS.reduce((s, r) => s + r.scripts.length, 0), color: C.blueD },
            { icon: "✅", label: "Fully Traced", val: allPassed, color: C.green },
        ].map((step, i, arr) => (<div key={step.label} className="flex items-center gap-1 flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base" style={{ background: `${step.color}15` }}>{step.icon}</div>
                    <p className="text-[11px] font-bold mt-1" style={{ ...mono, color: step.color }}>{step.val}</p>
                    <p className="text-[9px] text-center leading-tight mt-0.5" style={{ color: C.slate400 }}>{step.label}</p>
                  </div>
                  {i < arr.length - 1 && <div className="w-4 h-px shrink-0" style={{ background: C.slate200 }}/>}
                </div>))}
            </div>
          </div>

          {/* Coverage by module */}
          <div className="rounded-2xl px-5 py-4" style={{ background: C.white, border: `1px solid ${C.sidebarBorder}` }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: C.slate400 }}>Coverage by Module</p>
            <div className="space-y-2">
              {MODULES_LIST.map((mod) => {
            const rows = TRACE_ROWS.filter((r) => r.req.module === mod);
            const fc = rows.filter((r) => rowStatus(r) === "Fully Traced").length;
            const pct = rows.length ? Math.round((fc / rows.length) * 100) : 0;
            const col = pct >= 80 ? C.green : pct >= 50 ? C.orange : C.red;
            return (<div key={mod}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[11px] font-medium" style={{ color: C.slate700 }}>{mod}</span>
                      <span className="text-[10px] font-bold" style={{ color: col }}>{pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: C.blueL }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: col }}/>
                    </div>
                  </div>);
        })}
            </div>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div className="flex items-center gap-0 shrink-0 mx-8 rounded-xl overflow-hidden" style={{ border: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          <div className="flex items-center gap-2 px-4 py-2.5 border-r" style={{ borderColor: C.sidebarBorder }}>
            <Search size={12} style={{ color: C.slate400 }}/>
            <input className="text-xs bg-transparent outline-none w-40" placeholder="Search requirement…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ color: C.slate700 }}/>
          </div>
          <div className="flex items-center gap-1 px-3 py-2.5 border-r" style={{ borderColor: C.sidebarBorder }}>
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1" style={{ color: C.slate400 }}>Module</span>
            {modules.map((m) => (<button key={m} onClick={() => setFilterModule(m)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterModule === m ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{m}</button>))}
          </div>
          <div className="flex items-center gap-1 px-3 py-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide mr-1" style={{ color: C.slate400 }}>Status</span>
            {statusOpts.map((s) => (<button key={s} onClick={() => setFilterStatus(s)} className="text-[11px] px-2 py-0.5 rounded-md font-medium transition-colors" style={filterStatus === s ? { background: C.blue, color: C.white } : { background: C.blueL, color: C.slate600 }}>{s}</button>))}
          </div>
          <span className="text-[10px] ml-auto pr-4 shrink-0" style={{ color: C.slate400 }}>{filtered.length} / {totalReqs} requirements</span>
        </div>

        {/* ── Matrix table ── */}
        <div className="mx-8 mt-3 rounded-2xl overflow-hidden flex flex-col flex-1 min-h-0" style={{ border: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          {/* Table header */}
          <div className="grid px-5 py-2.5 text-[10px] font-semibold uppercase tracking-wider shrink-0" style={{ gridTemplateColumns: "72px 1fr 80px 60px 60px 80px 96px", color: C.slate400, borderBottom: `1px solid ${C.sidebarBorder}`, background: C.blueL }}>
            <span>Req ID</span><span>Requirement</span><span>Module</span><span>Tests</span><span>Scripts</span><span>Automation</span><span>Status</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (<div className="flex flex-col items-center justify-center h-32">
                <Info size={20} style={{ color: C.slate300 }}/>
                <p className="text-sm mt-2" style={{ color: C.slate400 }}>No rows match</p>
              </div>) : filtered.map((r) => {
            const st = rowStatus(r);
            const stCfg = STATUS_CFG_TRACE[st];
            const isOpen = expandedId === r.req.id;
            const autoPct = r.scripts.length > 0 ? Math.round((r.passed / r.scripts.length) * 100) : 0;
            return (<div key={r.req.id} style={{ borderBottom: `1px solid ${C.blueL}` }}>
                  <div className="grid px-5 items-center cursor-pointer hover:bg-blue-50 transition-colors group" style={{ gridTemplateColumns: "72px 1fr 80px 60px 60px 80px 96px", paddingTop: 11, paddingBottom: 11, background: isOpen ? C.blueL : undefined }} onClick={() => setExpandedId(isOpen ? null : r.req.id)}>
                    <span className="text-[11px] font-medium" style={{ ...mono, color: C.slate400 }}>{r.req.id}</span>
                    <p className="text-xs font-medium truncate pr-4 group-hover:text-blue-600 transition-colors" style={{ color: C.slate800 }}>{r.req.title}</p>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: `${MODULE_COLOR_MAP[r.req.module] ?? C.blue}15`, color: MODULE_COLOR_MAP[r.req.module] ?? C.blue }}>{r.req.module}</span>
                    <span className="text-[11px] font-medium" style={{ ...mono, color: r.tcs.length === 0 ? C.red : C.slate700 }}>{r.tcs.length || "—"}</span>
                    <span className="text-[11px] font-medium" style={{ ...mono, color: r.scripts.length === 0 ? C.slate300 : C.slate700 }}>{r.scripts.length || "—"}</span>
                    <div className="flex items-center gap-1.5">
                      {r.scripts.length > 0 ? (<>
                          <div className="flex-1 h-1.5 rounded-full" style={{ background: C.blueL, maxWidth: 40 }}>
                            <div className="h-full rounded-full" style={{ width: `${autoPct}%`, background: autoPct === 100 ? C.green : C.orange }}/>
                          </div>
                          <span className="text-[10px] font-bold" style={{ color: autoPct === 100 ? C.green : C.orange }}>{autoPct}%</span>
                        </>) : <span style={{ color: C.slate300, fontSize: 11 }}>—</span>}
                    </div>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit" style={{ background: stCfg.bg, color: stCfg.fg }}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: stCfg.dot }}/>
                      {stCfg.label}
                    </span>
                  </div>

                  {isOpen && (<div className="px-5 pb-4 pt-1 grid grid-cols-3 gap-4" style={{ background: `${C.blue}04`, borderTop: `1px solid ${C.blueL}` }}>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate400 }}>Test Cases ({r.tcs.length})</p>
                        {r.tcs.length === 0
                        ? <p className="text-xs" style={{ color: C.red }}>No test cases mapped yet.</p>
                        : r.tcs.slice(0, 5).map((tc) => (<div key={tc.id} className="flex items-center gap-2 mb-1">
                              <span style={{ ...mono, color: C.blue, fontSize: 10, minWidth: 48 }}>{tc.id}</span>
                              <span className="text-[11px] truncate flex-1" style={{ color: C.slate600 }}>{tc.title}</span>
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: tc.type === "Negative" ? C.redL : C.greenL, color: tc.type === "Negative" ? C.red : C.green }}>{tc.type === "Negative" ? "Neg" : "Pos"}</span>
                            </div>))}
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate400 }}>Automation Scripts ({r.scripts.length})</p>
                        {r.scripts.length === 0
                        ? <p className="text-xs" style={{ color: C.orange }}>No scripts yet.</p>
                        : r.scripts.slice(0, 5).map((sc) => {
                            const ic = { passed: { c: C.green, t: "✓" }, failed: { c: C.red, t: "✗" }, pending: { c: C.orange, t: "~" }, skipped: { c: C.slate400, t: "—" } }[sc.status];
                            return (<div key={sc.id} className="flex items-center gap-1.5 mb-1">
                                <span className="font-bold text-xs" style={{ color: ic.c }}>{ic.t}</span>
                                <span style={{ ...mono, color: C.slate600, fontSize: 10 }} className="truncate flex-1">{sc.file}</span>
                                <span style={{ color: C.slate400, fontSize: 10 }}>{sc.duration}</span>
                              </div>);
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2 content-start">
                        {[
                        { label: "Status", value: stCfg.label, color: stCfg.fg, bg: stCfg.bg },
                        { label: "Priority", value: r.req.priority, color: PRIORITY_COLOR[r.req.priority].fg, bg: PRIORITY_COLOR[r.req.priority].bg },
                        { label: "Module", value: r.req.module, color: MODULE_COLOR_MAP[r.req.module] ?? C.blue, bg: `${MODULE_COLOR_MAP[r.req.module] ?? C.blue}12` },
                        { label: "Updated", value: r.req.lastUpdated ?? "—", color: C.slate600, bg: C.blueL },
                    ].map((k) => (<div key={k.label} className="rounded-xl p-2" style={{ background: k.bg }}>
                            <p className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: C.slate400 }}>{k.label}</p>
                            <p className="text-xs font-bold mt-0.5" style={{ color: k.color }}>{k.value}</p>
                          </div>))}
                      </div>
                    </div>)}
                </div>);
        })}
          </div>
        </div>

        {/* ── AI Recommendations ── */}
        {AI_RECS.length > 0 && (<div className="mx-8 mt-3 mb-4 rounded-2xl px-5 py-4 shrink-0" style={{ background: "linear-gradient(135deg,#EEF2FF,#EFF6FF)", border: `1px solid #C7D2FE` }}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={13} style={{ color: "#6366F1" }}/>
              <p className="text-xs font-semibold" style={{ color: "#4338CA" }}>AI Recommendations — {AI_RECS.length} requirements need attention</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {AI_RECS.map((rec) => (<button key={rec.id} onClick={() => setActiveRec(activeRec === rec.id ? null : rec.id)} className="text-left rounded-xl px-3 py-2.5 transition-all hover:shadow-sm" style={{ background: C.white, border: `1px solid ${activeRec === rec.id ? "#6366F1" : C.sidebarBorder}` }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-bold" style={{ ...mono, color: "#6366F1" }}>{rec.id}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: `${MODULE_COLOR_MAP[rec.module] ?? C.blue}15`, color: MODULE_COLOR_MAP[rec.module] ?? C.blue }}>{rec.module}</span>
                      </div>
                      <p className="text-[11px] font-medium truncate" style={{ color: C.slate700 }}>{rec.title}</p>
                      {activeRec === rec.id && (<p className="text-[10px] mt-1" style={{ color: "#6366F1" }}>⚡ {rec.action}</p>)}
                    </div>
                    <span className="text-[10px] shrink-0 font-semibold px-1.5 py-0.5 rounded" style={{ background: "#EEF2FF", color: "#6366F1" }}>{rec.effort}</span>
                  </div>
                </button>))}
            </div>
          </div>)}

        {/* ── Footer ── */}
        <div className="px-8 py-3 flex items-center justify-between shrink-0" style={{ borderTop: `1px solid ${C.sidebarBorder}`, background: C.white }}>
          <p className="text-[11px] flex items-center gap-1.5" style={{ color: C.slate400 }}>
            <GitBranch size={11} style={{ color: C.blue }}/>
            End-to-end trace: BRD → Requirement → Test Case → Script → Execution
          </p>
          <button className="flex items-center gap-1.5 text-xs font-semibold px-4 py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity" style={{ background: "linear-gradient(135deg,#6366F1,#2563EB)" }}>
            <Download size={11}/> Export Full Matrix
          </button>
        </div>
      </div>
    </>);
}
// ─── AI Requirement Traceability Widget (dashboard card) ──────────────────────
function TraceabilityWidget() {
    const [open, setOpen] = useState(false);
    const { total, fully, partial, missing, pct } = traceStats();
    return (<>
      <Card className="p-5 flex flex-col gap-4">
        {/* Title row */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.blueL }}>
            <GitBranch size={16} style={{ color: C.blue }}/>
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: C.slate900 }}>AI Traceability</p>
            <p className="text-[11px] mt-0.5" style={{ color: C.slate500 }}>{total} requirements · Release 2.4.1</p>
          </div>
        </div>

        {/* Score block */}
        <div className="flex items-center gap-4 px-4 py-3.5 rounded-xl" style={{ background: C.blueL, border: `1px solid ${C.blueM}` }}>
          <div className="shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: C.slate400 }}>Traceability</p>
            <p className="text-4xl font-bold leading-none mt-1" style={{ ...mono, color: C.blue }}>{pct}%</p>
          </div>
          <div className="flex-1">
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: C.blueM }}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: C.blue }}/>
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: C.slate400 }}>BRD → Test Cases → Automation</p>
          </div>
        </div>

        {/* Status tiles */}
        <div className="grid grid-cols-3 gap-2.5">
          {[
            { label: "Traced", val: fully, bg: C.blue, fg: C.white, border: C.blueD },
            { label: "Partial", val: partial, bg: C.blueL, fg: C.blue, border: C.blueM },
            { label: "Missing", val: missing, bg: C.slate100, fg: C.slate600, border: C.slate200 },
        ].map((s) => (<div key={s.label} className="rounded-xl px-2 py-3.5 text-center" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
              <p className="text-2xl font-bold leading-none" style={{ ...mono, color: s.fg }}>{s.val}</p>
              <p className="text-[10px] mt-1.5 font-medium" style={{ color: s.fg, opacity: 0.85 }}>{s.label}</p>
            </div>))}
        </div>

        {/* CTA */}
        <button onClick={() => setOpen(true)} className="w-full py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity" style={{ background: C.blue, color: C.white }}>
          View Details
        </button>
      </Card>
      {open && <TraceabilityPanel onClose={() => setOpen(false)}/>}
    </>);
}
// ─── App ──────────────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
    return (<div className="flex items-center gap-3 mb-3">
      <h2 className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: C.slate400 }}>{children}</h2>
      <div className="flex-1 h-px" style={{ background: C.blueL }}/>
    </div>);
}
function FinancesPage() {
    const { rates, update, reset } = useFinanceRates();
    const model = useFinanceModel(rates);
    return (<div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold" style={{ color: C.slate900 }}>Finances</h1>
        <p className="text-sm mt-1" style={{ color: C.slate500 }}>
          Derived from {model.basis.testCases.toLocaleString()} generated test cases, {model.basis.automationRuns} automation runs
          and {(model.basis.llmApiMs / 60000).toFixed(1)} minutes of measured LLM time.
        </p>
      </div>
      <FinanceAssumptions rates={rates} update={update} reset={reset}/>
      <CostBreakdown model={model}/>
      <ManualVsAIComparison model={model}/>
    </div>);
}
function DashboardApp({ currentUser, onExit, onLogout }) {
    const [activePage, setActivePage] = useState("dashboard");
    // Owned here so the command palette and alert menu can open these drawers,
    // not just the KPI cards that used to hold the state privately.
    const [overlay, setOverlay] = useState(null);
    const openPanel = useCallback((panel) => {
        if (panel === "traceability" || panel === "testcases" || panel === "scripts")
            setActivePage("dashboard");
        setOverlay(panel);
    }, []);
    return (<div className="flex min-h-screen" style={{ ...sans, background: C.pageBg }}>
      <Sidebar activePage={activePage} onNavigate={setActivePage} currentUser={currentUser} onExit={onExit} onLogout={onLogout}/>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopNav activePage={activePage} currentUser={currentUser} onExit={onExit} onLogout={onLogout} onOpenPanel={openPanel}/>

        {overlay === "testcases" && <TestCasesPanel onClose={() => setOverlay(null)}/>}
        {overlay === "scripts" && <AutomationScriptsPanel onClose={() => setOverlay(null)}/>}
        {overlay === "traceability" && <ScriptTraceabilityDrilldown onClose={() => setOverlay(null)}/>}

        <main className="flex-1 overflow-y-auto" style={{ padding: "16px 20px 28px" }}>
          {activePage === "finances" ? <FinancesPage /> : <div className="space-y-3">
            <DataSourceBanner />
            {/* ROW 1 — KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <ReleaseReadiness />
              <ReqCoverage />
              <TestCasesKPI onOpen={() => openPanel("testcases")} />
              <AutoScriptsKPI onOpen={() => openPanel("scripts")} />
              <FlakyTestRateKPI />
              <CriticalIssuesKPI />
            </div>

            {/* ROW 2 — Release Lifecycle · Req Coverage · Module Risk */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
              <ReleaseFunnel />
              <ReqDonut />
              <ModuleDefectDistribution />
            </div>

            {/* ROW 3 — AI Health · Backward Traceability */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch" style={{ gridAutoRows: "380px" }}>
              <AIHealth />
              <BRDWidget onOpen={() => openPanel("traceability")} />
            </div>

          </div>}
        </main>
      </div>
    </div>);
}
/**
 * The dashboard reads coverage through context, so the provider has to sit
 * above every widget. It serves the demo numbers whenever the API is
 * unreachable or has ingested nothing, which keeps this shell unchanged.
 *
 * Rendered full-bleed: it brings its own sidebar and top bar, so App.jsx hides
 * the TestSuite AI header on this route and `onExit` is the way back.
 */
export function DashboardWorkspace({ currentUser = null, onExit, onLogout }) {
    return (<CoverageProvider fallback={COVERAGE_FALLBACK}>
      <TestSuiteDataProvider>
        <DashboardApp currentUser={currentUser} onExit={onExit} onLogout={onLogout}/>
      </TestSuiteDataProvider>
    </CoverageProvider>);
}

export default DashboardWorkspace;
